import React, { useState, useEffect, useMemo } from 'react'
import { ensureSessionHeaders, getSessionHeaders, getServerSessionId } from '../../lib/serverSession'
import { resolveProofUrl, isSignableProofRef } from '../../lib/proofUrlResolver'
import { calculateBonusShares, calculateTotalShares } from '../../lib/bonusShareCalculator'
import { API_BASE_URL } from '../../constants'
import HashRemovalManager from './HashRemovalManager'
import PhaseDiscrepancyAudit from './PhaseDiscrepancyAudit'
import { findFuzzyMatches, FuzzyMatchResult, getMatchTypeLabel, getMatchTypeColor, compareCharacters, getCharStatusColor } from '../../lib/fuzzySearch'
import { getPhaseAtTimestamp, validatePhasePrice } from '../../lib/phaseCutoffUtils'
import { logAdminAction } from '../../lib/adminAuth'
import { logAction, logSearch } from '../../lib/personalActivityLogger'
import { hasUsdCoverage } from '../../lib/moneyMath.js'
import { MANUAL_UNDERPAY_TOLERANCE_USD } from '../../lib/paymentApprovalBlockchainGate.js'
import {
  applyChosenManualPackage,
  computeProductionCapitalBreakdown,
  isProductionCapitalPackageId,
  listActiveProductionCapitalPackages,
  stampManualPaymentPcapNotes,
} from '../../lib/productionCapitalCampaign.js'

interface Payment {
  id: string
  user_id: number
  amount: number
  shares_to_purchase: number
  network: string
  currency: string
  sender_wallet: string
  receiver_wallet: string
  transaction_hash: string
  screenshot_url: string
  status: 'pending' | 'approved' | 'rejected' | 'reversed' | 'flagged_duplicate' | 'review_requested' | 'underpaid'
  created_at: string
  approved_at?: string
  rejected_at?: string
  reversed_at?: string
  approved_by_admin_id?: number
  rejected_by_admin_id?: number
  reversed_by_admin_id?: number
  rejection_reason?: string
  reversal_reason?: string
  admin_notes?: string
  verification_status?: string
  blockchain_verified_at?: string | null
  blockchain_amount?: number | string | null
  blockchain_confirmations?: number | string | null
  blockchain_sender_wallet?: string | null
  transaction_notes?: string | null
  record_kind?: 'commission_conversion' | 'commission_topup'
  commission_review?: {
    verified?: boolean
    code?: string | null
    availableUsdt?: number
    reservedUsdt?: number
    requestedUsdt?: number
    balanceBefore?: number | null
    enough?: boolean
    packages?: Array<{ packageId?: string; qty?: number }> | null
    checks?: Array<{ name: string; ok: boolean; detail: string }>
  } | null
  topup_verification?: {
    success?: boolean
    error?: string | null
    errorCode?: string | null
    matches?: { sender?: boolean; receiver?: boolean; amount?: boolean } | null
    blockchain?: { from?: string; to?: string; amount?: number; explorerUrl?: string; confirmations?: number } | null
  } | null
  remitano_charge_id?: string | null
  users?: {
    id: number
    auth_user_id?: string
    username: string
    email: string
    full_name?: string
    phone_number?: string
    telegram_users?: {
      username?: string
      first_name?: string
      last_name?: string
    } | Array<{
      username?: string
      first_name?: string
      last_name?: string
    }>
  }
}

const TRUSTED_BLOCKCHAIN_APPROVAL_STATUSES = new Set([
  'auto_verified',
  'blockchain_verified',
  'blockchain_verified_approved'
])

const MANUAL_OVERRIDE_PAID_VALUES = new Set(['PAID', 'Paid', 'paid'])
const MANUAL_OVERRIDE_TYPE = 'OWNER_VERIFIED_EXTERNAL_EVIDENCE'
const AUTHORIZED_MANUAL_OVERRIDE_EMAILS = new Set(['johan@beaversden.co.za', 'jp.rademeyer84@gmail.com'])

function isValidManualOverridePaidValue(value: string): boolean {
  return MANUAL_OVERRIDE_PAID_VALUES.has(value.trim())
}

interface ManualApprovalOverrideDetails {
  reason: string
  confirmation: string
  type: string
}

const toRouteToken = (value: unknown): string => String(value ?? '').trim().toUpperCase().replace(/[\s-]+/g, '_')

const parsePaymentNotes = (notes: Payment['transaction_notes']): Record<string, any> => {
  if (!notes) return {}
  if (typeof notes === 'object') return notes as Record<string, any>
  try {
    return JSON.parse(notes) || {}
  } catch {
    return {}
  }
}

const isBankTransferPayment = (payment: Payment): boolean => {
  const network = toRouteToken(payment.network)
  const notes = parsePaymentNotes(payment.transaction_notes)
  const noteType = toRouteToken(notes.payment_type || notes.payment_method || notes.type)
  return network.includes('BANK_TRANSFER') || network.includes('BANK') || network.includes('EFT') || noteType.includes('BANK_TRANSFER') || noteType.includes('BANK')
}

const isCommissionConversion = (payment: Payment): boolean => payment.record_kind === 'commission_conversion' || payment.network === 'COMMISSION_CONVERSION'
const isCommissionTopup = (payment: Payment): boolean => payment.record_kind === 'commission_topup'

const paymentKindLabel = (payment: Payment): string => {
  if (isCommissionTopup(payment)) return 'COMMISSION TOP-UP'
  if (isCommissionConversion(payment)) return 'COMMISSION CONVERSION'
  return 'NORMAL PAYMENT'
}

const paymentKindBadgeClass = (payment: Payment): string => {
  if (isCommissionTopup(payment)) return 'bg-cyan-300 text-black'
  if (isCommissionConversion(payment)) return 'bg-amber-300 text-black'
  return 'bg-blue-600 text-white'
}

const paymentKindCardClass = (payment: Payment): string => {
  if (isCommissionTopup(payment)) return 'border-cyan-300 border-l-8 bg-cyan-950/40'
  if (isCommissionConversion(payment)) return 'border-amber-300 border-l-8 bg-amber-950/40'
  return 'border-blue-500 border-l-8 bg-slate-900'
}

const isBlockchainPayment = (payment: Payment): boolean => {
  if (isCommissionConversion(payment) || isCommissionTopup(payment)) return false
  if (isBankTransferPayment(payment)) return false
  const network = toRouteToken(payment.network)
  const notes = parsePaymentNotes(payment.transaction_notes)
  const noteType = toRouteToken(notes.payment_type || notes.payment_method || notes.type)
  if (network.includes('BANK_TRANSFER') || network.includes('BANK') || network.includes('EFT') || network.includes('WIRE_TRANSFER')) return false
  return network.includes('BSC') || network.includes('BEP20') || network.includes('BEP_20') ||
    network.includes('TRC20') || network.includes('TRC_20') || network.includes('TRON') ||
    network.includes('ERC20') || network.includes('ERC_20') || network.includes('ETH') ||
    network.includes('POLYGON') || network.includes('MATIC') ||
    noteType === 'CRYPTO' || noteType === 'CRYPTOCURRENCY'
}

const hasBlockchainApprovalAttestation = (payment: Payment): boolean => {
  const status = String(payment.verification_status || '').trim().toLowerCase()
  const blockchainAmount = Number(payment.blockchain_amount)
  return TRUSTED_BLOCKCHAIN_APPROVAL_STATUSES.has(status) &&
    Boolean(payment.blockchain_verified_at) &&
    Number.isFinite(blockchainAmount) && blockchainAmount > 0 &&
    Boolean(payment.transaction_hash)
}

const readableApiMessage = (payload: unknown, fallback: string): string => {
  if (!payload) return fallback
  if (typeof payload === 'string') return payload || fallback
  if (payload instanceof Error) return payload.message || fallback
  if (typeof payload === 'object') {
    const record = payload as Record<string, unknown>
    const candidates = [record.message, record.error, record.details, record.hint]
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) return candidate
    }
    try {
      const serialized = JSON.stringify(payload)
      return serialized && serialized !== '{}' ? serialized : fallback
    } catch {
      return fallback
    }
  }
  return fallback
}

const readApiResponse = async <T,>(response: Response, fallback: string): Promise<T> => {
  const rawText = await response.text()
  let payload: unknown = null

  if (rawText) {
    try {
      payload = JSON.parse(rawText)
    } catch {
      payload = rawText
    }
  }

  if (!response.ok) {
    throw new Error(readableApiMessage(payload, `${fallback} (HTTP ${response.status})`))
  }

  return payload as T
}

// Validation error interface for wallet mismatches
interface ValidationError {
  type: 'SENDER_MISMATCH' | 'RECEIVER_MISMATCH'
  label: string
  found: string
  expected: string
  note?: string
}

// Remitano verification result interface
interface RemitanoVerificationResult {
  success: boolean
  verified: boolean
  amountMatch?: boolean
  amountStatus?: 'exact' | 'warning' | 'mismatch' | 'underpaid' | 'overpaid_ok' | 'within_warning' // exact: <$1, warning: $1-$5, mismatch: >$5
  amountDifference?: string // Dollar amount difference
  amountWarning?: boolean // True if between $1-$5 difference
  autoApproved?: boolean
  autoRejected?: boolean
  // v7.3.392: Shortfall / commission-coverage fields for underpayment scenarios
  shortfall?: string // Dollar amount user is short on the blockchain transfer (when underpaid)
  availableCommission?: string // User's available USDT commission balance at verify time
  shortfallCoveredByCommission?: boolean // True if auto-approval used USDT commission to cover shortfall
  canCoverWithCommission?: boolean
  message: string
  hint?: string
  // Structured validation errors for wallet mismatches
  validationErrors?: ValidationError[]
  verification?: {
    remitanoChargeId?: string
    status?: string
    amount?: number
    currency?: string
    createdAt?: string
    completedAt?: string
    description?: string
    paymentUrl?: string
    // Blockchain-specific fields
    txHash?: string
    network?: string
    blockchainAmount?: number
    expectedAmount?: number
    confirmations?: number
    blockNumber?: number
    explorerUrl?: string
    // Wallet verification fields
    actualSender?: string
    expectedSender?: string
    actualReceiver?: string
    expectedReceiver?: string
    senderMatch?: boolean
    receiverMatch?: boolean
    // Sender wallet correction fields (when user mistakenly entered company wallet)
    senderWallet?: string
    senderWalletCorrected?: boolean
    originalSenderWallet?: string
    // Native coin / batch transfer fields
    transferType?: string
    nativeAmount?: number
    nativeCoin?: string
    nativePrice?: number
    batchInfo?: {
      isBatch?: boolean
      isRevolutBatch?: boolean
      totalTransfers?: number
      allTransfers?: Array<{
        index?: number
        amount?: number
        to?: string
        isCompanyTransfer?: boolean
      }>
    }
  }
  // Payment info for rejection workflow
  paymentInfo?: {
    paymentId: string
    userId: number
    userEmail?: string
    userName?: string
    amount: number
    network: string
  }
  approval?: {
    paymentId: string
    sharePurchaseId: string
    amount: number
    expectedAmount?: number
    baseShares: number
    bonusShares: number
    totalShares: number
    packageName: string
    commission?: {
      usdt: number
      shares: number
    }
  }
  error?: string
  errorCode?: string
  approvalBlocked?: boolean
  report?: {
    verdict: 'ACCURATE' | 'CHAIN_VERIFIED' | 'NOT_ACCURATE'
    approvalReady: boolean
    summary: string
    lines: Array<{ label: string, value: string, ok: boolean | null }>
    transfers: Array<{ amount: number, to: string, toLabel: string, selected: boolean }>
  }
}

const CEX_HOT_MANUAL_REVIEW_CODE = 'CEX_HOT_MANUAL_REVIEW'
const UNDERPAID_ERROR_CODE = 'UNDERPAID'
const DUPLICATE_HASH_CODE = 'DUPLICATE_HASH'
const NATIVE_BNB_NOT_USDT_CODE = 'NATIVE_BNB_NOT_USDT'
const EXODUS_BSC_NETWORKS = new Set([
  'BSC',
  'BNB',
  'BEP20',
  'BNBSMARTCHAIN',
  'USDTBSC',
  'BSCUSDT',
  'USDTBEP20',
  'BEP20USDT',
])

const isExodusBscNetwork = (network?: string | null) => {
  const normalized = String(network || '').toUpperCase().replace(/[^A-Z0-9]/g, '')
  return EXODUS_BSC_NETWORKS.has(normalized)
}

const isVerifyAndApproveBlocked = (
  result: RemitanoVerificationResult | null,
  payment?: Payment | null
) => {
  if (result?.errorCode === DUPLICATE_HASH_CODE) return true
  if (!result) return true
  if (result.report && result.report.approvalReady !== true) return true
  if (result.errorCode === NATIVE_BNB_NOT_USDT_CODE) return true
  if (result.errorCode === CEX_HOT_MANUAL_REVIEW_CODE && result.report?.approvalReady === true) {
    return false
  }
  const shortfallNum = parseFloat(String(result.shortfall || 0))
  const commNum = parseFloat(String(result.availableCommission || 0))
  const canCover = result.canCoverWithCommission === true
    || (shortfallNum > 0 && hasUsdCoverage(commNum, shortfallNum))
  const isUnderpaid = payment?.status === 'underpaid'
    || result.amountStatus === 'underpaid'
    || result.errorCode === UNDERPAID_ERROR_CODE
  if (isUnderpaid && !canCover) return true
  return false
}

interface PaymentManagerProps {
  currentUser: any
}

// Advanced Filter Interface
interface AdvancedFilters {
  email: string
  userId: string
  minAmount: string
  maxAmount: string
  network: string
  currency: string
  transactionHash: string
  startDate: string
  endDate: string
}

// Manual Payment Interfaces
interface InvestmentPhase {
  id: number
  phase_number: number
  price_per_share: number
  shares_available: number
  shares_sold: number
  start_date: string
  end_date: string
  is_active: boolean
  phase_name?: string
}

interface UserSearchResult {
  id: number
  username: string
  email: string
  full_name: string | null
  phone_number: string | null
}

// Phase pricing analysis for discrepancy detection
interface PhasePricingAnalysis {
  paymentDate: string
  activePhaseAtPayment: {
    id: number
    phase_number: number
    phase_name: string
    price_per_share: number
  } | null
  usedPhase: {
    id: number | null
    phase_number: number | null
    phase_name: string | null
    price_per_share: number | null
  }
  expectedShares: number
  actualShares: number
  hasDiscrepancy: boolean
  discrepancyAmount: number
  discrepancyMessage: string
}

interface ManualPaymentForm {
  paymentType: 'crypto' | 'bank_transfer'
  selectedUser: UserSearchResult | null
  amount: string
  currency: string
  network: string
  transactionHash: string
  senderWallet: string
  receiverWallet: string
  paymentReference: string
  phaseId: string
  shares: string
  sharesOverridden: boolean
  paymentProof: File | null
  adminNotes: string
  initialStatus: 'pending' | 'approved'
  approvalNotes: string
  forceCreateDuplicate: boolean
  paymentDate: string // Custom date for the payment
  packageKind: '' | 'standard' | 'pcap'
  campaignPackage: '' | 'P1' | 'P2' | 'P3' | 'P4' | 'P5'
  campaignPackageQty: number
}

// Yoco Payment Interface
interface YocoPayment {
  id: string
  user_id: number
  investment_amount: number // ZAR cents
  fee_amount: number // ZAR cents
  total_amount: number // ZAR cents
  currency: string
  payment_method: string
  reference_number: string
  yoco_checkout_id: string
  yoco_payment_id?: string
  status: 'pending' | 'completed' | 'failed' | 'cancelled'
  phase_id?: number
  shares_allocated?: number
  webhook_processed: boolean
  created_at: string
  processed_at?: string
  metadata?: {
    usd_amount?: number
    exchange_rate?: number
    zar_base_amount?: number
    zar_fee_amount?: number
    zar_total_amount?: number
    phase_name?: string
    price_per_share?: number
  }
  users?: {
    id: number
    username: string
    email: string
    full_name?: string
  }
}

export const PaymentManager: React.FC<PaymentManagerProps> = ({ currentUser }) => {
  const [payments, setPayments] = useState<Payment[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null)
  const [paymentTypeFilter, setPaymentTypeFilter] = useState<'crypto' | 'bank' | 'phase-discrepancies'>('crypto')
  const [activeTab, setActiveTab] = useState<'pending' | 'approved' | 'rejected' | 'reversed' | 'underpaid' | 'flagged' | 'duplicates' | 'hash-removal'>('pending')

  // DEPRECATED (v4.3.61): Yoco removed
  const __yocoPayments: any[] = []
  const __yocoLoading = false
  const [rejectionReason, setRejectionReason] = useState('')
  const [showRejectionModal, setShowRejectionModal] = useState(false)
  const [processingPayment, setProcessingPayment] = useState<string | null>(null)
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info', text: string } | null>(null)
  const [showScreenshotModal, setShowScreenshotModal] = useState(false)
  const [currentScreenshot, setCurrentScreenshot] = useState<string | null>(null)

  // Owner manual approval override state
  const [showManualOverrideModal, setShowManualOverrideModal] = useState(false)
  const [manualOverridePayment, setManualOverridePayment] = useState<Payment | null>(null)
  const [manualOverrideConfirmation, setManualOverrideConfirmation] = useState('')

  // Payment Reversal State
  const [showReversalModal, setShowReversalModal] = useState(false)
  const [reversalReason, setReversalReason] = useState('')
  const [reversalConfirmation, setReversalConfirmation] = useState(false)
  const [reversalConfirmText, setReversalConfirmText] = useState('')
  const [processingReversal, setProcessingReversal] = useState(false)
  // Reverse + Reassign: target the CORRECT user the deposit should have gone to.
  const [reassignUserId, setReassignUserId] = useState<number | null>(null)
  const [reassignSearch, setReassignSearch] = useState('')
  const [reassignNewAmount, setReassignNewAmount] = useState('')

  // Duplicate Finder State (approved bank-transfer double-approvals).
  // Detection is read-only via /api/admin/payment-integrity (find_duplicate_payments);
  // cancellation reuses the pure atomic /api/admin/reverse-payment endpoint.
  interface DuplicateLeg {
    payment_id: string
    approved_at: string
    created_at: string
    keep: boolean
    seconds_after_first: number | null
  }
  interface DuplicateGroup {
    user_id: number
    username: string | null
    email: string | null
    full_name: string | null
    amount: number
    currency: string
    count: number
    window_minutes: number
    first_approved_at: string
    last_approved_at: string
    legs: DuplicateLeg[]
  }
  const [duplicateGroups, setDuplicateGroups] = useState<DuplicateGroup[]>([])
  const [duplicatesLoading, setDuplicatesLoading] = useState(false)
  const [duplicateWindowMinutes, setDuplicateWindowMinutes] = useState(30)
  const [cancellingDuplicateId, setCancellingDuplicateId] = useState<string | null>(null)
  // v4.8.84 — per-row spinner for the "Cancel Duplicate" action on an APPROVED
  // payment row in the main payments list (kept separate from the Duplicates-tab
  // cancellingDuplicateId so the two flows never fight over the same spinner).
  const [cancellingApprovedRowId, setCancellingApprovedRowId] = useState<string | null>(null)
  const [conversionCheck, setConversionCheck] = useState<{
    paymentId: string
    error?: string
    review?: {
      verified?: boolean
      requestedUsdt?: number
      reservedUsdt?: number
      availableUsdt?: number
      shares?: number
      sharePrice?: number
      packages?: Array<{ packageId: string, qty: number }> | null
      checks?: Array<{ name: string, ok: boolean, detail: string }>
    }
  } | null>(null)
  const [conversionsReadyToApprove, setConversionsReadyToApprove] = useState<Set<string>>(() => new Set())

  // Remitano Verification State
  const [showRemitanoModal, setShowRemitanoModal] = useState(false)
  const [_remitanoChargeId, setRemitanoChargeId] = useState('')
  const [remitanoVerifying, setRemitanoVerifying] = useState(false)
  const [remitanoResult, setRemitanoResult] = useState<RemitanoVerificationResult | null>(null)
  const [remitanoPayment, setRemitanoPayment] = useState<Payment | null>(null)

  // v2.9.94: Admin edit primary hash
  const [editingHashPaymentId, setEditingHashPaymentId] = useState<string | null>(null)
  const [editHashValue, setEditHashValue] = useState('')
  const [savingEditHash, setSavingEditHash] = useState(false)

  // Remitano Rejection Workflow State
  const [showRemitanoRejectionModal, setShowRemitanoRejectionModal] = useState(false)
  const [remitanoRejectionReason, setRemitanoRejectionReason] = useState('')
  const [remitanoRejectionNotes, setRemitanoRejectionNotes] = useState('')
  const [processingRemitanoRejection, setProcessingRemitanoRejection] = useState(false)

  // Delete Payment State
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteReason, setDeleteReason] = useState('')
  const [deleteConfirmed, setDeleteConfirmed] = useState(false)
  const [processingDelete, setProcessingDelete] = useState(false)

  // Flag as Duplicate State
  const [showFlagDuplicateModal, setShowFlagDuplicateModal] = useState(false)
  const [flagDuplicateReason, setFlagDuplicateReason] = useState('')
  const [processingFlagDuplicate, setProcessingFlagDuplicate] = useState(false)

  // Underpayment Handling State
  const [showUnderpaidModal, setShowUnderpaidModal] = useState(false)
  const [underpaidPayment, setUnderpaidPayment] = useState<Payment | null>(null)
  const [underpaidForm, setUnderpaidForm] = useState({
    originalAmountReceived: '',
    balanceOwed: '',
    reason: ''
  })
  const [processingUnderpaid, setProcessingUnderpaid] = useState(false)

  // v7.3.393: Per-user USDT commission balances for the Underpaid tab — populated from
  // /api/admin/load-payments and used to render shortfall coverage controls.
  const [commissionBalances, setCommissionBalances] = useState<Record<number, number>>({})

  // Additional Hash & Proof Upload State (for underpaid/balance payments)
  const [additionalHash, setAdditionalHash] = useState('')
  const [additionalProofFile, setAdditionalProofFile] = useState<File | null>(null)
  const [uploadingAdditionalProof, setUploadingAdditionalProof] = useState(false)
  const [savingAdditionalHash, setSavingAdditionalHash] = useState(false)

  // Transaction Notes State
  const [transactionNotes, setTransactionNotes] = useState<Map<string, string>>(new Map())
  const [savingNotes, setSavingNotes] = useState<Set<string>>(new Set())

  // Transaction Hash Lookup State
  const [hashLookupInput, setHashLookupInput] = useState('')
  const [hashLookupResult, setHashLookupResult] = useState<{
    found: boolean
    payment?: Payment
    // For temp bank reference lookups (user has reference but no payment yet)
    tempRefUser?: {
      id: number
      username: string
      email: string
      full_name: string | null
      temp_bank_reference: string
      payments: Payment[] // All payments for this user
    }
    // For partial username search (e.g., -MIR)
    usernameSearchResults?: {
      searchTerm: string
      matchingUsers: Array<{ id: number; username: string; email: string; full_name: string | null }>
      payments: Payment[]
    }
    // For fuzzy search results when no exact match is found
    fuzzyMatches?: Array<{
      type: 'temp_reference' | 'payment_reference' | 'username'
      reference: string
      user?: {
        id: number
        username: string
        email: string
        full_name: string | null
        temp_bank_reference?: string
      }
      payment?: Payment
      matchResult: FuzzyMatchResult
    }>
    loading: boolean
    error?: string
  }>({ found: false, loading: false })
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null)

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [totalCount, setTotalCount] = useState(0)

  // Sorting State
  const [sortField, setSortField] = useState<'created_at' | 'approved_at'>('created_at')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  // Duplicate Flags State
  const [duplicateFlags, setDuplicateFlags] = useState<Set<string>>(new Set())
  const [duplicateRefs, setDuplicateRefs] = useState<Map<string, number>>(new Map())

  // Search State
  const [searchQuery, setSearchQuery] = useState('')
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null)
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [matchedFields, setMatchedFields] = useState<Map<string, string[]>>(new Map())

  // Share Validation State
  const [shareValidationResults, setShareValidationResults] = useState<Map<string, {
    isValid: boolean
    expectedShares: number
    recordedShares: number
    phaseName: string
    phasePrice: number
    difference: number
  }>>(new Map())

  // Hash Lookup Phase Pricing Analysis State
  const [hashLookupPricingAnalysis, setHashLookupPricingAnalysis] = useState<PhasePricingAnalysis | null>(null)
  const [showShareAdjustmentModal, setShowShareAdjustmentModal] = useState(false)
  const [shareAdjustmentNewShares, setShareAdjustmentNewShares] = useState('')
  const [shareAdjustmentReason, setShareAdjustmentReason] = useState('')
  const [shareAdjustmentProcessing, setShareAdjustmentProcessing] = useState(false)

  // Advanced Filters State
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  const [filterLoading, setFilterLoading] = useState(false)
  const currentAdminEmail = String(currentUser?.email || currentUser?.adminUser?.email || '').trim().toLowerCase()
  const canUseManualOverride = AUTHORIZED_MANUAL_OVERRIDE_EMAILS.has(currentAdminEmail)
  const [filters, setFilters] = useState<AdvancedFilters>({
    email: '',
    userId: '',
    minAmount: '',
    maxAmount: '',
    network: '',
    currency: '',
    transactionHash: '',
    startDate: '',
    endDate: ''
  })

  // Manual Payment Creation State
  const [showManualPaymentModal, setShowManualPaymentModal] = useState(false)
  const [manualPaymentStep, setManualPaymentStep] = useState(1)
  const [allUsers, setAllUsers] = useState<UserSearchResult[]>([])
  const [filteredUsers, setFilteredUsers] = useState<UserSearchResult[]>([])
  const [userSearchQuery, setUserSearchQuery] = useState('')
  const [usersLoading, setUsersLoading] = useState(false)
  const [investmentPhases, setInvestmentPhases] = useState<InvestmentPhase[]>([])
  const [duplicateHashCheck, setDuplicateHashCheck] = useState<{ exists: boolean, paymentId?: string, checkFailed?: boolean, message?: string }>({ exists: false })
  const [manualPaymentForm, setManualPaymentForm] = useState<ManualPaymentForm>({
    paymentType: 'crypto',
    selectedUser: null,
    amount: '',
    currency: 'USDT',
    network: 'TRC20',
    transactionHash: '',
    senderWallet: '',
    receiverWallet: '',
    paymentReference: '',
    phaseId: '',
    shares: '',
    sharesOverridden: false,
    paymentProof: null,
    adminNotes: '',
    initialStatus: 'pending',
    approvalNotes: '',
    forceCreateDuplicate: false,
    paymentDate: '',
    packageKind: '',
    campaignPackage: '',
    campaignPackageQty: 1
  })
  const [manualPaymentSubmitting, setManualPaymentSubmitting] = useState(false)

  useEffect(() => {
    setCurrentPage(1) // Reset to page 1 when tab or payment type changes

    // DEPRECATED (v4.3.61): Yoco removed

    // Duplicates tab loads its own read-only dataset (bank double-approvals)
    // and does not use the standard paginated payment loader/filters.
    if (activeTab === 'duplicates') {
      loadDuplicatePayments()
      return
    }

    // Load saved filters from localStorage first
    const savedFilters = localStorage.getItem('paymentManagerFilters')
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters)
        setFilters(parsed)
        // If there are active filters, applyAdvancedFilters will be called by the filters useEffect
        // Otherwise, we need to load payments normally
        if (!Object.values(parsed).some(v => v !== '')) {
          loadPayments()
        }
      } catch (e) {
        console.error('Failed to load saved filters:', e)
        loadPayments()
      }
    } else {
      loadPayments()
    }
  }, [activeTab, paymentTypeFilter])

  useEffect(() => {
    // Payment type changed — reload

    // When pagination/sort changes, respect active filters
    const hasActiveFilters = Object.values(filters).some(v => v !== '')
    if (hasActiveFilters) {
      applyAdvancedFilters()
    } else {
      loadPayments()
    }
  }, [currentPage, pageSize, sortField, sortDirection, paymentTypeFilter])

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('paymentManagerFilters', JSON.stringify(filters))
  }, [filters])

  // Debounced filter application
  useEffect(() => {
    const timer = setTimeout(() => {
      applyAdvancedFilters()
    }, 300)
    return () => clearTimeout(timer)
  }, [filters])

  // Load investment phases and users when manual payment modal opens
  useEffect(() => {
    if (showManualPaymentModal) {
      if (investmentPhases.length === 0) {
        loadInvestmentPhases()
      }
      if (allUsers.length === 0) {
        loadAllUsers()
      }
    }
  }, [showManualPaymentModal])

  // AJAX filter users as user types - use functional update to avoid stale closure
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!userSearchQuery || userSearchQuery.trim().length === 0) {
        setFilteredUsers(allUsers)
        return
      }

      const trimmedQuery = userSearchQuery.trim().toLowerCase()
      const isNumeric = /^\d+$/.test(trimmedQuery)

      const filtered = allUsers.filter(user => {
        if (isNumeric) {
          // Search by user ID (exact match)
          return user.id.toString() === trimmedQuery
        } else {
          // Search by email or username (partial match)
          return (
            (user.email && user.email.toLowerCase().includes(trimmedQuery)) ||
            (user.username && user.username.toLowerCase().includes(trimmedQuery)) ||
            (user.full_name && user.full_name.toLowerCase().includes(trimmedQuery))
          )
        }
      })

      setFilteredUsers(filtered)
    }, 300)
    return () => clearTimeout(timer)
  }, [userSearchQuery, allUsers])

  // Check for duplicate hash
  useEffect(() => {
    const timer = setTimeout(() => {
      checkDuplicateHash(manualPaymentForm.transactionHash)
    }, 500)
    return () => clearTimeout(timer)
  }, [manualPaymentForm.transactionHash])

  // Auto-calculate shares when amount, phase, or phases list changes
  useEffect(() => {
    if (!manualPaymentForm.sharesOverridden && manualPaymentForm.amount && manualPaymentForm.phaseId && investmentPhases.length > 0) {
      const amount = parseFloat(manualPaymentForm.amount)
      const phase = investmentPhases.find(p => p.id === parseInt(manualPaymentForm.phaseId))

      if (amount > 0 && phase && phase.price_per_share > 0) {
        const calculatedShares = amount / phase.price_per_share
        setManualPaymentForm(prev => ({ ...prev, shares: calculatedShares.toFixed(2) }))
      }
    }
  }, [manualPaymentForm.amount, manualPaymentForm.phaseId, manualPaymentForm.sharesOverridden, investmentPhases])

  // CRITICAL FIX: Auto-set payment date when a past phase is selected
  // This ensures the timestamp-based phase detection uses the correct phase price
  useEffect(() => {
    if (manualPaymentForm.phaseId && investmentPhases.length > 0) {
      const selectedPhase = investmentPhases.find(p => p.id === parseInt(manualPaymentForm.phaseId))

      if (selectedPhase) {
        const today = new Date()
        const todayStr = today.toISOString().split('T')[0]

        // If the selected phase is NOT the currently active phase, we need to backdate
        if (!selectedPhase.is_active && selectedPhase.end_date) {
          // Use the last day of the phase (end_date) to ensure timestamp detection works
          const phaseEndDate = new Date(selectedPhase.end_date)
          // Subtract 1 minute to be safely within the phase
          phaseEndDate.setMinutes(phaseEndDate.getMinutes() - 1)
          const backdatedStr = phaseEndDate.toISOString().split('T')[0]

          // Only auto-set if the current paymentDate would NOT fall within the selected phase
          const currentPaymentDate = manualPaymentForm.paymentDate ? new Date(manualPaymentForm.paymentDate) : today
          const phaseStart = selectedPhase.start_date ? new Date(selectedPhase.start_date) : null
          const phaseEnd = new Date(selectedPhase.end_date)

          const isCurrentDateInPhase = (
            (!phaseStart || currentPaymentDate >= phaseStart) &&
            currentPaymentDate <= phaseEnd
          )

          if (!isCurrentDateInPhase) {
            console.log(`📅 [MANUAL PAYMENT] Auto-backdating payment to ${backdatedStr} for ${selectedPhase.phase_name} (was: ${manualPaymentForm.paymentDate || todayStr})`)
            setManualPaymentForm(prev => ({ ...prev, paymentDate: backdatedStr }))
          }
        } else if (selectedPhase.is_active) {
          // For active phase, if paymentDate is in the future or empty, set to today
          if (!manualPaymentForm.paymentDate) {
            setManualPaymentForm(prev => ({ ...prev, paymentDate: todayStr }))
          }
        }
      }
    }
  }, [manualPaymentForm.phaseId, investmentPhases])

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer)
      }
    }
  }, [debounceTimer, searchDebounceTimer])

  // Deep-link support: when opened via the Reverse Payment modal with a
  // #payments&lookup=<reference-or-uuid> hash, pre-fill the Transaction Hash
  // Lookup and auto-run it so the admin lands directly on the exact payment.
  useEffect(() => {
    try {
      const hash = typeof window !== 'undefined' ? window.location.hash : ''
      if (!hash || hash.length <= 1) return
      const params = hash.substring(1).split('&')
      const lookupPart = params.find(p => p.startsWith('lookup='))
      if (!lookupPart) return
      const lookupValue = decodeURIComponent(lookupPart.slice('lookup='.length)).trim()
      if (!lookupValue) return
      setHashLookupInput(lookupValue)
      handleHashLookup(lookupValue)
    } catch (e) {
      console.error('[PaymentManager] Deep-link lookup failed:', e)
    }
    // Run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Debounce search query
  useEffect(() => {
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer)
    }

    const timer = setTimeout(async () => {
      setDebouncedSearchQuery(searchQuery)
      setCurrentPage(1) // Reset to page 1 when search changes
      if (searchQuery && searchQuery.trim().length >= 2) {
        logSearch('payments', searchQuery.trim())
      }

      // v4.7.98: If query is a payment UUID, fetch it server-side and prepend
      // to the loaded payments list so the client-side filter can show it
      // regardless of which tab/page the admin is on (e.g. reversed payments
      // not visible from the Approved tab).
      const trimmed = (searchQuery || '').trim()
      const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      if (UUID_REGEX.test(trimmed)) {
        try {
          const sessionId = getServerSessionId()
          const resp = await fetch('/api/admin/payment-operations', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(sessionId ? { 'X-Session-Id': sessionId } : {})
            },
            body: JSON.stringify({
              operation: 'comprehensive_hash_lookup',
              data: { searchTerm: trimmed }
            })
          })
          const json = await resp.json()
          const r = (json && json.data) || json
          if (r && r.found && r.payment && r.payment.id) {
            setPayments(prev => {
              if (prev.some(p => p.id === r.payment.id)) return prev
              return [r.payment as Payment, ...prev]
            })
          }
        } catch (e) {
          console.error('[PaymentManager] UUID lookup failed:', e)
        }
      }
    }, 400) // 400ms debounce delay

    setSearchDebounceTimer(timer)

    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [searchQuery])

  const applyAdvancedFilters = async () => {
    // Check if any filters are active
    const hasActiveFilters = Object.values(filters).some(v => v !== '')

    if (!hasActiveFilters) {
      loadPayments() // No filters - load normally
      return
    }

    try {
      setFilterLoading(true)
      const sessionId = getServerSessionId()

      // Build filter parameters
      const filterParams: any = {
        p_status: activeTab
      }

      if (filters.email) filterParams.p_email = filters.email.toLowerCase().trim()
      if (filters.userId) filterParams.p_user_id = parseInt(filters.userId)
      if (filters.minAmount) filterParams.p_min_amount = parseFloat(filters.minAmount)
      if (filters.maxAmount) filterParams.p_max_amount = parseFloat(filters.maxAmount)
      if (filters.network) filterParams.p_network = filters.network
      if (filters.currency) filterParams.p_currency = filters.currency
      if (filters.transactionHash) filterParams.p_transaction_hash = filters.transactionHash.toLowerCase().trim()
      if (filters.startDate) filterParams.p_start_date = filters.startDate
      if (filters.endDate) filterParams.p_end_date = filters.endDate

      // Call API endpoint with filters
      const response = await fetch('/api/admin/payment-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'filter_payments',
          data: { filterParams, paymentTypeFilter }
        })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to filter payments')

      setPayments(result.data.payments || [])
      setTotalCount(result.data.totalCount || 0)
    } catch (err) {
      console.error('Error applying filters:', err)
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to apply filters'
      })
      setTimeout(() => setMessage(null), 5000)
    } finally {
      setFilterLoading(false)
    }
  }

  const clearAllFilters = () => {
    setFilters({
      email: '',
      userId: '',
      minAmount: '',
      maxAmount: '',
      network: '',
      currency: '',
      transactionHash: '',
      startDate: '',
      endDate: ''
    })
    setSearchQuery('')
    loadPayments()
  }

  const getActiveFilterCount = (): number => {
    return Object.values(filters).filter(v => v !== '').length
  }

  // Manual Payment Functions
  const loadAllUsers = async () => {
    try {
      console.log('🔄 [MANUAL PAYMENT] Starting to load users...')
      setUsersLoading(true)
      const sessionId = getServerSessionId()

      console.log('🔄 [MANUAL PAYMENT] Fetching users from API...')

      const response = await fetch('/api/admin/payment-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'load_all_users', data: {} })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to load users')

      const allUsersData = result.data.users || []

      console.log('✅ [MANUAL PAYMENT] Loaded all users:', allUsersData.length)
      console.log('📊 [MANUAL PAYMENT] First 3 users:', allUsersData.slice(0, 3))

      setAllUsers(allUsersData)
      setFilteredUsers(allUsersData)

      console.log('✅ [MANUAL PAYMENT] State updated with users')
    } catch (err) {
      console.error('❌ [MANUAL PAYMENT] Error loading users:', err)
      setMessage({
        type: 'error',
        text: `Failed to load users: ${err instanceof Error ? err.message : 'Unknown error'}`
      })
      setTimeout(() => setMessage(null), 5000)
    } finally {
      setUsersLoading(false)
      console.log('🏁 [MANUAL PAYMENT] User loading complete')
    }
  }

  const loadInvestmentPhases = async () => {
    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/payment-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'load_investment_phases', data: {} })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to load phases')

      setInvestmentPhases(result.data.phases || [])
    } catch (err) {
      console.error('Error loading investment phases:', err)
    }
  }

  const checkDuplicateHash = async (hash: string) => {
    if (!hash || hash.trim().length === 0) {
      setDuplicateHashCheck({ exists: false })
      return
    }

    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/payment-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'check_duplicate_hash', data: { hash: hash.trim() } })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to check duplicate hash')

      if (result.data.exists) {
        setDuplicateHashCheck({ exists: true, paymentId: result.data.paymentId, message: result.data.message || undefined })
      } else {
        setDuplicateHashCheck({ exists: false })
      }
    } catch (err) {
      console.error('❌ [MANUAL PAYMENT] Error checking duplicate hash:', err)
      setDuplicateHashCheck({ exists: false, checkFailed: true })
    }
  }

  const calculateShares = (): number => {
    const amount = parseFloat(manualPaymentForm.amount)
    const phase = investmentPhases.find(p => p.id === parseInt(manualPaymentForm.phaseId))

    if (!amount || !phase || amount <= 0 || phase.price_per_share <= 0) {
      return 0
    }

    return amount / phase.price_per_share
  }

  // Generate blockchain explorer URL based on network
  const getBlockchainExplorerUrl = (network: string, hash: string): string | null => {
    if (!hash) return null
    const net = network?.toUpperCase() || ''
    if (net.includes('BSC') || net.includes('BEP20')) return `https://bscscan.com/tx/${hash}`
    if (net.includes('TRC') || net.includes('TRON')) return `https://tronscan.org/#/transaction/${hash}`
    if (net.includes('ERC') || net.includes('ETH')) return `https://etherscan.io/tx/${hash}`
    if (net.includes('POL') || net.includes('POLYGON') || net.includes('MATIC')) return `https://polygonscan.com/tx/${hash}`
    if (net.includes('ARBITRUM')) return `https://arbiscan.io/tx/${hash}`
    return null
  }

  // Analyze phase pricing for discrepancy detection
  const analyzePhasePricing = async (payment: Payment): Promise<PhasePricingAnalysis | null> => {
    try {
      const sessionId = getServerSessionId()
      const paymentDate = new Date(payment.created_at)

      // Get all phases via API
      const response = await fetch('/api/admin/payment-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'load_investment_phases', data: {} })
      })

      const result = await response.json()
      if (!response.ok) return null

      const phases = result.data.phases || []
      if (!phases.length) return null

      // Find the phase that was active at payment creation time
      let activePhaseAtPayment = phases.find((p: any) => {
        const startDate = p.start_date ? new Date(p.start_date) : null
        const endDate = p.end_date ? new Date(p.end_date) : null

        if (!startDate && !p.is_active) return false
        if (startDate && paymentDate < startDate) return false
        if (endDate && paymentDate > endDate) return false
        return true
      })

      // Fallback: Find the most recent phase that had started before the payment date
      if (!activePhaseAtPayment) {
        const phasesWithDates = phases.filter((p: any) => p.start_date && new Date(p.start_date) <= paymentDate)
        if (phasesWithDates.length > 0) {
          activePhaseAtPayment = phasesWithDates.reduce((latest: any, p: any) =>
            p.phase_number > latest.phase_number ? p : latest
          )
        } else {
          activePhaseAtPayment = phases.find((p: any) => p.is_active) || phases[0]
        }
      }

      // Calculate expected shares based on phase that was active at payment
      const expectedShares = activePhaseAtPayment
        ? payment.amount / activePhaseAtPayment.price_per_share
        : 0
      const actualShares = payment.shares_to_purchase || 0

      const hasDiscrepancy = Math.abs(expectedShares - actualShares) > 0.01
      const discrepancyAmount = expectedShares - actualShares

      let discrepancyMessage = ''
      if (hasDiscrepancy) {
        if (discrepancyAmount > 0) {
          discrepancyMessage = `User should have received ${discrepancyAmount.toFixed(2)} more shares`
        } else {
          discrepancyMessage = `User received ${Math.abs(discrepancyAmount).toFixed(2)} more shares than expected`
        }
      }

      return {
        paymentDate: payment.created_at,
        activePhaseAtPayment: activePhaseAtPayment ? {
          id: activePhaseAtPayment.id,
          phase_number: activePhaseAtPayment.phase_number,
          phase_name: activePhaseAtPayment.phase_name,
          price_per_share: activePhaseAtPayment.price_per_share
        } : null,
        usedPhase: {
          id: null,
          phase_number: null,
          phase_name: null,
          price_per_share: null
        },
        expectedShares,
        actualShares,
        hasDiscrepancy,
        discrepancyAmount,
        discrepancyMessage
      }
    } catch (err) {
      console.error('Error analyzing phase pricing:', err)
      return null
    }
  }

  // Adjust user shares for a payment
  // Interface for phase fix data from PhaseDiscrepancyAudit
  interface PhaseFixData {
    paymentId: string
    newShares: number
    reason: string
    correctPhaseId: number
    correctPricePerShare: number
    oldPhaseId: number
    oldPricePerShare: number
    oldShares: number
    sharePurchaseId: number | null
  }

  const adjustPaymentShares = async (fixData: PhaseFixData) => {
    const { paymentId, newShares, reason, correctPhaseId, correctPricePerShare, oldPhaseId, oldPricePerShare, oldShares, sharePurchaseId } = fixData

    try {
      setShareAdjustmentProcessing(true)
      const sessionId = getServerSessionId()

      const response = await fetch('/api/admin/payment-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'adjust_payment_shares',
          data: {
            paymentId,
            newShares,
            reason,
            correctPhaseId,
            correctPricePerShare,
            oldPhaseId,
            oldPricePerShare,
            oldShares,
            sharePurchaseId,
            adminId: currentUser?.id,
            adminEmail: currentUser?.email
          }
        })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to adjust shares')

      const { commissionsFixed, totalCommissionSharesAdjusted, userId } = result.data

      // Log the admin action
      await logAdminAction(
        currentUser?.email || 'unknown',
        'PHASE_CORRECTION',
        'payment',
        paymentId,
        {
          user_id: userId,
          reason: reason,
          commissions_fixed: commissionsFixed,
          commission_shares_adjusted: totalCommissionSharesAdjusted
        },
        { phase_id: oldPhaseId, price_per_share: oldPricePerShare, shares: oldShares },
        { phase_id: correctPhaseId, price_per_share: correctPricePerShare, shares: newShares }
      )

      const commissionMsg = commissionsFixed > 0
        ? ` + ${commissionsFixed} commission records (+${totalCommissionSharesAdjusted.toFixed(4)} shares)`
        : ''
      setMessage({ type: 'success', text: `Phase corrected: ${oldShares} → ${newShares} shares, Phase ${oldPhaseId} → ${correctPhaseId}${commissionMsg} for payment ${paymentId.slice(0, 8)}...` })
      setShowShareAdjustmentModal(false)
      setShareAdjustmentNewShares('')
      setShareAdjustmentReason('')

      // Refresh the hash lookup result
      if (hashLookupResult.payment) {
        handleHashLookup(hashLookupInput)
      }

    } catch (err: any) {
      console.error('Error fixing phase discrepancy:', err)
      setMessage({ type: 'error', text: `Failed to fix phase: ${err.message}` })
    } finally {
      setShareAdjustmentProcessing(false)
    }
  }

  // Simple manual share adjustment (for Hash Lookup modal - doesn't change phase/price)
  const manualShareAdjustment = async (paymentId: string, newShares: number, reason: string) => {
    try {
      setShareAdjustmentProcessing(true)
      const sessionId = getServerSessionId()

      const response = await fetch('/api/admin/payment-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'manual_share_adjustment',
          data: { paymentId, newShares, reason, adminId: currentUser?.id }
        })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to adjust shares')

      const { oldShares } = result.data

      // Log the admin action
      await logAdminAction(
        currentUser?.email || 'unknown',
        'MANUAL_SHARE_ADJUSTMENT',
        'payment',
        paymentId,
        { reason: reason },
        { shares: oldShares },
        { shares: newShares }
      )

      setMessage({ type: 'success', text: `Shares adjusted: ${oldShares} → ${newShares} for payment ${paymentId.slice(0, 8)}...` })
      setShowShareAdjustmentModal(false)
      setShareAdjustmentNewShares('')
      setShareAdjustmentReason('')

      // Refresh the hash lookup result
      if (hashLookupResult.payment) {
        handleHashLookup(hashLookupInput)
      }

    } catch (err: any) {
      console.error('Error adjusting shares:', err)
      setMessage({ type: 'error', text: `Failed to adjust shares: ${err.message}` })
    } finally {
      setShareAdjustmentProcessing(false)
    }
  }

  const resetManualPaymentForm = () => {
    // Default to today's date in YYYY-MM-DD format
    const today = new Date().toISOString().split('T')[0]

    setManualPaymentForm({
      paymentType: 'crypto',
      selectedUser: null,
      amount: '',
      currency: 'USDT',
      network: 'TRC20',
      transactionHash: '',
      senderWallet: '',
      receiverWallet: '',
      paymentReference: '',
      phaseId: '',
      shares: '',
      sharesOverridden: false,
      paymentProof: null,
      adminNotes: '',
      initialStatus: 'pending',
      approvalNotes: '',
      forceCreateDuplicate: false,
      paymentDate: today,
      packageKind: '',
      campaignPackage: '',
      campaignPackageQty: 1
    })
    setManualPaymentStep(1)
    setUserSearchQuery('')
    setFilteredUsers(allUsers)
    setDuplicateHashCheck({ exists: false })
  }

  const applyManualPackagePick = (kind: 'standard' | 'pcap', packageId?: string, qty?: number) => {
    if (kind === 'standard') {
      setManualPaymentForm((prev) => ({
        ...prev,
        packageKind: 'standard',
        campaignPackage: '',
        campaignPackageQty: 1,
        sharesOverridden: false,
      }))
      return
    }
    const q = Math.max(1, Math.floor(Number(qty) || 1))
    const breakdown = computeProductionCapitalBreakdown(packageId, q)
    if (!breakdown) return
    setManualPaymentForm((prev) => ({
      ...prev,
      packageKind: 'pcap',
      campaignPackage: breakdown.packageId as ManualPaymentForm['campaignPackage'],
      campaignPackageQty: breakdown.qty,
      amount: String(breakdown.priceUsd),
      shares: String(breakdown.paidShares),
      sharesOverridden: true,
    }))
  }

  const validateStep = (step: number): boolean => {
    if (step === 1) {
      return manualPaymentForm.selectedUser !== null
    }

    if (step === 2) {
      const amount = parseFloat(manualPaymentForm.amount)
      const hasValidAmount = !isNaN(amount) && amount >= 1
      const hasPhase = manualPaymentForm.phaseId !== ''

      let hasValidShares = true
      if (manualPaymentForm.sharesOverridden) {
        const shares = parseFloat(manualPaymentForm.shares)
        hasValidShares = !isNaN(shares) && shares > 0
      }

      const hasPackage = manualPaymentForm.packageKind === 'standard'
        || (manualPaymentForm.packageKind === 'pcap' && isProductionCapitalPackageId(manualPaymentForm.campaignPackage))

      // Payment type specific validation
      if (manualPaymentForm.paymentType === 'crypto') {
        const hasWallets = (manualPaymentForm.senderWallet?.trim().length || 0) > 0 &&
                          (manualPaymentForm.receiverWallet?.trim().length || 0) > 0
        const hasHash = (manualPaymentForm.transactionHash?.trim().length || 0) > 0
        const hashClear = !duplicateHashCheck.exists && !duplicateHashCheck.checkFailed
        return hasValidAmount && hasPhase && hasPackage && hasWallets && hasValidShares && hasHash && hashClear
      } else {
        // Bank transfer
        const hasReference = (manualPaymentForm.paymentReference?.trim().length || 0) > 0
        const hasPaymentProof = manualPaymentForm.paymentProof !== null
        return hasValidAmount && hasPhase && hasPackage && hasReference && hasPaymentProof && hasValidShares
      }
    }

    return true
  }

  const submitManualPayment = async () => {
    // Validate based on payment type
    if (!manualPaymentForm.selectedUser) {
      setMessage({ type: 'error', text: 'Please select a user' })
      setTimeout(() => setMessage(null), 5000)
      return
    }

    if (manualPaymentForm.packageKind !== 'standard' && manualPaymentForm.packageKind !== 'pcap') {
      setMessage({ type: 'error', text: 'Select Standard or a Production Capital package. The system will not pick the highest package on its own.' })
      setTimeout(() => setMessage(null), 5000)
      return
    }
    if (manualPaymentForm.packageKind === 'pcap' && !isProductionCapitalPackageId(manualPaymentForm.campaignPackage)) {
      setMessage({ type: 'error', text: 'Select the Production Capital package for this user.' })
      setTimeout(() => setMessage(null), 5000)
      return
    }

    if (manualPaymentForm.paymentType === 'crypto') {
      // Crypto payment validation
      if (!manualPaymentForm.senderWallet?.trim() || !manualPaymentForm.receiverWallet?.trim()) {
        setMessage({ type: 'error', text: 'Sender and receiver wallet addresses are required for crypto payments' })
        setTimeout(() => setMessage(null), 5000)
        return
      }
      if (!manualPaymentForm.transactionHash?.trim()) {
        setMessage({ type: 'error', text: 'Transaction hash is required for crypto payments' })
        setTimeout(() => setMessage(null), 5000)
        return
      }
      if (duplicateHashCheck.exists) {
        setMessage({ type: 'error', text: duplicateHashCheck.message || 'Transaction hash already exists. Create is refused.' })
        setTimeout(() => setMessage(null), 5000)
        return
      }
      if (duplicateHashCheck.checkFailed) {
        setMessage({ type: 'error', text: 'Hash uniqueness check failed. Create is refused until the check succeeds.' })
        setTimeout(() => setMessage(null), 5000)
        return
      }
    } else {
      // Bank transfer validation
      if (!manualPaymentForm.paymentReference?.trim()) {
        setMessage({ type: 'error', text: 'Payment reference is required for bank transfers' })
        setTimeout(() => setMessage(null), 5000)
        return
      }
      if (!manualPaymentForm.paymentProof) {
        setMessage({ type: 'error', text: 'Payment proof is required for bank transfers' })
        setTimeout(() => setMessage(null), 5000)
        return
      }
    }

    try {
      setManualPaymentSubmitting(true)
      const sessionId = getServerSessionId()
      const selectedPhase = investmentPhases.find(p => p.id === parseInt(manualPaymentForm.phaseId))

      // Validate file type if provided
      if (manualPaymentForm.paymentProof) {
        if (!manualPaymentForm.paymentProof.type.startsWith('image/') && manualPaymentForm.paymentProof.type !== 'application/pdf') {
          throw new Error(`Invalid file type: ${manualPaymentForm.paymentProof.type}. Only images and PDFs are allowed.`)
        }
      }

      // Calculate shares
      const finalShares = manualPaymentForm.sharesOverridden
        ? parseFloat(manualPaymentForm.shares)
        : calculateShares()

      if (!Number.isFinite(finalShares) || finalShares <= 0) {
        throw new Error('Invalid share calculation for manual payment. Please verify amount, phase, and shares.')
      }

      const amountNumeric = parseFloat(manualPaymentForm.amount)
      if (!Number.isFinite(amountNumeric) || amountNumeric <= 0) {
        throw new Error('Invalid amount for manual payment.')
      }

      // Get admin database user ID
      const adminDatabaseId = currentUser.database_user?.id || currentUser.id

      // Payment timestamp
      const paymentTimestamp = manualPaymentForm.paymentDate
        ? new Date(manualPaymentForm.paymentDate + 'T' + new Date().toISOString().split('T')[1]).toISOString()
        : new Date().toISOString()

      const chosenPackage = applyChosenManualPackage({
        manual_entry: true,
        payment_type: manualPaymentForm.paymentType,
        created_by_admin_id: adminDatabaseId,
        investment_phase_id: parseInt(manualPaymentForm.phaseId),
        override_shares: manualPaymentForm.sharesOverridden,
        approval_notes: manualPaymentForm.approvalNotes || null,
        ...(manualPaymentForm.paymentType === 'bank_transfer'
          ? { payment_reference: manualPaymentForm.paymentReference.trim() }
          : {}),
      }, {
        packageKind: manualPaymentForm.packageKind,
        packageId: manualPaymentForm.campaignPackage,
        qty: manualPaymentForm.campaignPackageQty,
      })
      if (!chosenPackage.chosen) {
        throw new Error(chosenPackage.error || 'Choose Standard or a Production Capital package.')
      }
      const stampedNotes = stampManualPaymentPcapNotes(chosenPackage.notes, {
        paidShares: finalShares,
        amountUsd: amountNumeric,
        now: new Date(paymentTimestamp),
      }).notes

      // Build payment data
      const paymentData: any = {
        user_id: manualPaymentForm.selectedUser.id,
        amount: amountNumeric,
        currency: manualPaymentForm.currency,
        shares_to_purchase: finalShares,
        status: manualPaymentForm.initialStatus,
        admin_notes: manualPaymentForm.adminNotes || null,
        verification_status: 'manual_entry',
        created_at: paymentTimestamp
      }

      // Add payment-type-specific fields
      if (manualPaymentForm.paymentType === 'crypto') {
        paymentData.network = manualPaymentForm.network
        paymentData.transaction_hash = manualPaymentForm.transactionHash.trim() || null
        paymentData.sender_wallet = manualPaymentForm.senderWallet.trim()
        paymentData.receiver_wallet = manualPaymentForm.receiverWallet.trim()
        paymentData.transaction_notes = JSON.stringify(stampedNotes)
      } else {
        paymentData.network = 'BANK_TRANSFER'
        paymentData.transaction_hash = manualPaymentForm.paymentReference.trim()
        paymentData.sender_wallet = 'BANK_TRANSFER'
        paymentData.receiver_wallet = 'BANK_TRANSFER'
        paymentData.transaction_notes = JSON.stringify(stampedNotes)
      }

      // If approved, add approval fields
      if (manualPaymentForm.initialStatus === 'approved') {
        paymentData.approved_at = paymentTimestamp
        paymentData.approved_by_admin_id = adminDatabaseId
      }

      // Convert file to base64 if provided
      let fileBase64 = null
      let fileName = null
      let contentType = null
      if (manualPaymentForm.paymentProof) {
        const arrayBuffer = await manualPaymentForm.paymentProof.arrayBuffer()
        // SAFE base64 conversion — process in chunks to avoid "Maximum call stack size exceeded"
        // (spreading a large Uint8Array passes every byte as an argument, which overflows the stack)
        const bytes = new Uint8Array(arrayBuffer)
        let binary = ''
        const chunkSize = 8192
        for (let i = 0; i < bytes.length; i += chunkSize) {
          binary += String.fromCharCode(...bytes.slice(i, i + chunkSize))
        }
        fileBase64 = btoa(binary)
        fileName = manualPaymentForm.paymentProof.name
        contentType = manualPaymentForm.paymentProof.type
      }

      // Calculate bonus and total shares
      const baseShares = finalShares
      const bonusShares = calculateBonusShares(baseShares)
      const totalShares = calculateTotalShares(baseShares)

      // Submit via API
      const response = await fetch('/api/admin/payment-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'submit_manual_payment',
          data: {
            paymentData,
            phaseId: parseInt(manualPaymentForm.phaseId),
            userId: manualPaymentForm.selectedUser.id,
            amount: amountNumeric,
            shares: { baseShares, bonusShares, totalShares },
            adminId: currentUser.id,
            adminEmail: currentUser.email,
            processApproved: manualPaymentForm.initialStatus === 'approved',
            paymentType: manualPaymentForm.paymentType === 'crypto'
              ? `${manualPaymentForm.network} (${manualPaymentForm.currency})`
              : `Bank Transfer (${manualPaymentForm.currency})`,
            paymentTimestamp,
            fileBase64,
            fileName,
            contentType,
            packageKind: manualPaymentForm.packageKind,
            campaignPackage: manualPaymentForm.campaignPackage,
            campaignPackageQty: manualPaymentForm.campaignPackageQty
          }
        })
      })

      const result = await readApiResponse<any>(response, 'Failed to create manual payment')

      console.log('✅ [MANUAL PAYMENT] Created successfully:', result.data.paymentId)

      // Send payment approval email if approved
      if (manualPaymentForm.initialStatus === 'approved') {
        try {
          const emailResponse = await fetch('/api/send-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
            body: JSON.stringify({
              emailType: 'payment_approval',
              data: {
                email: manualPaymentForm.selectedUser.email,
                fullName: manualPaymentForm.selectedUser.full_name || manualPaymentForm.selectedUser.username,
                amount: amountNumeric,
                shares: totalShares,
                pricePerShare: selectedPhase?.price_per_share,
                network: manualPaymentForm.paymentType === 'crypto' ? manualPaymentForm.network : 'Bank Transfer',
                transactionHash: manualPaymentForm.paymentType === 'crypto' ? manualPaymentForm.transactionHash : manualPaymentForm.paymentReference,
                processedDate: new Date().toISOString(),
                dashboardUrl: `${typeof window !== 'undefined' ? window.location.origin : 'https://aureus.africa'}/dashboard`
              }
            })
          })
          if (!emailResponse.ok) console.error('Failed to send approval email')
        } catch (emailError) {
          console.error('Error sending approval email:', emailError)
        }
      }

      setMessage({
        type: 'success',
        text: `✅ Manual payment created successfully (Payment ID: ${result.data.paymentId})`
      })
      setTimeout(() => setMessage(null), 5000)

      // Reset and close modal
      resetManualPaymentForm()
      setShowManualPaymentModal(false)

      // Refresh payment list
      loadPayments()
    } catch (err) {
      console.error('Error creating manual payment:', err)
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to create manual payment'
      })
      setTimeout(() => setMessage(null), 5000)
    } finally {
      setManualPaymentSubmitting(false)
    }
  }

  const loadPayments = async () => {
    setLoading(true)
    setMessage(null)

    try {
      // Call API endpoint for loading payments (server-side uses service role key)
      const sessionId = getServerSessionId()
      const response = await fetch(`${API_BASE_URL}/api/admin/load-payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          activeTab,
          paymentTypeFilter,
          currentPage,
          pageSize,
          sortField,
          sortDirection
        })
      })

      const result = await readApiResponse<any>(response, 'Failed to load payments')

      const { payments: loadedPayments, totalCount: count, commissionBalances: loadedBalances } = result.data

      setTotalCount(count || 0)
      setPayments(loadedPayments || [])
      // v7.3.393: capture per-user USDT commission balances when present (Underpaid tab only)
      setCommissionBalances(loadedBalances || {})

      // Detect duplicates by reference number
      detectDuplicates(loadedPayments || [])

      // Load duplicate flags from database
      loadDuplicateFlags(loadedPayments || [])

      // Validate share calculations for all payments
      await validateShareCalculations(loadedPayments || [])

      if ((loadedPayments || []).length === 0) {
        setMessage({ type: 'info', text: `No ${activeTab} payments found` })
      }

    } catch (error: any) {
      console.error('Error loading payments:', error)
      setMessage({
        type: 'error',
        text: error.message || 'Failed to load payments. Please check your connection and try again.'
      })
      setPayments([]) // Clear payments on error
    } finally {
      setLoading(false)
    }
  }
  // REMOVED: Inline loadPayments implementation - now using /api/admin/load-payments.js
  // [All server-side logic moved to API endpoint for security]

  // Load Yoco Card Payments
  const __loadYocoPayments = async () => {
    const setYocoLoading = (_: any) => {}
    const setYocoPayments = (_: any) => {}
    setYocoLoading(true)
    setMessage(null)

    try {
      const sessionId = getServerSessionId()

      // Determine status filter based on activeTab
      let statusFilter: string[] = []
      if (activeTab === 'pending') {
        statusFilter = ['pending']
      } else if (activeTab === 'approved') {
        statusFilter = ['completed']
      } else if (activeTab === 'rejected') {
        statusFilter = ['failed', 'cancelled']
      } else {
        // For flagged and hash-removal tabs, show nothing for Yoco
        setYocoPayments([])
        setYocoLoading(false)
        return
      }

      const response = await fetch('/api/admin/payment-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'load_yoco_payments', data: { statusFilter } })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to load Yoco payments')

      setYocoPayments(result.data.payments || [])

      if ((result.data.payments || []).length === 0) {
        setMessage({ type: 'info', text: `No ${activeTab} Yoco payments found` })
      }
    } catch (error: any) {
      console.error('Error loading Yoco payments:', error)
      setMessage({
        type: 'error',
        text: error.message || 'Failed to load Yoco payments.'
      })
      setYocoPayments([])
    } finally {
      setYocoLoading(false)
    }
  }

  // Helper function to validate share calculations for payments
  const validateShareCalculations = async (paymentsToValidate: Payment[]) => {
    try {
      const sessionId = getServerSessionId()

      // Get all phases for validation via API
      const response = await fetch('/api/admin/payment-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'validate_share_calculations', data: { payments: paymentsToValidate } })
      })

      const result = await response.json()
      if (!response.ok) {
        console.warn('⚠️ Unable to load phases for share validation')
        return
      }

      const allPhases = result.data.phases
      if (!allPhases || allPhases.length === 0) {
        console.warn('⚠️ Unable to load phases for share validation')
        return
      }

      const validationResults = new Map<string, {
        isValid: boolean
        expectedShares: number
        recordedShares: number
        phaseName: string
        phasePrice: number
        difference: number
      }>()

      for (const payment of paymentsToValidate) {
        if (payment.record_kind === 'commission_conversion' || payment.record_kind === 'commission_topup') continue
        const recordedShares = payment.shares_to_purchase || 0

        // PURCHASES ARE ALWAYS WHOLE SHARES
        // Validate: did they pay enough for the shares they're buying?
        // Find the phase that matches: amount / shares ≈ phase price
        const impliedPricePerShare = recordedShares > 0 ? payment.amount / recordedShares : 0

        // Find the CLOSEST phase to the implied price
        let matchingPhase: InvestmentPhase | undefined
        if (impliedPricePerShare > 0) {
          matchingPhase = allPhases.reduce((closest: InvestmentPhase | null, p: InvestmentPhase) => {
            const phasePrice = parseFloat(p.price_per_share as any)
            const closestPrice = closest ? parseFloat(closest.price_per_share as any) : Infinity
            return Math.abs(phasePrice - impliedPricePerShare) < Math.abs(closestPrice - impliedPricePerShare) ? p : closest
          }, null) as InvestmentPhase | undefined
        }

        if (matchingPhase) {
          const phasePrice = parseFloat(matchingPhase.price_per_share as any)
          // Check if they paid ENOUGH for the whole shares (allow small underpayment due to fees/rounding)
          const minimumRequired = recordedShares * phasePrice
          const shortfall = minimumRequired - payment.amount
          // Allow up to $10 or 1% underpayment (whichever is greater) for fees/rounding
          const allowedShortfall = Math.max(10, minimumRequired * 0.01)
          const isValid = shortfall <= allowedShortfall

          validationResults.set(payment.id, {
            isValid,
            expectedShares: recordedShares, // Purchases are whole shares - use what they recorded
            recordedShares,
            phaseName: matchingPhase.phase_name as string,
            phasePrice: phasePrice,
            difference: 0 // No difference for valid whole share purchases
          })
        }
      }

      setShareValidationResults(validationResults)
    } catch (error) {
      console.error('Error validating share calculations:', error)
    }
  }

  // Filter payments based on search query (without state updates)
  const filteredPayments = useMemo(() => {
    if (!debouncedSearchQuery || debouncedSearchQuery.trim() === '') {
      return payments
    }

    const lowerQuery = debouncedSearchQuery.toLowerCase().trim()

    return payments.filter(payment => {
      // Helper function to check matches
      const checkMatch = (value: any): boolean => {
        if (value === null || value === undefined) return false
        return String(value).toLowerCase().includes(lowerQuery)
      }

      // Search user fields
      if (payment.users) {
        if (checkMatch(payment.users.username)) return true
        if (checkMatch(payment.users.email)) return true
        if (checkMatch(payment.users.full_name)) return true
        if (checkMatch(payment.users.phone_number)) return true
        if (checkMatch(payment.users.id)) return true
        if (checkMatch(payment.users.auth_user_id)) return true

        // Check telegram_users if available
        if (payment.users.telegram_users) {
          const tg = Array.isArray(payment.users.telegram_users)
            ? payment.users.telegram_users[0]
            : payment.users.telegram_users
          if (tg) {
            if (checkMatch(tg.username)) return true
            if (checkMatch(tg.first_name)) return true
            if (checkMatch(tg.last_name)) return true
          }
        }
      }

      // Search transaction fields
      if (checkMatch(payment.id)) return true
      if (checkMatch(payment.amount)) return true
      if (checkMatch(payment.network)) return true
      if (checkMatch(payment.status)) return true
      if (checkMatch(payment.transaction_hash)) return true
      if (checkMatch(payment.currency)) return true
      if (checkMatch(payment.shares_to_purchase)) return true

      // Search transaction_notes for reference_number and amount_zar
      if (payment.transaction_notes) {
        try {
          const notes = typeof payment.transaction_notes === 'string'
            ? JSON.parse(payment.transaction_notes)
            : payment.transaction_notes

          if (notes.reference_number && checkMatch(notes.reference_number)) return true
          if (notes.amount_zar && checkMatch(notes.amount_zar)) return true
        } catch (e) {
          // Ignore JSON parse errors
        }
      }

      // Search dates (support multiple formats)
      const createdDate = new Date(payment.created_at)
      const dateFormats = [
        createdDate.toISOString().split('T')[0], // 2024-01-15
        createdDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }), // Jan 2024
        createdDate.toLocaleDateString('en-US', { month: 'long' }), // January
        createdDate.getFullYear().toString(), // 2024
      ]

      for (const format of dateFormats) {
        if (format.toLowerCase().includes(lowerQuery)) return true
      }

      return false
    })
  }, [payments, debouncedSearchQuery])

  // Update matched fields in a separate useEffect to avoid state updates during render
  useEffect(() => {
    if (!debouncedSearchQuery || debouncedSearchQuery.trim() === '') {
      setMatchedFields(new Map())
      return
    }

    const lowerQuery = debouncedSearchQuery.toLowerCase().trim()
    const matches = new Map<string, string[]>()

    filteredPayments.forEach(payment => {
      const matchedFieldsList: string[] = []

      const checkMatch = (value: any, fieldName: string): boolean => {
        if (value === null || value === undefined) return false
        const strValue = String(value).toLowerCase()
        if (strValue.includes(lowerQuery)) {
          matchedFieldsList.push(fieldName)
          return true
        }
        return false
      }

      // Check all fields and record which ones matched
      if (payment.users) {
        checkMatch(payment.users.username, 'Username')
        checkMatch(payment.users.email, 'Email')
        checkMatch(payment.users.full_name, 'Full Name')
        checkMatch(payment.users.phone_number, 'Phone')
        checkMatch(payment.users.id, 'User ID')
        checkMatch(payment.users.auth_user_id, 'Auth User ID')

        if (payment.users.telegram_users) {
          const tg = Array.isArray(payment.users.telegram_users)
            ? payment.users.telegram_users[0]
            : payment.users.telegram_users
          if (tg) {
            checkMatch(tg.username, 'Telegram Username')
            checkMatch(tg.first_name, 'First Name')
            checkMatch(tg.last_name, 'Last Name')
          }
        }
      }

      checkMatch(payment.id, 'Transaction ID')
      checkMatch(payment.amount, 'Amount USD')
      checkMatch(payment.network, 'Network')
      checkMatch(payment.status, 'Status')
      checkMatch(payment.transaction_hash, 'Transaction Hash')
      checkMatch(payment.currency, 'Currency')
      checkMatch(payment.shares_to_purchase, 'Shares')

      if (payment.transaction_notes) {
        try {
          const notes = typeof payment.transaction_notes === 'string'
            ? JSON.parse(payment.transaction_notes)
            : payment.transaction_notes
          if (notes.reference_number) checkMatch(notes.reference_number, 'Bank Reference')
          if (notes.amount_zar) checkMatch(notes.amount_zar, 'Amount ZAR')
        } catch (e) {
          // Ignore
        }
      }

      const createdDate = new Date(payment.created_at)
      const dateFormats = [
        createdDate.toISOString().split('T')[0],
        createdDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        createdDate.toLocaleDateString('en-US', { month: 'long' }),
        createdDate.getFullYear().toString(),
      ]
      dateFormats.forEach(format => {
        if (format.toLowerCase().includes(lowerQuery)) {
          matchedFieldsList.push('Created Date')
        }
      })

      if (matchedFieldsList.length > 0) {
        matches.set(payment.id, matchedFieldsList)
      }
    })

    setMatchedFields(matches)
  }, [filteredPayments, debouncedSearchQuery])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  const getTimeAgo = (dateString: string) => {
    const now = new Date()
    const date = new Date(dateString)
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
    
    if (diffInHours < 1) return 'Less than 1 hour ago'
    if (diffInHours < 24) return `${diffInHours} hours ago`
    const diffInDays = Math.floor(diffInHours / 24)
    return `${diffInDays} days ago`
  }

  const getNetworkIcon = (network: string) => {
    switch (network.toLowerCase()) {
      case 'bsc': return '🟡'
      case 'polygon': return '🟣'
      case 'tron': return '🔴'
      case 'bank_transfer': return '🏦'
      default: return '💎'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'text-yellow-400 bg-yellow-500/20'
      case 'approved': return 'text-green-400 bg-green-500/20'
      case 'rejected': return 'text-red-400 bg-red-500/20'
      case 'reversed': return 'text-orange-400 bg-orange-500/20'
      default: return 'text-white bg-gray-500/20'
    }
  }

  // Extract bank transfer reference from transaction_notes OR transaction_hash
  const getBankTransferReference = (payment: Payment): string | null => {
    // For manual entries, reference is stored in transaction_hash
    if (payment.network === 'BANK_TRANSFER' && payment.transaction_hash) {
      return payment.transaction_hash
    }
    // For user-submitted bank transfers, check transaction_notes
    if (!payment.transaction_notes) return null
    try {
      const notes = JSON.parse(payment.transaction_notes)
      return notes.reference_number || null
    } catch (e) {
      return null
    }
  }

  // Detect duplicate reference numbers
  const detectDuplicates = (paymentList: Payment[]) => {
    const refCounts = new Map<string, number>()

    paymentList.forEach(payment => {
      const ref = getBankTransferReference(payment)
      if (ref) {
        refCounts.set(ref, (refCounts.get(ref) || 0) + 1)
      }
    })

    setDuplicateRefs(refCounts)
  }

  // Load duplicate flags from database
  const loadDuplicateFlags = (paymentList: Payment[]) => {
    const flaggedPayments = new Set<string>()

    paymentList.forEach(payment => {
      // Check if payment has duplicate flag in transaction_notes
      try {
        const notes = typeof payment.transaction_notes === 'string'
          ? JSON.parse(payment.transaction_notes)
          : payment.transaction_notes

        if (notes?.duplicate_flagged === true) {
          flaggedPayments.add(payment.id)
        }
      } catch (error) {
        // Ignore parse errors
      }
    })

    setDuplicateFlags(flaggedPayments)
  }

  // Check if payment has duplicate reference
  const isDuplicateRef = (payment: Payment): boolean => {
    const ref = getBankTransferReference(payment)
    if (!ref) return false
    return (duplicateRefs.get(ref) || 0) > 1
  }

  // Toggle duplicate flag for a payment
  const toggleDuplicateFlag = async (paymentId: string) => {
    const newFlags = new Set(duplicateFlags)
    const isFlagged = newFlags.has(paymentId)

    if (isFlagged) {
      newFlags.delete(paymentId)
    } else {
      newFlags.add(paymentId)
    }
    setDuplicateFlags(newFlags)

    // Save flag to database via API
    try {
      const sessionId = getServerSessionId()

      const response = await fetch('/api/admin/payment-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'toggle_duplicate_flag',
          data: { paymentId, isFlagged, adminId: currentUser?.id }
        })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to save duplicate flag')

      setMessage({
        type: 'success',
        text: result.data.flagged ? 'Payment flagged as duplicate' : 'Duplicate flag removed'
      })
    } catch (error) {
      console.error('Error saving duplicate flag:', error)
      setMessage({ type: 'error', text: 'Failed to save duplicate flag' })
      // Revert the flag change on error
      if (isFlagged) {
        newFlags.add(paymentId)
      } else {
        newFlags.delete(paymentId)
      }
      setDuplicateFlags(newFlags)
    }
  }

  // Toggle sort direction
  const toggleSort = (field: 'created_at' | 'approved_at') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  // Open WhatsApp chat with user
  const openWhatsApp = (phoneNumber: string) => {
    if (!phoneNumber) {
      alert('No phone number available for this user')
      return
    }
    // Remove any non-digit characters and ensure it starts with country code
    const cleanNumber = phoneNumber.replace(/\D/g, '')
    const whatsappUrl = `https://wa.me/${cleanNumber}`
    window.open(whatsappUrl, '_blank')
  }

  // Navigate to user profile (you'll need to implement this based on your routing)
  const openUserProfile = (userId: number) => {
    // TODO: Implement navigation to user profile
    // For now, just show an alert
    alert(`User Profile feature coming soon. User ID: ${userId}`)
  }

  const handleViewScreenshot = async (payment: Payment) => {
    if (!payment.screenshot_url) {
      setMessage({ type: 'error', text: 'No screenshot available for this payment' })
      return
    }

    console.log('Attempting to view screenshot for payment:', payment.id)
    console.log('Screenshot URL from database:', payment.screenshot_url)

    try {
      // Wave 4: signed URL only. Do not construct or HEAD /object/public/proof/
      // — that path 403s once the bucket is private.
      const signedUrl = await resolveProofUrl(payment.screenshot_url)
      if (signedUrl) {
        console.log('Using signed proof URL')
        setCurrentScreenshot(signedUrl)
        setShowScreenshotModal(true)
        return
      }

      console.log('Signed proof URL unavailable, attempting storage download via API...')

      const sessionId = getServerSessionId()

      // Try different path variations
      const pathVariations = [
        payment.screenshot_url,
        payment.screenshot_url.replace(/^\/+/, ''), // Remove leading slashes
        payment.screenshot_url.replace(/^proof\//, ''), // Remove proof/ prefix
        `payment_${payment.user_id}_${payment.screenshot_url}` // Try with user prefix
      ]

      const downloadResponse = await fetch('/api/admin/payment-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'download_from_storage',
          data: { pathVariations }
        })
      })

      const downloadResult = await downloadResponse.json()
      if (!downloadResponse.ok) {
        throw new Error(downloadResult.error || 'Storage download failed')
      }

      // Convert base64 back to blob
      const byteCharacters = atob(downloadResult.data.fileBase64)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: 'image/jpeg' })
      const blobUrl = URL.createObjectURL(blob)

      console.log('Created blob URL for viewing')
      setCurrentScreenshot(blobUrl)
      setShowScreenshotModal(true)

    } catch (error: any) {
      console.error('Error loading screenshot:', error)
      setMessage({
        type: 'error',
        text: `Failed to load screenshot: ${error.message}. Please check the browser console for more details.`
      })
    }
  }

  // Wave 4 leftover: bank/Remitano rows sometimes store the proof object URL in
  // sender_wallet (or additional_proofs[].url). A raw <a href> hits
  // /object/public/proof/ and 404s NoSuchBucket now that the bucket is private.
  // Sign first; never navigate to the public URL.
  const openProofHref = async (raw: string | null | undefined) => {
    const value = (raw || '').trim()
    if (!value) return
    if (isSignableProofRef(value)) {
      const signed = await resolveProofUrl(value)
      if (!signed) {
        setMessage({
          type: 'error',
          text: 'Could not open payment proof. Use View Payment Proof on this card.'
        })
        return
      }
      window.open(signed, '_blank', 'noopener,noreferrer')
      return
    }
    if (/^https?:\/\//i.test(value)) {
      window.open(value, '_blank', 'noopener,noreferrer')
    }
  }

  const runRemitanoVerification = async (targetPayment: Payment, autoApprove: boolean = false): Promise<RemitanoVerificationResult> => {
    if (!targetPayment.transaction_hash) {
      throw new Error('No transaction hash found in payment record')
    }

    // v4.8.219: rewired from the retired /api/verify-remitano-payment endpoint
    // (permanently disabled 2026-09-13 per SA crypto regulation change) to the
    // admin-only /api/admin/verify-blockchain-transaction attestation endpoint.
    // This endpoint performs BSC on-chain attestation for ANY user's payment
    // (no ENABLED_USER_IDS allowlist — admin is the trust source) and supports
    // personal wallets (Exodus, Trust Wallet, MetaMask, etc.). Auto-approve is
    // delegated to the existing hardened /api/admin/approve-payment pipeline.
    const verifyResponse = await fetch('/api/admin/verify-blockchain-transaction', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
      body: JSON.stringify({
        paymentId: targetPayment.id
      })
    })

    const rawText = await verifyResponse.text()
    let verifyResult: any = null
    if (rawText) {
      try {
        verifyResult = JSON.parse(rawText)
      } catch {
        verifyResult = null
      }
    }

    if (!verifyResponse.ok) {
      const errorCode = verifyResult?.errorCode
      const errorMessage = verifyResult?.error || verifyResult?.message || 'Failed to verify blockchain transaction'
      return {
        success: false,
        verified: false,
        autoApproved: false,
        errorCode,
        error: errorMessage,
        message: errorMessage,
        report: verifyResult?.report,
        amountStatus: errorCode === UNDERPAID_ERROR_CODE ? 'underpaid' : undefined,
      } as RemitanoVerificationResult
    }

    const attestation = (verifyResult || {}) as RemitanoVerificationResult
    const blocked = isVerifyAndApproveBlocked(attestation, targetPayment)

    // Operator-clicked Verify & Approve (Exodus / BSC only). Unattended
    // auto-mint stays suspended on the user-facing verify-bsc path.
    if (autoApprove && attestation.verified && !blocked) {
      if (!currentUser?.database_user?.id) {
        throw new Error('Admin user not found')
      }

      const shortfallNum = parseFloat(String(attestation.shortfall || 0))
      const commNum = parseFloat(String(attestation.availableCommission || 0))
      const coverCommissionShortfall = attestation.canCoverWithCommission === true
        || (shortfallNum > 0 && hasUsdCoverage(commNum, shortfallNum))
        || targetPayment.status === 'underpaid'

      const approveResponse = await fetch(`${API_BASE_URL}/api/admin/approve-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
        body: JSON.stringify({
          paymentId: targetPayment.id,
          adminUserId: currentUser.database_user.id,
          ...(coverCommissionShortfall ? { coverCommissionShortfall: true } : {}),
          ...(canUseManualOverride && shortfallNum > 0 && shortfallNum <= MANUAL_UNDERPAY_TOLERANCE_USD
            ? { underpayToleranceUsd: MANUAL_UNDERPAY_TOLERANCE_USD }
            : {}),
        })
      })

      const approveResult = await readApiResponse<any>(approveResponse, 'Failed to approve payment')

      return {
        ...attestation,
        autoApproved: true,
        approval: approveResult.data,
        message: approveResult.message || 'Payment verified and approved via blockchain attestation.',
      } as RemitanoVerificationResult
    }

    if (autoApprove && blocked) {
      return {
        ...attestation,
        autoApproved: false,
        message: attestation.message || attestation.error || 'Manual review required. CEX-hot and underpay stay on the two-step Approve Payment path.',
      } as RemitanoVerificationResult
    }

    return attestation
  }

  const verifyBlockchainPreflightForApproval = async (payment: Payment): Promise<boolean> => {
    if (!isBlockchainPayment(payment) || hasBlockchainApprovalAttestation(payment)) return true

    setRemitanoVerifying(true)
    setRemitanoResult(null)
    setMessage({ type: 'info', text: '🔍 Blockchain attestation missing. Verifying transaction before approval...' })

    try {
      const result = await runRemitanoVerification(payment, false)
      setRemitanoResult(result)

      if (result.verified) {
        setMessage({ type: 'info', text: '✅ Blockchain verification refreshed. Continuing approval...' })
        return true
      }

      const failureMessage = result.error || result.message || 'Blockchain verification failed. Approval was not attempted.'
      setMessage({ type: 'error', text: `❌ Verification failed: ${failureMessage}` })
      return false
    } catch (error: any) {
      console.error('Approval preflight blockchain verification error:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to verify blockchain transaction before approval' })
      return false
    } finally {
      setRemitanoVerifying(false)
    }
  }

  const verifyCommissionConversionCard = async (payment: Payment) => {
    setProcessingPayment(payment.id)
    setConversionCheck(null)
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/commission-operations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
        body: JSON.stringify({ operation: 'verify_commission_conversion', data: { requestId: payment.id } })
      })
      const result = await readApiResponse<any>(response, 'Commission verification failed')
      const review = result.data
      setPayments((prev) => prev.map((row) => row.id === payment.id ? { ...row, commission_review: review } : row))
      setConversionsReadyToApprove((prev) => {
        const next = new Set(prev)
        if (review?.verified === true) next.add(payment.id)
        else next.delete(payment.id)
        return next
      })
      setConversionCheck({ paymentId: payment.id, review })
    } catch (error: any) {
      setConversionsReadyToApprove((prev) => {
        const next = new Set(prev)
        next.delete(payment.id)
        return next
      })
      setConversionCheck({ paymentId: payment.id, error: error.message || 'Commission verification failed' })
    } finally {
      setProcessingPayment(null)
    }
  }

  const approveCommissionConversionCard = async (payment: Payment) => {
    if (!conversionsReadyToApprove.has(payment.id) || payment.commission_review?.verified !== true) {
      setConversionCheck({
        paymentId: payment.id,
        error: 'Verify the commission on this card first. Approve stays off until that check is shown and passes.'
      })
      return
    }
    if (!currentUser?.database_user?.id) {
      setMessage({ type: 'error', text: 'Admin user not found' })
      return
    }
    setProcessingPayment(payment.id)
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/commission-operations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
        body: JSON.stringify({
          operation: 'approve_conversion_full',
          data: { requestId: payment.id, adminId: currentUser.database_user.id, userId: payment.user_id }
        })
      })
      await readApiResponse<any>(response, 'Conversion approval failed')
      setMessage({ type: 'success', text: 'Commission conversion approved. Shares are now on the account.' })
      await loadPayments()
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Conversion approval failed' })
    } finally {
      setProcessingPayment(null)
    }
  }

  const rejectCommissionConversionCard = async (payment: Payment) => {
    const reason = window.prompt('Reason for rejecting this commission conversion. The reserved commission will be returned.')
    if (reason === null) return
    setProcessingPayment(payment.id)
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/commission-operations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
        body: JSON.stringify({
          operation: 'reject_conversion_full',
          data: { requestId: payment.id, adminId: currentUser?.database_user?.id, adminNotes: reason }
        })
      })
      await readApiResponse<any>(response, 'Conversion rejection failed')
      setMessage({ type: 'success', text: 'Conversion rejected. The reserved commission was returned.' })
      await loadPayments()
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Conversion rejection failed' })
    } finally {
      setProcessingPayment(null)
    }
  }

  const verifyCommissionTopupCard = async (payment: Payment) => {
    setProcessingPayment(payment.id)
    setMessage({ type: 'info', text: 'Checking the hash, sending wallet, amount, and network...' })
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/topup-approval`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
        body: JSON.stringify({ operation: 'verify', data: { topupId: payment.id } })
      })
      const result = await readApiResponse<any>(response, 'Top-up verification failed')
      const verification = result.data?.verification || result.verification
      setPayments((prev) => prev.map((row) => row.id === payment.id ? { ...row, topup_verification: verification } : row))
      setMessage({
        type: verification?.success ? 'success' : 'error',
        text: verification?.success
          ? 'Hash, wallet, amount, and network match this top-up.'
          : (verification?.error || 'Top-up verification did not pass.')
      })
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Top-up verification failed' })
    } finally {
      setProcessingPayment(null)
    }
  }

  const approveCommissionTopupCard = async (payment: Payment) => {
    if (payment.topup_verification?.success !== true) {
      setMessage({ type: 'error', text: 'Verify the hash before crediting this top-up.' })
      return
    }
    setProcessingPayment(payment.id)
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/topup-approval`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
        body: JSON.stringify({ operation: 'approve', data: { topupId: payment.id } })
      })
      const result = await readApiResponse<any>(response, 'Top-up approval failed')
      setMessage({ type: 'success', text: result.message || result.data?.message || 'Top-up approved. The commission balance was credited.' })
      await loadPayments()
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Top-up approval failed' })
    } finally {
      setProcessingPayment(null)
    }
  }

  const rejectCommissionTopupCard = async (payment: Payment) => {
    const reason = window.prompt('Reason for rejecting this top-up:')
    if (reason === null) return
    setProcessingPayment(payment.id)
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/topup-approval`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
        body: JSON.stringify({ operation: 'reject', data: { topupId: payment.id, reason } })
      })
      await readApiResponse<any>(response, 'Top-up rejection failed')
      setMessage({ type: 'success', text: 'Top-up rejected. No commission was credited.' })
      await loadPayments()
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Top-up rejection failed' })
    } finally {
      setProcessingPayment(null)
    }
  }

  const approvePayment = async (
    payment: Payment,
    manualApprovalOverride = false,
    manualOverrideDetails?: ManualApprovalOverrideDetails,
    approvalOptions?: { coverCommissionShortfall?: boolean; underpayToleranceUsd?: number }
  ): Promise<boolean> => {
    if (!currentUser?.database_user?.id) {
      setMessage({ type: 'error', text: 'Admin user not found' })
      return false
    }

    setProcessingPayment(payment.id)
    setMessage(null)

    try {
      if (!manualApprovalOverride) {
        const verificationReady = await verifyBlockchainPreflightForApproval(payment)
        if (!verificationReady) return false
      }

      // Call API endpoint for payment approval (server-side uses service role key)
      const response = await fetch(`${API_BASE_URL}/api/admin/approve-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
        body: JSON.stringify({
          paymentId: payment.id,
          adminUserId: currentUser.database_user.id,
          ...(payment.status === 'underpaid' || approvalOptions?.coverCommissionShortfall ? { coverCommissionShortfall: true } : {}),
          ...(approvalOptions?.underpayToleranceUsd ? { underpayToleranceUsd: approvalOptions.underpayToleranceUsd } : {}),
          manualApprovalOverride,
          ...(manualApprovalOverride ? {
            manualOverride: true,
            manualOverrideType: manualOverrideDetails?.type || MANUAL_OVERRIDE_TYPE,
            manualOverrideReason: manualOverrideDetails?.reason || '',
            manualOverrideConfirmation: manualOverrideDetails?.confirmation || ''
          } : {})
        })
      })

      const result = await readApiResponse<any>(response, 'Failed to approve payment')

      setMessage({
        type: 'success',
        text: result.message || `Payment approved successfully! ${result.data?.totalShares || 'N/A'} shares allocated.`
      })

      logAction('payment', 'payment_approved', { target_type: 'payment', target_id: String(payment.id), target_label: payment.users?.username || payment.users?.full_name || `user ${payment.user_id}`, details: { amount: payment.amount, currency: payment.currency } })

      // Reload payments
      await loadPayments()
      setSelectedPayment(null)
      return true

    } catch (error: any) {
      console.error('Error approving payment:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to approve payment' })
      return false
    } finally {
      setProcessingPayment(null)
    }
  }

  const closeManualOverrideModal = () => {
    setShowManualOverrideModal(false)
    setManualOverridePayment(null)
    setManualOverrideConfirmation('')
  }

  const handleManualOverrideClick = (payment: Payment) => {
    if (!canUseManualOverride) {
      setMessage({ type: 'error', text: 'Manual override approval is restricted to Johan/JP.' })
      return
    }
    setManualOverridePayment(payment)
    setManualOverrideConfirmation('')
    setShowManualOverrideModal(true)
  }

  const handleManualOverrideSubmit = async () => {
    if (!manualOverridePayment) return
    const paidConfirmation = manualOverrideConfirmation.trim()
    if (!isValidManualOverridePaidValue(paidConfirmation)) {
      setMessage({ type: 'error', text: 'Type PAID to confirm Johan/JP has manually verified this payment.' })
      return
    }
    if (!canUseManualOverride) {
      setMessage({ type: 'error', text: 'Manual override approval is restricted to Johan/JP.' })
      return
    }
    const approved = await approvePayment(manualOverridePayment, true, {
      reason: paidConfirmation,
      confirmation: paidConfirmation,
      type: MANUAL_OVERRIDE_TYPE
    })
    if (!approved) return

    if (hashLookupResult.payment?.id === manualOverridePayment.id) {
      setHashLookupResult({ found: false, loading: false })
      setHashLookupInput('')
    }
    closeManualOverrideModal()
  }

  // NOTE: Old inline approvePayment code removed - logic moved to /api/admin/approve-payment.js
  // This handles: balance payments, gift purchases, commission processing, phase detection, etc.

  // REMOVED: Inline approvePayment implementation - now using /api/admin/approve-payment.js
  // [All server-side logic moved to API endpoint for security]

  const rejectPayment = async (payment: Payment, reason: string) => {
    if (!currentUser?.database_user?.id) {
      setMessage({ type: 'error', text: 'Admin user not found' })
      return
    }

    const rejectionReason = (reason ?? '').trim()

    if (!rejectionReason) {
      setMessage({ type: 'error', text: 'Rejection reason is required' })
      return
    }

    if (rejectionReason.length > 500) {
      setMessage({ type: 'error', text: 'Rejection reason is too long (maximum 500 characters).' })
      return
    }

    setProcessingPayment(payment.id)
    setMessage(null)

    try {
      // Call API endpoint for payment rejection (server-side uses service role key)
      const response = await fetch(`${API_BASE_URL}/api/admin/reject-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          paymentId: payment.id,
          adminUserId: currentUser.database_user.id,
          rejectionReason: rejectionReason
        })
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to reject payment')
      }

      setMessage({
        type: 'success',
        text: result.message || `Payment rejected successfully. User ${payment.users?.username} has been notified.`
      })

      logAction('payment', 'payment_rejected', { target_type: 'payment', target_id: String(payment.id), target_label: payment.users?.username || payment.users?.full_name || `user ${payment.user_id}`, details: { amount: payment.amount, currency: payment.currency, reason: rejectionReason } })

      // Reload payments
      await loadPayments()
      setSelectedPayment(null)
      setShowRejectionModal(false)
      setRejectionReason('')

    } catch (error: any) {
      console.error('Error rejecting payment:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to reject payment' })
    } finally {
      setProcessingPayment(null)
    }
  }
  // REMOVED: Inline rejectPayment implementation - now using /api/admin/reject-payment.js
  // [All server-side logic moved to API endpoint for security]

  // Duplicate Finder — READ-ONLY detection of approved bank-transfer
  // double-approvals (same user + same amount approved within a short window).
  // Fetches via the existing admin payment-integrity endpoint; performs no
  // mutation. The "Cancel Duplicate" action below reuses the pure atomic
  // /api/admin/reverse-payment endpoint on the later leg only.
  const loadDuplicatePayments = async () => {
    setDuplicatesLoading(true)
    setMessage(null)
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/payment-integrity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'find_duplicate_payments',
          data: { windowMinutes: duplicateWindowMinutes }
        })
      })
      const result = await response.json()
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to load duplicate payments')
      }
      setDuplicateGroups(result.data || [])
      if ((result.data || []).length === 0) {
        setMessage({ type: 'info', text: `No duplicate bank payments found within ${duplicateWindowMinutes} minutes.` })
      }
    } catch (error: any) {
      console.error('Error loading duplicate payments:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to load duplicate payments' })
      setDuplicateGroups([])
    } finally {
      setDuplicatesLoading(false)
    }
  }

  // Cancels ONE duplicate leg using the pure atomic reversal RPC (unwinds the
  // share purchase, commission cascade, team volume and pool credit, and syncs
  // both payment tables to 'reversed'). This is the earliest leg's later
  // sibling(s) only — the FIRST leg is always kept.
  const cancelDuplicatePayment = async (group: DuplicateGroup, leg: DuplicateLeg) => {
    if (!canUseManualOverride) {
      setMessage({ type: 'error', text: 'Cancelling a duplicate payment is restricted to Johan/JP.' })
      return
    }
    if (leg.keep) {
      setMessage({ type: 'error', text: 'The first (original) payment is kept; only later duplicate legs can be cancelled.' })
      return
    }
    const adminId = currentUser?.database_user?.id
    if (!adminId) {
      setMessage({ type: 'error', text: 'Admin user not found' })
      return
    }
    const label = group.username || group.full_name || `user ${group.user_id}`
    const confirmed = window.confirm(
      `Cancel this DUPLICATE approved bank payment?\n\n` +
      `User: ${label} (id ${group.user_id})\n` +
      `Amount: $${group.amount.toFixed(2)} ${group.currency}\n` +
      `Duplicate approved: ${new Date(leg.approved_at).toLocaleString()}\n` +
      `(${leg.seconds_after_first ?? '?'}s after the original)\n\n` +
      `This will atomically reverse the share purchase, any SS_SPECIAL BOGO gifts (buyer + upline), commission cascade, ` +
      `team volume and pool credit for THIS leg only. Rows are cancelled, not deleted. The original payment is kept. ` +
      `This action is audit-logged.`
    )
    if (!confirmed) return

    const reason = `Duplicate approved bank payment cancelled: same user+amount ($${group.amount.toFixed(2)} ${group.currency}) approved ${leg.seconds_after_first ?? '?'}s after the original within a ${group.window_minutes}-minute window.`

    setCancellingDuplicateId(leg.payment_id)
    setMessage(null)
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/reverse-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          paymentId: leg.payment_id,
          adminUserId: adminId,
          reversalReason: reason
        })
      })
      const result = await response.json()
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to cancel duplicate payment')
      }
      setMessage({ type: 'success', text: result.message || 'Duplicate payment reversed. The user has been notified.' })
      logAction('payment', 'cancel_duplicate_payment', {
        target_type: 'payment',
        target_id: String(leg.payment_id),
        target_label: label,
        details: { user_id: group.user_id, amount: group.amount, currency: group.currency, reason, rpc: result.data?.rpc }
      })
      await loadDuplicatePayments()
    } catch (error: any) {
      console.error('Error cancelling duplicate payment:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to cancel duplicate payment' })
    } finally {
      setCancellingDuplicateId(null)
    }
  }

  // v4.8.84 — Cancel/reverse an accidental DUPLICATE directly from an APPROVED
  // row in the main payments list (owner-only: Johan/JP), without opening the
  // 🧬 Duplicates tab. Option A: single quick-confirm + auto-generated reason,
  // reusing the SAME pure atomic /api/admin/reverse-payment endpoint (which runs
  // the admin_reverse_payment RPC) as the Duplicates tab, so the share purchase,
  // commission cascade, team volume and pool credit are all unwound safely and
  // both payment tables are synced to 'reversed'. This is a full reversal of the
  // selected payment only; it is audit-logged (admin_actions + financial_audit_log).
  const handleCancelDuplicateRow = async (payment: Payment) => {
    if (!canUseManualOverride) {
      setMessage({ type: 'error', text: 'Cancelling a duplicate payment is restricted to Johan/JP.' })
      return
    }
    if (payment.status !== 'approved') {
      setMessage({ type: 'error', text: 'Only approved payments can be cancelled as duplicates.' })
      return
    }
    const adminId = currentUser?.database_user?.id
    if (!adminId) {
      setMessage({ type: 'error', text: 'Admin user not found' })
      return
    }
    const label = payment.users?.username || payment.users?.full_name || `user ${payment.user_id}`
    const confirmed = window.confirm(
      `Cancel this APPROVED payment as a DUPLICATE?\n\n` +
      `User: ${label} (id ${payment.user_id})\n` +
      `Amount: $${Number(payment.amount).toFixed(2)} ${payment.currency}\n` +
      `Approved: ${payment.approved_at ? new Date(payment.approved_at).toLocaleString() : '(unknown)'}\n\n` +
      `This will atomically reverse the share purchase, any SS_SPECIAL BOGO gifts (buyer + upline), commission cascade, ` +
      `team volume and pool credit for THIS payment. Rows are cancelled, not deleted. This action is audit-logged ` +
      `and the user will be notified.`
    )
    if (!confirmed) return

    const reason = `Duplicate payment reversal (Manual Override): approved payment $${Number(payment.amount).toFixed(2)} ${payment.currency} for user ${label} (id ${payment.user_id}) cancelled as an accidental duplicate.`

    setCancellingApprovedRowId(payment.id)
    setMessage(null)
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/reverse-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          paymentId: payment.id,
          adminUserId: adminId,
          reversalReason: reason
        })
      })
      const result = await response.json()
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to cancel duplicate payment')
      }
      setMessage({ type: 'success', text: result.message || 'Duplicate payment reversed. The user has been notified.' })
      logAction('payment', 'cancel_duplicate_payment_row', {
        target_type: 'payment',
        target_id: String(payment.id),
        target_label: label,
        details: { user_id: payment.user_id, amount: payment.amount, currency: payment.currency, reason, rpc: result.data?.rpc }
      })
      await loadPayments()
      setSelectedPayment(null)
    } catch (error: any) {
      console.error('Error cancelling duplicate payment (approved row):', error)
      setMessage({ type: 'error', text: error.message || 'Failed to cancel duplicate payment' })
    } finally {
      setCancellingApprovedRowId(null)
    }
  }

  // Payment Reverse + Reassign System — server-side via /api/admin/reassign-payment.
  // The browser never touches commission_balances or aureus_share_purchases
  // directly; the atomic admin_reassign_payment RPC recalls the deposit from the
  // WRONG user (cancelling shares + clawing back the commission cascade) and
  // re-applies the funds to the CORRECT user (re-running the cascade for their
  // upline) — all inside a single Postgres transaction.
  const reversePayment = async (payment: Payment, reason: string) => {
    if (!currentUser?.database_user?.id) {
      setMessage({ type: 'error', text: 'Admin user not found' })
      return
    }
    if (!reassignUserId) {
      setMessage({ type: 'error', text: 'Select the correct recipient user' })
      return
    }
    if (reassignUserId === payment.user_id) {
      setMessage({ type: 'error', text: 'Correct recipient must be different from the current (wrong) user' })
      return
    }

    const trimmed = (reason ?? '').trim()
    if (!trimmed) {
      setMessage({ type: 'error', text: 'Reversal reason is required' })
      return
    }
    if (trimmed.length < 10) {
      setMessage({ type: 'error', text: 'Reversal reason must be at least 10 characters long' })
      return
    }
    if (trimmed.length > 500) {
      setMessage({ type: 'error', text: 'Reversal reason is too long (maximum 500 characters).' })
      return
    }

    let newAmountPayload: number | undefined = undefined
    if (reassignNewAmount.trim() !== '') {
      const parsed = Number(reassignNewAmount)
      if (!Number.isFinite(parsed) || parsed <= 0) {
        setMessage({ type: 'error', text: 'Corrected amount must be a positive number' })
        return
      }
      newAmountPayload = parsed
    }

    setProcessingReversal(true)
    setMessage(null)

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/reassign-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          paymentId: payment.id,
          adminUserId: currentUser.database_user.id,
          correctUserId: reassignUserId,
          newAmount: newAmountPayload,
          reassignReason: trimmed
        })
      })

      const result = await response.json()

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to reverse payment')
      }

      setMessage({
        type: 'success',
        text: result.message || 'Payment reversed and reassigned successfully. Both users have been notified.'
      })

      logAction('payment', 'payment_reassigned', {
        target_type: 'payment',
        target_id: String(payment.id),
        target_label: payment.users?.username || payment.users?.full_name || `user ${payment.user_id}`,
        details: { amount: payment.amount, currency: payment.currency, correct_user_id: reassignUserId, new_amount: newAmountPayload ?? null, reason: trimmed, rpc: result.data?.rpc }
      })

      await loadPayments()
      setShowReversalModal(false)
      setSelectedPayment(null)
      setReversalReason('')
      setReversalConfirmation(false)
      setReversalConfirmText('')
      setReassignUserId(null)
      setReassignSearch('')
      setReassignNewAmount('')
    } catch (error: any) {
      console.error('Error reversing payment:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to reverse payment' })
    } finally {
      setProcessingReversal(false)
    }
  }

  const handleReverseClick = (payment: Payment) => {
    if (payment.status !== 'approved') {
      setMessage({ type: 'error', text: 'Only approved payments can be reversed.' })
      return
    }
    setSelectedPayment(payment)
    setReversalReason('Funds deposited to the wrong account; recalling and reassigning to the correct user.')
    setReversalConfirmation(false)
    setReversalConfirmText('')
    setReassignUserId(null)
    setReassignSearch('')
    setReassignNewAmount('')
    if (allUsers.length === 0) {
      loadAllUsers()
    }
    setShowReversalModal(true)
    logAction('payment', 'open_payment_reverse', {
      target_type: 'user',
      target_id: String(payment.user_id),
      target_label: payment.users?.username || payment.users?.email || `User ${payment.user_id}`,
      details: { payment_id: payment.id, amount: payment.amount, currency: payment.currency, network: payment.network }
    })
  }

  const handleReversalSubmit = () => {
    if (!selectedPayment) {
      setMessage({ type: 'error', text: 'No payment selected for reversal' })
      return
    }
    if (!reassignUserId) {
      setMessage({ type: 'error', text: 'Select the correct recipient user before reversing.' })
      return
    }
    if (reassignUserId === selectedPayment.user_id) {
      setMessage({ type: 'error', text: 'Correct recipient must be different from the current (wrong) user.' })
      return
    }
    if (!reversalReason.trim() || reversalReason.trim().length < 10) {
      setMessage({ type: 'error', text: 'Reversal reason must be at least 10 characters long' })
      return
    }
    if (!reversalConfirmation) {
      setMessage({ type: 'error', text: 'You must acknowledge the consequences before reversing.' })
      return
    }
    if (reversalConfirmText !== 'REVERSE') {
      setMessage({ type: 'error', text: 'Type REVERSE (uppercase, exact) to confirm.' })
      return
    }
    reversePayment(selectedPayment, reversalReason.trim())
  }

  const handleRejectClick = (payment: Payment) => {
    setSelectedPayment(payment)
    setShowRejectionModal(true)
    setRejectionReason('')
    // v4.7.53 — capture which user's payment is being rejected
    logAction('payment', 'open_payment_reject', {
      target_type: 'user',
      target_id: String(payment.user_id),
      target_label: payment.users?.username || payment.users?.email || `User ${payment.user_id}`,
      details: { payment_id: payment.id, amount: payment.amount, currency: payment.currency, network: payment.network }
    })
  }

  // Delete Payment Functions
  const handleDeleteClick = (payment: Payment) => {
    setSelectedPayment(payment)
    setDeleteReason('')
    setDeleteConfirmed(false)
    setShowDeleteModal(true)
    // v4.7.53 — capture which user's payment is being deleted
    logAction('payment', 'open_payment_delete', {
      target_type: 'user',
      target_id: String(payment.user_id),
      target_label: payment.users?.username || payment.users?.email || `User ${payment.user_id}`,
      details: { payment_id: payment.id, amount: payment.amount, currency: payment.currency, network: payment.network }
    })
  }

  const deletePayment = async (payment: Payment, reason: string) => {
    if (!currentUser?.database_user?.id) {
      setMessage({ type: 'error', text: 'Admin user not found' })
      return
    }

    if (!reason.trim()) {
      setMessage({ type: 'error', text: 'Deletion reason is required' })
      return
    }

    setProcessingDelete(true)

    try {
      const sessionId = getServerSessionId()

      const response = await fetch('/api/admin/payment-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'delete_payment',
          data: {
            paymentId: payment.id,
            payment,
            adminId: currentUser.database_user.id,
            adminEmail: currentUser.email,
            reason
          }
        })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to delete payment')

      setMessage({ type: 'success', text: `Payment deleted successfully. Reason: ${reason}` })
      setShowDeleteModal(false)
      setSelectedPayment(null)
      setDeleteReason('')
      setDeleteConfirmed(false)
      await loadPayments()

    } catch (error: any) {
      console.error('Error deleting payment:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to delete payment' })
    } finally {
      setProcessingDelete(false)
    }
  }

  // Flag as Duplicate Functions
  const handleFlagDuplicateClick = (payment: Payment) => {
    setSelectedPayment(payment)
    setFlagDuplicateReason('')
    setShowFlagDuplicateModal(true)
    // v4.7.53 — capture which user's payment is being flagged
    logAction('payment', 'open_payment_flag_duplicate', {
      target_type: 'user',
      target_id: String(payment.user_id),
      target_label: payment.users?.username || payment.users?.email || `User ${payment.user_id}`,
      details: { payment_id: payment.id, amount: payment.amount, currency: payment.currency, network: payment.network }
    })
  }

  const flagPaymentAsDuplicate = async (payment: Payment, reason: string) => {
    if (!currentUser?.database_user?.id) {
      setMessage({ type: 'error', text: 'Admin user not found' })
      return
    }

    if (!reason.trim()) {
      setMessage({ type: 'error', text: 'Flag reason is required' })
      return
    }

    setProcessingFlagDuplicate(true)

    try {
      // Get existing transaction notes for email
      let existingNotes = {}
      try {
        existingNotes = typeof payment.transaction_notes === 'string'
          ? JSON.parse(payment.transaction_notes)
          : (payment.transaction_notes || {})
      } catch (e) {
        existingNotes = {}
      }

      // Call API to flag payment
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/payment-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'flag_payment_duplicate',
          data: {
            paymentId: payment.id,
            reason: reason.trim(),
            adminId: currentUser.database_user.id,
            adminEmail: currentUser.email,
            existingNotes
          }
        })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to flag payment')

      // Send email notification to user
      try {
        const userEmail = payment.users?.email
        const userName = payment.users?.full_name || payment.users?.username || 'User'

        if (userEmail) {
          // Get payment reference
          let paymentRef = payment.transaction_hash
          try {
            const notes = typeof payment.transaction_notes === 'string'
              ? JSON.parse(payment.transaction_notes)
              : payment.transaction_notes
            paymentRef = notes?.reference_number || notes?.payment_reference || payment.transaction_hash
          } catch (e) {
            // Use transaction_hash as fallback
          }

          await fetch('/api/send-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
            body: JSON.stringify({
              emailType: 'custom',
              data: {
                email: userEmail,
                subject: '⚠️ Payment Flagged for Review - Aureus Alliance Holdings',
                htmlContent: `
                  <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
                    <div style="background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%); padding: 30px; border-radius: 10px;">
                      <h1 style="color: #fbbf24; margin: 0 0 20px 0;">⚠️ Payment Flagged for Review</h1>
                      <p style="color: #e5e7eb; font-size: 16px;">Dear ${userName},</p>
                      <p style="color: #e5e7eb; font-size: 16px;">Your payment has been flagged for review by our admin team.</p>

                      <div style="background: #374151; padding: 20px; border-radius: 8px; margin: 20px 0;">
                        <h3 style="color: #fbbf24; margin: 0 0 15px 0;">Payment Details</h3>
                        <p style="color: #e5e7eb; margin: 5px 0;"><strong>Reference:</strong> ${paymentRef || 'N/A'}</p>
                        <p style="color: #e5e7eb; margin: 5px 0;"><strong>Amount:</strong> $${payment.amount} ${payment.currency}</p>
                        <p style="color: #e5e7eb; margin: 5px 0;"><strong>Shares:</strong> ${payment.shares_to_purchase}</p>
                      </div>

                      <div style="background: #7c2d12; padding: 15px; border-radius: 8px; margin: 20px 0;">
                        <h4 style="color: #fbbf24; margin: 0 0 10px 0;">Reason for Flag:</h4>
                        <p style="color: #fef3c7; margin: 0;">${reason}</p>
                      </div>

                      <p style="color: #e5e7eb; font-size: 16px;">If you believe this is an error, please log in to your dashboard and click "Request Review" on the flagged payment.</p>

                      <div style="text-align: center; margin-top: 30px;">
                        <a href="https://aureus.africa/dashboard" style="background: #fbbf24; color: #1a1a2e; padding: 12px 30px; border-radius: 6px; text-decoration: none; font-weight: bold;">View Dashboard</a>
                      </div>

                      <p style="color: #9ca3af; font-size: 14px; margin-top: 30px;">Best regards,<br>Aureus Alliance Holdings Team</p>
                    </div>
                  </div>
                `
              }
            })
          })
        }
      } catch (emailError) {
        console.error('Error sending flag notification email:', emailError)
        // Don't fail the operation if email fails
      }

      setMessage({ type: 'success', text: 'Payment flagged as duplicate and user notified' })
      setShowFlagDuplicateModal(false)
      setSelectedPayment(null)
      setFlagDuplicateReason('')
      await loadPayments()

    } catch (error: any) {
      console.error('Error flagging payment:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to flag payment' })
    } finally {
      setProcessingFlagDuplicate(false)
    }
  }

  // Underpayment Handling Functions
  const handleMarkUnderpaidClick = async (payment: Payment) => {
    // For underpaid payments, check if a balance payment has already been submitted
    // If so, prevent editing to avoid data inconsistency
    if (payment.status === 'underpaid') {
      try {
        const sessionId = getServerSessionId()
        const response = await fetch('/api/admin/payment-operations', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(sessionId ? { 'X-Session-Id': sessionId } : {})
          },
          body: JSON.stringify({
            operation: 'check_linked_balance_payments',
            data: { paymentId: payment.id, userId: payment.user_id }
          })
        })
        const linkResult = await response.json()

        if (response.ok && linkResult.data?.hasLinked) {
          const balancePayment = linkResult.data.linkedPayments[0]
          setMessage({
            type: 'error',
            text: `⚠️ Cannot edit underpayment details - a balance payment has already been submitted (ID: ${balancePayment.id}, Status: ${balancePayment.status}). Editing amounts now would cause data inconsistency.`
          })
          return
        }
      } catch (e) {
        console.error('Error checking for balance payments:', e)
        // Allow editing if check fails (edge case)
      }
    }

    setUnderpaidPayment(payment)
    setUnderpaidForm({
      originalAmountReceived: '',
      balanceOwed: '',
      reason: ''
    })
    setShowUnderpaidModal(true)
  }

  const handleUnderpaidFormChange = (field: keyof typeof underpaidForm, value: string) => {
    setUnderpaidForm(prev => {
      const updated = { ...prev, [field]: value }

      // Auto-calculate balance when originalAmountReceived changes
      if (field === 'originalAmountReceived' && underpaidPayment) {
        const received = parseFloat(value) || 0
        const expected = underpaidPayment.amount
        const balance = Math.max(0, expected - received)
        updated.balanceOwed = balance.toFixed(2)
      }

      return updated
    })
  }

  const markPaymentAsUnderpaid = async () => {
    if (!currentUser?.database_user?.id) {
      setMessage({ type: 'error', text: 'Admin user not found' })
      return
    }

    if (!underpaidPayment) {
      setMessage({ type: 'error', text: 'No payment selected' })
      return
    }

    const received = parseFloat(underpaidForm.originalAmountReceived)
    if (isNaN(received) || received <= 0) {
      setMessage({ type: 'error', text: 'Please enter a valid amount received' })
      return
    }

    if (received >= underpaidPayment.amount) {
      setMessage({ type: 'error', text: 'Amount received must be less than the expected amount' })
      return
    }

    if (!underpaidForm.reason.trim()) {
      setMessage({ type: 'error', text: 'Please provide a reason for the underpayment' })
      return
    }

    setProcessingUnderpaid(true)

    try {
      const totalExpected = underpaidPayment.amount
      const balanceOwed = totalExpected - received

      // Get existing transaction notes for email
      let existingNotes: Record<string, any> = {}
      try {
        existingNotes = typeof underpaidPayment.transaction_notes === 'string'
          ? JSON.parse(underpaidPayment.transaction_notes)
          : (underpaidPayment.transaction_notes || {})
      } catch (e) {
        existingNotes = {}
      }

      // Call API to mark payment as underpaid
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/payment-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'mark_underpaid',
          data: {
            paymentId: underpaidPayment.id,
            received,
            balanceOwed,
            totalExpected,
            reason: underpaidForm.reason.trim(),
            adminId: currentUser.database_user.id,
            adminEmail: currentUser.email,
            existingNotes
          }
        })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to mark payment as underpaid')

      // Send email notification to user
      try {
        const userEmail = underpaidPayment.users?.email
        const userName = underpaidPayment.users?.full_name || underpaidPayment.users?.username || 'User'

        if (userEmail) {
          // Get payment reference
          let paymentRef = underpaidPayment.transaction_hash
          try {
            paymentRef = existingNotes?.reference_number || existingNotes?.payment_reference || underpaidPayment.transaction_hash
          } catch (e) {
            // Use transaction_hash as fallback
          }

          await fetch('/api/send-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
            body: JSON.stringify({
              emailType: 'custom',
              data: {
                email: userEmail,
                subject: '⚠️ Underpayment Notice - Balance Payment Required - Aureus Alliance Holdings',
                htmlContent: `
                  <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
                    <div style="background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%); padding: 30px; border-radius: 10px;">
                      <h1 style="color: #f59e0b; margin: 0 0 20px 0;">⚠️ Underpayment Notice</h1>
                      <p style="color: #e5e7eb; font-size: 16px;">Dear ${userName},</p>
                      <p style="color: #e5e7eb; font-size: 16px;">
                        We have received your payment, but the amount is less than expected. Please submit the remaining balance to complete your purchase.
                      </p>
                      <div style="background: rgba(245, 158, 11, 0.1); padding: 20px; border-radius: 8px; margin: 20px 0; border: 1px solid rgba(245, 158, 11, 0.3);">
                        <p style="color: #f59e0b; margin: 0 0 10px 0;"><strong>Payment Details:</strong></p>
                        <p style="color: #e5e7eb; margin: 5px 0;">Reference: <strong>${paymentRef}</strong></p>
                        <p style="color: #e5e7eb; margin: 5px 0;">Expected Amount: <strong>$${totalExpected.toFixed(2)}</strong></p>
                        <p style="color: #e5e7eb; margin: 5px 0;">Amount Received: <strong>$${received.toFixed(2)}</strong></p>
                        <p style="color: #f59e0b; margin: 10px 0 0 0; font-size: 18px;">Balance Owed: <strong>$${balanceOwed.toFixed(2)}</strong></p>
                      </div>
                      <p style="color: #e5e7eb; font-size: 16px;">
                        <strong>Reason:</strong> ${underpaidForm.reason.trim()}
                      </p>
                      <p style="color: #e5e7eb; font-size: 16px;">
                        Please log in to your dashboard to submit the balance payment. When submitting the balance, use the same reference with "-BALANCE" appended (e.g., "${paymentRef}-BALANCE").
                      </p>
                      <p style="color: #9ca3af; font-size: 14px; margin-top: 20px;">
                        If you have any questions, please contact our support team.
                      </p>
                    </div>
                  </div>
                `
              }
            })
          })
        }
      } catch (emailError) {
        console.error('Error sending underpayment notification:', emailError)
        // Don't fail the operation if email fails
      }

      setMessage({ type: 'success', text: `Payment marked as underpaid. Balance owed: $${balanceOwed.toFixed(2)}. User notified.` })
      setShowUnderpaidModal(false)
      setUnderpaidPayment(null)
      setUnderpaidForm({ originalAmountReceived: '', balanceOwed: '', reason: '' })
      await loadPayments()

    } catch (error: any) {
      console.error('Error marking payment as underpaid:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to mark payment as underpaid' })
    } finally {
      setProcessingUnderpaid(false)
    }
  }

  // Save Additional Transaction Hash
  const handleSaveAdditionalHash = async (paymentId: string, hashValue: string) => {
    if (!hashValue.trim()) {
      setMessage({ type: 'error', text: 'Please enter a transaction hash' })
      return
    }

    try {
      setSavingAdditionalHash(true)

      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/payment-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'save_additional_hash',
          data: {
            paymentId,
            hashValue: hashValue.trim(),
            addedBy: currentUser?.email || 'admin'
          }
        })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to save additional hash')

      setMessage({ type: 'success', text: 'Additional transaction hash saved successfully!' })
      setAdditionalHash('')
      await loadPayments()

    } catch (error: any) {
      console.error('Error saving additional hash:', error)
      setMessage({ type: 'error', text: 'Failed to save additional hash: ' + (error.message || 'Unknown error') })
    } finally {
      setSavingAdditionalHash(false)
    }
  }

  // v2.9.94: Edit the primary transaction hash on a payment
  const handleEditTransactionHash = async (paymentId: string, newHash: string) => {
    if (!newHash.trim()) {
      setMessage({ type: 'error', text: 'Please enter a transaction hash' })
      return
    }

    try {
      setSavingEditHash(true)
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/payment-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'edit_transaction_hash',
          data: { paymentId, newHash: newHash.trim() }
        })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to edit hash')

      setMessage({ type: 'success', text: `Transaction hash updated successfully. Old: ${result.oldHash?.slice(0, 12)}... → New: ${result.newHash?.slice(0, 12)}...` })
      setEditingHashPaymentId(null)
      setEditHashValue('')
      await loadPayments()
    } catch (error: any) {
      console.error('Error editing hash:', error)
      setMessage({ type: 'error', text: 'Failed to edit hash: ' + (error.message || 'Unknown error') })
    } finally {
      setSavingEditHash(false)
    }
  }

  // Upload Additional Proof of Payment
  const handleUploadAdditionalProof = async (paymentId: string, file: File) => {
    if (!file) {
      setMessage({ type: 'error', text: 'Please select a file to upload' })
      return
    }

    // Validate file type
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      setMessage({ type: 'error', text: 'Only images and PDFs are allowed' })
      return
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'File size must be less than 5MB' })
      return
    }

    try {
      setUploadingAdditionalProof(true)

      // Convert file to base64
      const fileArrayBuffer = await file.arrayBuffer()
      const fileBase64 = btoa(
        new Uint8Array(fileArrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
      )

      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/payment-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'upload_additional_proof',
          data: {
            paymentId,
            fileBase64,
            fileName: file.name,
            contentType: file.type
          }
        })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to upload proof')

      setMessage({ type: 'success', text: 'Additional proof of payment uploaded successfully!' })
      setAdditionalProofFile(null)
      await loadPayments()

    } catch (error: any) {
      console.error('Error uploading additional proof:', error)
      setMessage({ type: 'error', text: 'Failed to upload proof: ' + (error.message || 'Unknown error') })
    } finally {
      setUploadingAdditionalProof(false)
    }
  }

  // Remitano Verification Functions
  const handleRemitanoVerifyClick = (payment: Payment) => {
    setRemitanoPayment(payment)
    setRemitanoChargeId(payment.remitano_charge_id || '')
    setRemitanoResult(null)
    setShowRemitanoModal(true)
  }

  // v7.3.393: paymentOverride lets callers (e.g. the Underpaid tab "Verify & Cover with
  // Commission" button) supply the target payment directly, avoiding the React state
  // propagation race when the modal is opened and verification is requested in the same
  // event handler.
  const handleRemitanoVerify = async (autoApprove: boolean = false, paymentOverride?: Payment): Promise<boolean> => {
    const targetPayment = paymentOverride ?? remitanoPayment
    if (!targetPayment) {
      setMessage({ type: 'error', text: 'No payment selected' })
      return false
    }

    if (!targetPayment.transaction_hash) {
      setMessage({ type: 'error', text: 'No transaction hash found in payment record' })
      return false
    }

    setRemitanoVerifying(true)
    setRemitanoResult(null)

    try {
      const result = await runRemitanoVerification(targetPayment, autoApprove)
      setRemitanoResult(result)

      if (result.autoApproved) {
        // v7.3.392: when shortfall was covered by commission, surface that explicitly
        const successText = (result.shortfallCoveredByCommission || result.approval?.shortfallCoveredByCommission) && (result.shortfall || result.approval?.shortfall)
          ? `Payment verified and approved. A shortfall of $${result.shortfall || result.approval?.shortfall} was covered from the user's USDT commission.`
          : result.approval?.overpaymentCredited
            ? `Payment verified and approved. $${result.approval.overpaymentAmount} overpayment credited to USDT commission.`
            : 'Payment verified and approved via blockchain attestation (Exodus / BSC).'
        setMessage({ type: 'success', text: successText })
        setShowRemitanoModal(false)
        await loadPayments()
        return true
      } else if (result.autoRejected) {
        setMessage({ type: 'info', text: `⚠️ Payment auto-rejected: ${result.message}` })
        setShowRemitanoModal(false)
        await loadPayments()
        return true
      } else if (result.verified) {
        const blocked = isVerifyAndApproveBlocked(result, targetPayment)
        setMessage({
          type: blocked ? 'info' : 'success',
          text: blocked
            ? (result.message || 'Blockchain attestation verified. CEX-hot / underpay stay two-step — use Approve Payment after review.')
            : '✅ Blockchain transaction verified. Review the evidence before approval.'
        })
        return true
      } else if (result.error) {
        setMessage({ type: 'error', text: `❌ Verification failed: ${result.error}` })
      }
      return false
    } catch (error: any) {
      console.error('Remitano verification error:', error)
      setRemitanoResult({
        success: false,
        verified: false,
        message: 'Failed to connect to verification service',
        error: error.message
      })
      setMessage({ type: 'error', text: 'Failed to verify blockchain transaction' })
      return false
    } finally {
      setRemitanoVerifying(false)
    }
  }

  const closeRemitanoModal = () => {
    setShowRemitanoModal(false)
    setRemitanoPayment(null)
    setRemitanoChargeId('')
    setRemitanoResult(null)
  }

  const handleApproveWithManualUnderpayTolerance = async (payment: Payment) => {
    if (!canUseManualOverride) {
      setMessage({ type: 'error', text: 'The $1 shortfall override is restricted to Johan/JP.' })
      return
    }
    if (!payment.transaction_hash) {
      setMessage({ type: 'error', text: 'No transaction hash on this payment. Verify the hash before using the $1 shortfall override.' })
      return
    }

    setProcessingPayment(payment.id)
    setRemitanoVerifying(true)
    try {
      const attestation = await runRemitanoVerification(payment, false)
      setRemitanoResult(attestation)
      if (!attestation.verified) {
        setMessage({ type: 'error', text: attestation.error || attestation.message || 'Blockchain attestation failed' })
        return
      }

      const shortfallNum = parseFloat(String(attestation.shortfall || 0))
      if (shortfallNum > MANUAL_UNDERPAY_TOLERANCE_USD) {
        setMessage({ type: 'error', text: `Shortfall $${shortfallNum.toFixed(2)} is above the $1.00 Johan override. Cover with commission or collect the balance.` })
        return
      }

      const approved = await approvePayment(payment, false, undefined, {
        underpayToleranceUsd: MANUAL_UNDERPAY_TOLERANCE_USD,
      })
      if (approved) {
        setShowRemitanoModal(false)
      }
    } catch (error: any) {
      console.error('Manual $1 shortfall override failed:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to approve with $1 shortfall override' })
    } finally {
      setProcessingPayment(null)
      setRemitanoVerifying(false)
    }
  }

  const handleVerifyAndCoverWithCommission = async (payment: Payment) => {
    if (!currentUser?.database_user?.id) {
      setMessage({ type: 'error', text: 'Admin user not found' })
      return
    }
    if (!payment.transaction_hash) {
      setMessage({ type: 'error', text: 'No transaction hash on this payment. Attest an Exodus / BSC hash before covering with commission.' })
      return
    }

    setProcessingPayment(payment.id)
    setRemitanoVerifying(true)
    try {
      const attestation = await runRemitanoVerification(payment, false)
      setRemitanoResult(attestation)
      if (!attestation.verified) {
        setMessage({ type: 'error', text: attestation.error || attestation.message || 'Blockchain attestation failed' })
        return
      }

      const approveResponse = await fetch(`${API_BASE_URL}/api/admin/approve-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
        body: JSON.stringify({
          paymentId: payment.id,
          adminUserId: currentUser.database_user.id,
          coverCommissionShortfall: true,
        })
      })
      const approveResult = await readApiResponse<any>(approveResponse, 'Failed to cover shortfall and approve payment')
      setMessage({
        type: 'success',
        text: approveResult.message
          || (approveResult.data?.shortfallCoveredByCommission
            ? `Payment approved. A shortfall of $${approveResult.data.shortfall} was covered from the user's USDT commission.`
            : 'Payment approved with commission shortfall coverage.')
      })
      await loadPayments()
      setShowRemitanoModal(false)
    } catch (error: any) {
      console.error('Verify & Cover with Commission failed:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to cover shortfall with commission' })
    } finally {
      setProcessingPayment(null)
      setRemitanoVerifying(false)
    }
  }

  // Open Remitano rejection modal with pre-populated reason
  const openRemitanoRejectionModal = () => {
    if (!remitanoResult?.validationErrors) return

    // Build rejection reason from validation errors
    const reasons = remitanoResult.validationErrors.map(err => {
      if (err.type === 'SENDER_MISMATCH') {
        return `Sender wallet mismatch: The transaction was sent from ${err.found}, but we expected ${err.expected}.`
      } else if (err.type === 'RECEIVER_MISMATCH') {
        return `Receiver wallet mismatch: The transaction was sent to ${err.found}, but our company wallet is ${err.expected}.`
      }
      return `${err.label}: Found ${err.found}, expected ${err.expected}`
    })

    setRemitanoRejectionReason(reasons.join('\n\n'))
    setRemitanoRejectionNotes('')
    setShowRemitanoRejectionModal(true)
  }

  // Handle Remitano rejection submission
  const handleRemitanoRejectionSubmit = async () => {
    if (!remitanoPayment || !remitanoRejectionReason.trim()) {
      setMessage({ type: 'error', text: 'Please provide a rejection reason' })
      return
    }

    if (!currentUser?.database_user?.id) {
      setMessage({ type: 'error', text: 'Admin user not found' })
      return
    }

    setProcessingRemitanoRejection(true)

    try {
      // Combine rejection reason with admin notes
      const fullRejectionReason = remitanoRejectionNotes.trim()
        ? `${remitanoRejectionReason.trim()}\n\nAdmin Notes: ${remitanoRejectionNotes.trim()}`
        : remitanoRejectionReason.trim()

      // Parse existing notes
      let existingNotes = {}
      try {
        existingNotes = typeof remitanoPayment.transaction_notes === 'string'
          ? JSON.parse(remitanoPayment.transaction_notes)
          : (remitanoPayment.transaction_notes || {})
      } catch (e) {
        existingNotes = {}
      }

      // Call API to reject payment
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/payment-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'reject_remitano_payment',
          data: {
            paymentId: remitanoPayment.id,
            rejectionReason: fullRejectionReason,
            adminId: currentUser.database_user.id,
            existingNotes,
            validationErrors: remitanoResult?.validationErrors || null
          }
        })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to reject payment')

      // Send rejection email to user
      if (remitanoPayment.users?.email) {
        try {
          await fetch('/api/send-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
            body: JSON.stringify({
              emailType: 'payment_rejection',
              data: {
                email: remitanoPayment.users.email,
                fullName: remitanoPayment.users.full_name || remitanoPayment.users.username,
                amount: remitanoPayment.amount,
                network: remitanoPayment.network,
                transactionHash: remitanoPayment.transaction_hash || '',
                rejectionReason: fullRejectionReason,
                rejectedDate: new Date().toISOString(),
                dashboardUrl: `${typeof window !== 'undefined' ? window.location.origin : 'https://aureus.africa'}/dashboard`
              }
            })
          })
        } catch (emailError) {
          console.error('Failed to send rejection email:', emailError)
        }
      }

      setMessage({ type: 'success', text: 'Payment rejected and user notified via email' })

      // Close modals and reload
      setShowRemitanoRejectionModal(false)
      closeRemitanoModal()
      await loadPayments()

    } catch (error: any) {
      console.error('Error rejecting Remitano payment:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to reject payment' })
    } finally {
      setProcessingRemitanoRejection(false)
    }
  }

  const handleRejectSubmit = () => {
    if (!selectedPayment || !rejectionReason.trim()) {
      setMessage({ type: 'error', text: 'Please provide a rejection reason' })
      return
    }

    if (rejectionReason.trim().length < 5) {
      setMessage({ type: 'error', text: 'Rejection reason must be at least 5 characters long' })
      return
    }

    rejectPayment(selectedPayment, rejectionReason)
  }

  // Save transaction notes to database
  const saveTransactionNotes = async (paymentId: string, notes: string) => {
    try {
      setSavingNotes(prev => new Set(prev).add(paymentId))

      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/payment-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'save_admin_notes',
          data: {
            paymentId,
            notes: notes.trim() || null
          }
        })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to save notes')

      setMessage({ type: 'success', text: 'Notes saved successfully' })

      // Update the payment in the local state
      setPayments(prev => prev.map(p =>
        p.id === paymentId ? { ...p, admin_notes: notes.trim() || undefined } : p
      ))
    } catch (error: any) {
      console.error('Error saving transaction notes:', error)
      setMessage({ type: 'error', text: 'Failed to save notes: ' + error.message })
    } finally {
      setSavingNotes(prev => {
        const newSet = new Set(prev)
        newSet.delete(paymentId)
        return newSet
      })
    }
  }

  // Fix transaction share calculation
  const fixTransactionShares = async (payment: Payment) => {
    try {
      setProcessingPayment(payment.id)

      // Get the validation result to get the correct share count
      const validation = shareValidationResults.get(payment.id)
      if (!validation) {
        throw new Error('Cannot fix transaction - validation data not available')
      }

      const correctShares = validation.expectedShares
      const currentShares = payment.shares_to_purchase

      if (correctShares === currentShares) {
        setMessage({ type: 'info', text: 'Transaction already has correct share count' })
        return
      }

      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/payment-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'fix_share_discrepancy',
          data: {
            paymentId: payment.id,
            correctShares,
            originalShares: currentShares,
            phasePrice: validation.phasePrice,
            adminId: currentUser?.id
          }
        })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to fix transaction')

      setMessage({
        type: 'success',
        text: `Transaction fixed! Shares updated from ${currentShares} to ${correctShares} (${validation.phaseName} @ $${validation.phasePrice}/share)`
      })

      // Reload payments to show updated data
      await loadPayments()

    } catch (error: any) {
      console.error('Error fixing transaction:', error)
      setMessage({ type: 'error', text: 'Failed to fix transaction: ' + error.message })
    } finally {
      setProcessingPayment(null)
    }
  }

  const handleHashLookup = async (searchInput: string) => {
    if (!searchInput) {
      setHashLookupResult({
        found: false,
        loading: false
      })
      setHashLookupPricingAnalysis(null)
      return
    }

    setHashLookupResult({ found: false, loading: true })
    setHashLookupPricingAnalysis(null)

    try {
      const searchTerm = searchInput.trim()

      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/payment-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'comprehensive_hash_lookup',
          data: { searchTerm }
        })
      })

      const rawResult = await response.json()
      if (!response.ok) throw new Error(rawResult.error || 'Failed to search')

      // Unwrap the { success, data } envelope from the API
      const result = rawResult.data || rawResult

      // Handle different result types
      if (result.found) {
        switch (result.type) {
          case 'payment_id_match':
          case 'hash_match':
          case 'bank_reference_match':
          case 'pattern_match':
            setHashLookupResult({
              found: true,
              payment: result.payment as Payment,
              loading: false
            })
            // Analyze phase pricing for discrepancy detection (non-blocking)
            analyzePhasePricing(result.payment as Payment).then(analysis => {
              if (analysis) setHashLookupPricingAnalysis(analysis)
            })
            break

          case 'temp_ref_user':
            setHashLookupResult({
              found: true,
              tempRefUser: {
                id: result.tempRefUser.id,
                username: result.tempRefUser.username,
                email: result.tempRefUser.email,
                full_name: result.tempRefUser.full_name,
                temp_bank_reference: result.tempRefUser.temp_bank_reference,
                payments: (result.tempRefUser.payments || []) as Payment[]
              },
              loading: false
            })
            break

          case 'username_search':
            setHashLookupResult({
              found: true,
              usernameSearchResults: {
                searchTerm: result.usernameSearchResults.searchTerm,
                matchingUsers: result.usernameSearchResults.matchingUsers,
                payments: result.usernameSearchResults.payments as Payment[]
              },
              loading: false
            })
            break
        }
        return
      }

      // Handle fuzzy search data from server
      if (result.type === 'fuzzy_data' && searchTerm.length >= 3) {
        console.log('🔍 [FUZZY SEARCH] Processing fuzzy data from server...')

        const fuzzyMatches: Array<{
          type: 'temp_reference' | 'payment_reference' | 'username'
          reference: string
          user?: {
            id: number
            username: string
            email: string
            full_name: string | null
            temp_bank_reference?: string
          }
          payment?: Payment
          matchResult: FuzzyMatchResult
        }> = []

        const { tempRefs, bankPayments } = result.fuzzyData

        // Process temp references for fuzzy matching
        if (tempRefs && tempRefs.length > 0) {
          const tempRefStrings = tempRefs.map((u: any) => u.temp_bank_reference).filter(Boolean)
          const tempRefFuzzyMatches = findFuzzyMatches(searchTerm, tempRefStrings, {
            minScore: 0.5,
            maxResults: 5,
            maxEditDistance: 3
          })

          for (const match of tempRefFuzzyMatches) {
            const user = tempRefs.find((u: any) => u.temp_bank_reference === match.value)
            if (user) {
              fuzzyMatches.push({
                type: 'temp_reference',
                reference: match.value,
                user: {
                  id: user.id,
                  username: user.username,
                  email: user.email,
                  full_name: user.full_name,
                  temp_bank_reference: user.temp_bank_reference || undefined
                },
                matchResult: match
              })
            }
          }
        }

        // Process bank payment references for fuzzy matching
        if (bankPayments && bankPayments.length > 0) {
          const paymentRefs: { ref: string; payment: any }[] = []
          for (const payment of bankPayments) {
            if (payment.transaction_notes) {
              try {
                const notes = typeof payment.transaction_notes === 'string'
                  ? JSON.parse(payment.transaction_notes)
                  : payment.transaction_notes
                if (notes.reference_number) {
                  paymentRefs.push({ ref: notes.reference_number, payment })
                }
              } catch (e) { /* ignore */ }
            }
          }

          const paymentRefStrings = paymentRefs.map(p => p.ref)
          const paymentRefFuzzyMatches = findFuzzyMatches(searchTerm, paymentRefStrings, {
            minScore: 0.5,
            maxResults: 5,
            maxEditDistance: 3
          })

          for (const match of paymentRefFuzzyMatches) {
            const refData = paymentRefs.find(p => p.ref === match.value)
            if (refData) {
              fuzzyMatches.push({
                type: 'payment_reference',
                reference: match.value,
                user: refData.payment.users ? {
                  id: refData.payment.users.id,
                  username: refData.payment.users.username,
                  email: refData.payment.users.email,
                  full_name: refData.payment.users.full_name,
                  temp_bank_reference: refData.payment.users.temp_bank_reference || undefined
                } : undefined,
                payment: refData.payment as Payment,
                matchResult: match
              })
            }
          }
        }

        // Sort by score and remove duplicates
        fuzzyMatches.sort((a, b) => b.matchResult.score - a.matchResult.score)
        const uniqueMatches = fuzzyMatches.filter((match, index, self) =>
          index === self.findIndex(m => m.reference === match.reference)
        ).slice(0, 8)

        if (uniqueMatches.length > 0) {
          console.log(`🔍 [FUZZY SEARCH] Found ${uniqueMatches.length} possible matches`)
          setHashLookupResult({
            found: false,
            fuzzyMatches: uniqueMatches,
            loading: false,
            error: `No exact match found. Did you mean one of these? (${uniqueMatches.length} similar references found)`
          })
          return
        }
      }

      // Nothing found at all
      setHashLookupResult({
        found: false,
        loading: false,
        error: 'No payment or user found with this transaction hash or reference number'
      })
    } catch (error: any) {
      console.error('Hash lookup failed:', error)
      setHashLookupResult({
        found: false,
        loading: false,
        error: error.message || 'Failed to lookup hash'
      })
    }
  }

  // Debounced search handler with minimum character threshold
  const handleHashInputChange = (value: string) => {
    setHashLookupInput(value)

    // Clear existing timer
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }

    const trimmedValue = value.trim()

    // If input is empty, clear results immediately
    if (!trimmedValue) {
      setHashLookupResult({ found: false, loading: false })
      return
    }

    // Minimum 3 character threshold before triggering search
    if (trimmedValue.length < 3) {
      setHashLookupResult({
        found: false,
        loading: false,
        error: 'Enter at least 3 characters to search...'
      })
      return
    }

    // Show loading state immediately
    setHashLookupResult({ found: false, loading: true })

    // Set new timer for debounced search (300ms as requested)
    const timer = setTimeout(() => {
      handleHashLookup(trimmedValue)
    }, 300) // 300ms debounce delay for responsive AJAX search

    setDebounceTimer(timer)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Payment Management</h2>
        <button
          onClick={loadPayments}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success' ? 'bg-green-900/20 border-green-500/30 text-green-300' :
          message.type === 'error' ? 'bg-red-900/20 border-red-500/30 text-red-300' :
          'bg-blue-900/20 border-blue-500/30 text-blue-300'
        }`}>
          {message.text}
        </div>
      )}

      {/* Transaction Hash Lookup Section */}
      <div className="bg-gray-800 rounded-lg border border-yellow-500/30 p-6">
        <h3 className="text-lg font-semibold text-yellow-500 mb-4">🔍 Transaction Hash Lookup</h3>

        <div className="mb-4">
          <input
            type="text"
            value={hashLookupInput}
            onChange={(e) => handleHashInputChange(e.target.value)}
            placeholder="Enter transaction hash, bank reference (e.g., AUR103125-LUC), or payment ID (UUID)..."
            className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
          />
          {hashLookupResult.loading && (
            <p className="text-white text-sm mt-2">🔄 Searching...</p>
          )}
        </div>

        {/* Results Display */}
        {hashLookupResult.error && !hashLookupResult.fuzzyMatches && (
          <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
            <p className="text-red-300">❌ {hashLookupResult.error}</p>
          </div>
        )}

        {/* Fuzzy Match Suggestions */}
        {hashLookupResult.fuzzyMatches && hashLookupResult.fuzzyMatches.length > 0 && (
          <div className="border rounded-lg overflow-hidden bg-amber-900/20 border-amber-500/30">
            {/* Header */}
            <div className="px-4 py-2 bg-amber-900/30 border-b border-amber-500/30 flex items-center justify-between">
              <p className="font-semibold text-amber-300 text-sm flex items-center gap-2">
                🔍 Did you mean one of these? ({hashLookupResult.fuzzyMatches.length} similar references)
              </p>
              <span className="px-2 py-0.5 rounded text-sm font-bold bg-amber-600 text-white">
                FUZZY MATCHES
              </span>
            </div>

            {/* Fuzzy Match List */}
            <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
              {hashLookupResult.fuzzyMatches.map((match, idx) => {
                // Get character-by-character comparison
                const comparison = compareCharacters(hashLookupInput, match.reference);

                return (
                  <div
                    key={idx}
                    className="p-3 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-amber-500/50 transition-colors cursor-pointer"
                    onClick={() => {
                      // Click to search for this exact reference
                      setHashLookupInput(match.reference)
                      handleHashLookup(match.reference)
                    }}
                  >
                    {/* Character-by-Character Comparison */}
                    <div className="mb-3 p-2 bg-gray-900/50 rounded border border-gray-700">
                      <div className="flex items-center gap-1 text-sm mb-1">
                        <span className="text-gray-500 w-16">You typed:</span>
                        <div className="flex font-mono">
                          {comparison.comparisons.map((comp, i) => (
                            <span
                              key={i}
                              className={`px-0.5 rounded ${getCharStatusColor(comp.status)}`}
                              title={comp.status === 'match' ? 'Correct' :
                                     comp.status === 'adjacent' ? 'Keyboard typo' :
                                     comp.status === 'mismatch' ? 'Wrong character' :
                                     comp.status === 'missing' ? 'Missing in match' : 'Extra character'}
                            >
                              {comp.searchChar}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 text-sm">
                        <span className="text-gray-500 w-16">Match:</span>
                        <div className="flex font-mono">
                          {comparison.comparisons.map((comp, i) => (
                            <span
                              key={i}
                              className={`px-0.5 rounded ${getCharStatusColor(comp.status)}`}
                            >
                              {comp.matchChar}
                            </span>
                          ))}
                        </div>
                      </div>
                      {/* Legend */}
                      <div className="flex gap-3 mt-2 text-[10px] text-gray-500 border-t border-gray-700 pt-1.5">
                        <span className="flex items-center gap-1">
                          <span className="w-2 h-2 rounded bg-green-500"></span> Correct ({comparison.matchCount})
                        </span>
                        {comparison.mismatchCount > 0 && (
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded bg-red-500"></span> Wrong ({comparison.mismatchCount})
                          </span>
                        )}
                        {comparison.adjacentCount > 0 && (
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded bg-amber-500"></span> Typo ({comparison.adjacentCount})
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-amber-400 font-mono font-bold text-sm">
                          {match.reference}
                        </span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${getMatchTypeColor(match.matchResult.matchType)}`}>
                          {getMatchTypeLabel(match.matchResult.matchType)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-white text-sm">
                          {Math.round(match.matchResult.score * 100)}% match
                        </span>
                        {match.type === 'temp_reference' && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-600 text-white">
                            TEMP REF
                          </span>
                        )}
                        {match.type === 'payment_reference' && (
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                            match.payment?.status === 'approved' ? 'bg-green-600 text-white' :
                            match.payment?.status === 'pending' ? 'bg-yellow-600 text-black' :
                            'bg-red-600 text-white'
                          }`}>
                            {match.payment?.status?.toUpperCase() || 'PAYMENT'}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* User Info */}
                    {match.user && (
                      <div className="text-sm text-white grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-gray-500">User:</span>{' '}
                          <span className="text-white">@{match.user.username}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Email:</span>{' '}
                          <span className="text-white">{match.user.email}</span>
                        </div>
                      {match.user.full_name && (
                        <div className="col-span-2">
                          <span className="text-gray-500">Name:</span>{' '}
                          <span className="text-white">{match.user.full_name}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Payment Info if available */}
                  {match.payment && (
                    <div className="text-sm text-white grid grid-cols-3 gap-2 mt-2 pt-2 border-t border-gray-700">
                      <div>
                        <span className="text-gray-500">Amount:</span>{' '}
                        <span className="text-green-400 font-medium">${match.payment.amount}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Shares:</span>{' '}
                        <span className="text-white">{match.payment.shares_to_purchase}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Date:</span>{' '}
                        <span className="text-white">{new Date(match.payment.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  )}

                    {/* Click hint */}
                    <p className="text-amber-400/60 text-[10px] mt-2 italic">
                      💡 Click to search for this reference
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Search Term Info */}
            <div className="px-4 py-2 bg-gray-900/50 border-t border-gray-700">
              <p className="text-white text-sm">
                Searched for: <span className="text-white font-mono">"{hashLookupInput}"</span>
                {hashLookupResult.fuzzyMatches[0] && (
                  <span className="ml-2">
                    | Best match: <span className="text-amber-400 font-mono">{hashLookupResult.fuzzyMatches[0].reference}</span>
                    {' '}({Math.round(hashLookupResult.fuzzyMatches[0].matchResult.score * 100)}% similar)
                  </span>
                )}
              </p>
            </div>
          </div>
        )}

        {hashLookupResult.found && hashLookupResult.payment && (
          <div className={`border rounded-lg overflow-hidden ${
            hashLookupResult.payment.status === 'pending'
              ? 'bg-yellow-900/20 border-yellow-500/30'
              : 'bg-red-900/20 border-red-500/30'
          }`}>
            {/* Compact Header with Status */}
            <div className={`px-3 py-1.5 border-b flex items-center justify-between ${
              hashLookupResult.payment.status === 'pending'
                ? 'bg-yellow-900/30 border-yellow-500/30'
                : 'bg-red-900/30 border-red-500/30'
            }`}>
              <p className={`font-semibold text-sm ${
                hashLookupResult.payment.status === 'pending' ? 'text-yellow-300' : 'text-red-300'
              }`}>
                {hashLookupResult.payment.status === 'pending' ? '⏳ Pending Payment Found' : '❌ Hash Already Used'}
              </p>
              <span className={`px-2 py-0.5 rounded text-sm font-bold ${
                hashLookupResult.payment.status === 'approved' ? 'bg-green-600 text-white' :
                hashLookupResult.payment.status === 'rejected' ? 'bg-red-600 text-white' :
                hashLookupResult.payment.status === 'reversed' ? 'bg-orange-600 text-white' :
                'bg-yellow-600 text-black'
              }`}>
                {hashLookupResult.payment.status.toUpperCase()}
              </span>
            </div>

            {/* Compact Grid Layout */}
            <div className="p-2">
              <div className="grid grid-cols-4 gap-x-3 gap-y-1 text-sm">
                <div>
                  <span className="text-gray-500">User:</span>{' '}
                  <button
                    onClick={() => window.open(`${window.location.origin}?admin=true#users&search=${encodeURIComponent(hashLookupResult.payment!.users?.username || hashLookupResult.payment!.user_id)}`, '_blank')}
                    className="text-blue-400 hover:text-blue-300 hover:underline font-medium"
                    title="Open User Management"
                  >
                    {hashLookupResult.payment.users?.username || 'N/A'} 🔗
                  </button>
                </div>
                <div><span className="text-gray-500">Amount:</span> <span className="text-white font-medium">${hashLookupResult.payment.amount} {hashLookupResult.payment.currency}</span></div>
                <div><span className="text-gray-500">Shares:</span> <span className="text-white font-medium">{hashLookupResult.payment.shares_to_purchase}</span></div>
                <div><span className="text-gray-500">Network:</span> <span className="text-white">{hashLookupResult.payment.network}</span></div>
                <div><span className="text-gray-500">ID:</span> <span className="text-white">{hashLookupResult.payment.user_id}</span></div>
                <div><span className="text-gray-500">Email:</span> <span className="text-white">{hashLookupResult.payment.users?.email || 'N/A'}</span></div>
                <div><span className="text-gray-500">Created:</span> <span className="text-white">{new Date(hashLookupResult.payment.created_at).toLocaleString()}</span></div>
                {/* Approval Date - Show when approved */}
                {hashLookupResult.payment.approved_at && (
                  <div><span className="text-gray-500">Approved:</span> <span className="text-green-400">{new Date(hashLookupResult.payment.approved_at).toLocaleString()}</span></div>
                )}
              </div>

              {/* Enhanced Bank Transfer Details with Calculation Breakdown */}
              {hashLookupResult.payment.network === 'BANK_TRANSFER' && hashLookupResult.payment.transaction_notes && (() => {
                try {
                  const notes = typeof hashLookupResult.payment!.transaction_notes === 'string'
                    ? JSON.parse(hashLookupResult.payment!.transaction_notes)
                    : hashLookupResult.payment!.transaction_notes
                  if (notes.zar_amount || notes.exchange_rate) {
                    const feePercent = notes.transaction_fee_percent || 10
                    const exchangeRate = notes.exchange_rate || 0
                    const zarAmount = notes.zar_amount || 0
                    const usdAmount = hashLookupResult.payment!.amount
                    // Calculate what the base ZAR would be before fee
                    const baseZar = zarAmount / (1 + feePercent / 100)
                    const feeZar = zarAmount - baseZar

                    return (
                      <div className="mt-2 p-2 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                        <p className="text-blue-300 font-semibold text-sm mb-2 flex items-center gap-1">
                          🏦 Bank Transfer Calculation Breakdown
                        </p>
                        <div className="space-y-1 text-sm">
                          {/* Reference Number - Prominent */}
                          {notes.reference_number && (
                            <div className="flex items-center justify-between bg-gray-900/50 rounded px-2 py-1 mb-2">
                              <span className="text-white">Payment Reference:</span>
                              <span className="text-yellow-400 font-mono font-bold text-sm">{notes.reference_number}</span>
                            </div>
                          )}

                          {/* Calculation Flow */}
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                            <div className="flex justify-between">
                              <span className="text-white">USD Amount:</span>
                              <span className="text-white font-medium">${usdAmount.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-white">Exchange Rate:</span>
                              <span className="text-white">R{exchangeRate} / $1</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-white">Base ZAR:</span>
                              <span className="text-white">R{baseZar.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-white">Transaction Fee ({feePercent}%):</span>
                              <span className="text-orange-400">+R{feeZar.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                            </div>
                          </div>

                          {/* Total to Pay - Highlighted */}
                          <div className="flex items-center justify-between bg-green-900/30 rounded px-2 py-1.5 mt-2">
                            <span className="text-green-300 font-medium">Total ZAR to Pay:</span>
                            <span className="text-green-400 font-bold text-lg">R{zarAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                          </div>

                          {/* Verification Formula */}
                          <div className="text-gray-500 text-sm mt-1 text-center">
                            ${usdAmount} × R{exchangeRate} × 1.{feePercent.toString().padStart(2, '0')} = R{zarAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </div>
                        </div>
                      </div>
                    )
                  }
                } catch (e) { /* ignore parse errors */ }
                return null
              })()}

              {/* Temp Bank Reference if user has one */}
              {(hashLookupResult.payment.users as any)?.temp_bank_reference && (
                <div className="mt-1.5 pt-1.5 border-t border-gray-700">
                  <span className="text-gray-500 text-sm">Temp Bank Ref:</span>{' '}
                  <span className="text-amber-400 font-mono text-sm font-bold">{(hashLookupResult.payment.users as any).temp_bank_reference}</span>
                </div>
              )}

              {/* Hash display with Blockchain Explorer Link */}
              {hashLookupResult.payment.transaction_hash && hashLookupResult.payment.network !== 'BANK_TRANSFER' && (
                <div className="mt-1.5 pt-1.5 border-t border-gray-700">
                  <div className="flex items-center justify-between">
                    <p className="text-gray-500 text-sm">
                      Hash: <span className="text-white font-mono text-sm">{hashLookupResult.payment.transaction_hash.slice(0, 20)}...{hashLookupResult.payment.transaction_hash.slice(-10)}</span>
                    </p>
                    {getBlockchainExplorerUrl(hashLookupResult.payment.network, hashLookupResult.payment.transaction_hash) && (
                      <a
                        href={getBlockchainExplorerUrl(hashLookupResult.payment.network, hashLookupResult.payment.transaction_hash) || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm px-2 py-0.5 bg-blue-600 hover:bg-blue-700 text-white rounded flex items-center gap-1"
                      >
                        🔗 View on Explorer
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Phase Pricing Discrepancy Warning */}
              {hashLookupPricingAnalysis && hashLookupPricingAnalysis.hasDiscrepancy && (
                <div className="mt-2 p-2 bg-orange-900/30 border border-orange-500/50 rounded-lg">
                  <div className="flex items-start gap-2">
                    <span className="text-lg">⚠️</span>
                    <div className="flex-1">
                      <p className="text-orange-300 font-semibold text-sm">Phase Pricing Discrepancy Detected</p>
                      <p className="text-orange-200 text-sm mt-1">{hashLookupPricingAnalysis.discrepancyMessage}</p>
                      <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                        <div>
                          <span className="text-white">Active Phase at Payment:</span>{' '}
                          <span className="text-white">
                            {hashLookupPricingAnalysis.activePhaseAtPayment?.phase_name || 'Unknown'}
                            (${hashLookupPricingAnalysis.activePhaseAtPayment?.price_per_share?.toFixed(2) || '?'}/share)
                          </span>
                        </div>
                        <div>
                          <span className="text-white">Phase Used:</span>{' '}
                          <span className="text-white">
                            {hashLookupPricingAnalysis.usedPhase?.phase_name || 'Unknown'}
                            (${hashLookupPricingAnalysis.usedPhase?.price_per_share?.toFixed(2) || '?'}/share)
                          </span>
                        </div>
                        <div>
                          <span className="text-white">Expected Shares:</span>{' '}
                          <span className="text-green-400 font-bold">{hashLookupPricingAnalysis.expectedShares.toFixed(2)}</span>
                        </div>
                        <div>
                          <span className="text-white">Actual Shares:</span>{' '}
                          <span className={hashLookupPricingAnalysis.discrepancyAmount > 0 ? 'text-red-400 font-bold' : 'text-white'}>
                            {hashLookupPricingAnalysis.actualShares.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setShareAdjustmentNewShares(hashLookupPricingAnalysis.expectedShares.toFixed(2))
                          setShareAdjustmentReason(`Phase pricing correction: User paid during ${hashLookupPricingAnalysis.activePhaseAtPayment?.phase_name} but received shares calculated at ${hashLookupPricingAnalysis.usedPhase?.phase_name} pricing`)
                          setShowShareAdjustmentModal(true)
                        }}
                        className="mt-2 px-3 py-1 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded"
                      >
                        🔧 Fix Share Allocation
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Phase Pricing Info (no discrepancy) */}
              {hashLookupPricingAnalysis && !hashLookupPricingAnalysis.hasDiscrepancy && hashLookupPricingAnalysis.activePhaseAtPayment && (
                <div className="mt-1.5 pt-1.5 border-t border-gray-700">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">Phase at Payment:</span>{' '}
                      <span className="text-white">{hashLookupPricingAnalysis.activePhaseAtPayment.phase_name}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Price/Share:</span>{' '}
                      <span className="text-green-400">${hashLookupPricingAnalysis.activePhaseAtPayment.price_per_share.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Manual Share Adjustment Button for approved transactions */}
              {hashLookupResult.payment.status === 'approved' && (
                <div className="mt-1.5 pt-1.5 border-t border-gray-700 flex items-center gap-2">
                  <button
                    onClick={() => {
                      setShareAdjustmentNewShares(String(hashLookupResult.payment!.shares_to_purchase || 0))
                      setShareAdjustmentReason('')
                      setShowShareAdjustmentModal(true)
                    }}
                    className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded flex items-center gap-1"
                  >
                    ✏️ Adjust Shares
                  </button>
                  <span className="text-gray-500 text-sm">Manually correct share allocation</span>
                </div>
              )}

              {/* Quick Actions for Pending Payments */}
              {hashLookupResult.payment.status === 'pending' && (
                <div className="mt-2 pt-2 border-t border-yellow-500/30 flex items-center gap-2">
                  <button
                    onClick={() => {
                      setSelectedPayment(hashLookupResult.payment!)
                    }}
                    className="flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded transition-colors"
                  >
                    📋 View Details
                  </button>
                  <button
	                    onClick={() => handleManualOverrideClick(hashLookupResult.payment!)}
                    disabled={processingPayment === hashLookupResult.payment!.id}
                    className="flex-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
                  >
                    {processingPayment === hashLookupResult.payment!.id ? (
                      <>
                        <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Approving...
                      </>
                    ) : (
                      '✅ Approve'
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setSelectedPayment(hashLookupResult.payment!)
                      setShowRejectionModal(true)
                    }}
                    className="flex-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded transition-colors"
                  >
                    ❌ Reject
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Temp Bank Reference User Result (User has reference but hasn't submitted payment yet) */}
        {hashLookupResult.found && hashLookupResult.tempRefUser && (
          <div className="border rounded-lg overflow-hidden bg-blue-900/20 border-blue-500/30">
            {/* Header */}
            <div className="px-4 py-2 bg-blue-900/30 border-b border-blue-500/30 flex items-center justify-between">
              <p className="font-semibold text-blue-300 text-sm flex items-center gap-2">
                🏦 Temporary Bank Reference Found
              </p>
              <span className="px-2 py-0.5 rounded text-sm font-bold bg-blue-600 text-white">
                PENDING PAYMENT
              </span>
            </div>

            {/* User Info */}
            <div className="p-4">
              <div className="bg-gray-800/50 rounded-lg p-3 mb-4">
                <h4 className="text-white font-semibold text-sm mb-2">👤 User Details</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500 block">User ID:</span>
                    <span className="text-white font-medium">{hashLookupResult.tempRefUser.id}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 block">Username:</span>
                    <span className="text-white font-medium">@{hashLookupResult.tempRefUser.username}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 block">Email:</span>
                    <span className="text-white font-medium">{hashLookupResult.tempRefUser.email || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 block">Full Name:</span>
                    <span className="text-white font-medium">{hashLookupResult.tempRefUser.full_name || 'N/A'}</span>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-700">
                  <span className="text-gray-500 text-sm">Bank Reference:</span>
                  <span className="text-yellow-400 font-mono font-bold text-sm ml-2">{hashLookupResult.tempRefUser.temp_bank_reference}</span>
                  <p className="text-amber-400 text-sm mt-1">⚠️ This user has generated a bank reference but has NOT yet submitted their payment.</p>
                </div>
              </div>

              {/* User's Payment History */}
              <div className="bg-gray-800/50 rounded-lg p-3">
                <h4 className="text-white font-semibold text-sm mb-2">📜 Payment History ({hashLookupResult.tempRefUser.payments.length} orders)</h4>
                {hashLookupResult.tempRefUser.payments.length === 0 ? (
                  <p className="text-white text-sm italic">No previous payments found for this user.</p>
                ) : (
                  <div className="max-h-60 overflow-y-auto space-y-2">
                    {hashLookupResult.tempRefUser.payments.map((payment, idx) => (
                      <div
                        key={payment.id || idx}
                        className={`p-2 rounded border text-sm ${
                          payment.status === 'approved'
                            ? 'bg-green-900/20 border-green-500/30'
                            : payment.status === 'rejected'
                            ? 'bg-red-900/20 border-red-500/30'
                            : 'bg-yellow-900/20 border-yellow-500/30'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                            payment.status === 'approved' ? 'bg-green-600 text-white' :
                            payment.status === 'rejected' ? 'bg-red-600 text-white' :
                            'bg-yellow-600 text-black'
                          }`}>
                            {payment.status}
                          </span>
                          <span className="text-white">{new Date(payment.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-[11px]">
                          <div>
                            <span className="text-gray-500">Amount:</span>
                            <span className="text-white ml-1">${payment.amount} {payment.currency}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Shares:</span>
                            <span className="text-white ml-1">{payment.shares_to_purchase}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Network:</span>
                            <span className="text-white ml-1">{payment.network}</span>
                          </div>
                        </div>
                        {payment.transaction_notes && (() => {
                          try {
                            const notes = JSON.parse(payment.transaction_notes)
                            if (notes.reference_number) {
                              return (
                                <div className="mt-1 pt-1 border-t border-gray-700">
                                  <span className="text-gray-500">Ref:</span>
                                  <span className="text-yellow-400 font-mono ml-1">{notes.reference_number}</span>
                                </div>
                              )
                            }
                          } catch (e) { return null }
                          return null
                        })()}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Username Search Results (e.g., -MIR or -DIA) */}
        {hashLookupResult.found && hashLookupResult.usernameSearchResults && (
          <div className="border rounded-lg overflow-hidden bg-purple-900/20 border-purple-500/30">
            {/* Header */}
            <div className="px-4 py-2 bg-purple-900/30 border-b border-purple-500/30 flex items-center justify-between">
              <p className="font-semibold text-purple-300 text-sm flex items-center gap-2">
                🔍 Username Search: "{hashLookupResult.usernameSearchResults.searchTerm}"
              </p>
              <span className="px-2 py-0.5 rounded text-sm font-bold bg-purple-600 text-white">
                {hashLookupResult.usernameSearchResults.payments.length} PAYMENTS FOUND
              </span>
            </div>

            {/* Matching Users */}
            <div className="p-4">
              <div className="bg-gray-800/50 rounded-lg p-3 mb-4">
                <h4 className="text-white font-semibold text-sm mb-2">👥 Matching Users ({hashLookupResult.usernameSearchResults.matchingUsers.length})</h4>
                <div className="flex flex-wrap gap-2">
                  {hashLookupResult.usernameSearchResults.matchingUsers.map((user) => (
                    <span key={user.id} className="px-2 py-1 bg-purple-600/30 text-purple-300 rounded text-sm">
                      @{user.username} (ID: {user.id})
                    </span>
                  ))}
                </div>
              </div>

              {/* Payments List */}
              <div className="bg-gray-800/50 rounded-lg p-3">
                <h4 className="text-white font-semibold text-sm mb-2">📜 All Payments</h4>
                {hashLookupResult.usernameSearchResults.payments.length === 0 ? (
                  <p className="text-white text-sm italic">No payments found for matching users.</p>
                ) : (
                  <div className="max-h-80 overflow-y-auto space-y-2">
                    {hashLookupResult.usernameSearchResults.payments.map((payment: any, idx) => (
                      <div
                        key={payment.id || idx}
                        className={`p-3 rounded border text-sm ${
                          payment.status === 'approved'
                            ? 'bg-green-900/20 border-green-500/30'
                            : payment.status === 'rejected'
                            ? 'bg-red-900/20 border-red-500/30'
                            : payment.status === 'flagged_duplicate' || payment.status === 'review_requested'
                            ? 'bg-orange-900/20 border-orange-500/30'
                            : payment.status === 'underpaid'
                            ? 'bg-amber-900/20 border-amber-500/30'
                            : 'bg-yellow-900/20 border-yellow-500/30'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                              payment.status === 'approved' ? 'bg-green-600 text-white' :
                              payment.status === 'rejected' ? 'bg-red-600 text-white' :
                              payment.status === 'flagged_duplicate' || payment.status === 'review_requested' ? 'bg-orange-600 text-white' :
                              payment.status === 'underpaid' ? 'bg-amber-500 text-black' :
                              'bg-yellow-600 text-black'
                            }`}>
                              {payment.status === 'underpaid' ? 'UNDERPAID' : payment.status}
                            </span>
                            <span className="text-purple-400 font-medium">@{payment.users?.username}</span>
                          </div>
                          <span className="text-white">{new Date(payment.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                          <div>
                            <span className="text-gray-500">Amount:</span>
                            <span className="text-white ml-1">${payment.amount} {payment.currency || 'USDT'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Shares:</span>
                            <span className="text-white ml-1">{payment.shares_to_purchase || payment.shares}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Network:</span>
                            <span className="text-white ml-1">{payment.network || 'Bank'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Reference:</span>
                            <span className="text-yellow-400 font-mono ml-1">{payment.transaction_hash || payment.reference || 'N/A'}</span>
                          </div>
                        </div>
                        {payment.source_table && (
                          <div className="mt-1 pt-1 border-t border-gray-700">
                            <span className="text-gray-500 text-[10px]">Source:</span>
                            <span className="text-white text-[10px] ml-1">{payment.source_table}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Payment Type Toggle - Crypto vs Bank vs Yoco */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-2">
        <div className="flex">
          <button
            onClick={() => setPaymentTypeFilter('crypto')}
            className={`flex-1 py-3 px-4 rounded-lg font-semibold text-base transition-all flex items-center justify-center gap-2 ${
              paymentTypeFilter === 'crypto'
                ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-black shadow-lg'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            <span className="text-xl">💎</span>
            <span>Crypto</span>
            <span className="text-sm opacity-75 hidden md:inline">(USDT/BTC)</span>
          </button>
          <button
            onClick={() => setPaymentTypeFilter('bank')}
            className={`flex-1 py-3 px-4 rounded-lg font-semibold text-base transition-all flex items-center justify-center gap-2 ${
              paymentTypeFilter === 'bank'
                ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            <span className="text-xl">🏦</span>
            <span>Bank</span>
            <span className="text-sm opacity-75 hidden md:inline">(EFT/Wire)</span>
          </button>
          <button
            onClick={() => setPaymentTypeFilter('phase-discrepancies')}
            className={`flex-1 py-3 px-4 rounded-lg font-semibold text-base transition-all flex items-center justify-center gap-2 ${
              paymentTypeFilter === 'phase-discrepancies'
                ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            <span className="text-xl">⚠️</span>
            <span>Phase Audit</span>
            <span className="text-sm opacity-75 hidden md:inline">(Discrepancies)</span>
          </button>
          {/* Yoco tab removed (v4.3.61) */}
        </div>
      </div>

      {/* Status Tab Navigation and Manual Payment Button - Hide for Phase Discrepancies */}
      {paymentTypeFilter !== 'phase-discrepancies' && (
        <div className="flex items-center gap-4">
          <div className="flex-1 flex space-x-1 bg-gray-800 rounded-lg p-1">
            {((): Array<'pending' | 'approved' | 'rejected' | 'reversed' | 'underpaid' | 'flagged' | 'duplicates' | 'hash-removal'> => {
              const base: Array<'pending' | 'approved' | 'rejected' | 'reversed' | 'underpaid' | 'flagged' | 'duplicates' | 'hash-removal'> =
                ['pending', 'approved', 'rejected', 'reversed', 'underpaid', 'flagged', 'hash-removal']
              // Duplicate Finder is owner-only; inserted before hash-removal so it
              // only shows for authorised admins.
              if (canUseManualOverride) base.splice(base.length - 1, 0, 'duplicates')
              return base
            })()
              .map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  activeTab === tab
                    ? tab === 'flagged'
                      ? 'bg-orange-500 text-white'
                      : tab === 'underpaid'
                        ? 'bg-amber-500 text-black'
                        : tab === 'reversed'
                          ? 'bg-orange-600 text-white'
                          : tab === 'duplicates'
                            ? 'bg-red-600 text-white'
                            : paymentTypeFilter === 'crypto'
                              ? 'bg-yellow-500 text-black'
                              : 'bg-blue-500 text-white'
                    : 'text-gray-300 hover:text-white hover:bg-gray-700'
                }`}
              >
                {tab === 'hash-removal' ? '🗑️ Remove Hash' :
                 tab === 'flagged' ? '🚩 Flagged' :
                 tab === 'underpaid' ? '⚠️ Underpaid' :
                 tab === 'reversed' ? '🔄 Reversed' :
                 tab === 'duplicates' ? '🧬 Duplicates' :
                 `${tab.charAt(0).toUpperCase() + tab.slice(1)} Payments`}
              </button>
            ))}
          </div>

          {(
            <button
              onClick={() => {
                resetManualPaymentForm()
                setShowManualPaymentModal(true)
              }}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              ➕ Create Manual Payment
            </button>
          )}
        </div>
      )}

      {/* Search Bar and Advanced Filters - Hide on hash-removal/duplicates tabs and phase-discrepancies */}
      {activeTab !== 'hash-removal' && activeTab !== 'duplicates' && paymentTypeFilter !== 'phase-discrepancies' && (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-white text-lg">🔍</span>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Quick Search by username, email, phone, user ID, transaction ID, reference..."
              className="w-full pl-10 pr-10 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white transition-colors"
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
              showAdvancedFilters
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            🔍 Advanced Filters
            {getActiveFilterCount() > 0 && (
              <span className="bg-yellow-500 text-black px-2 py-0.5 rounded-full text-sm font-bold">
                {getActiveFilterCount()}
              </span>
            )}
          </button>
          {debouncedSearchQuery && (
            <div className="text-sm text-white whitespace-nowrap">
              {filteredPayments.length === 0 ? (
                <span className="text-red-400">No results</span>
              ) : (
                <span className="text-green-400">
                  {filteredPayments.length} of {payments.length} {filteredPayments.length === 1 ? 'result' : 'results'}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Advanced Filters Panel */}
        {showAdvancedFilters && (
          <div className="border-t border-gray-700 pt-4 mt-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white font-semibold text-lg">🔍 Advanced Filters</h3>
              <button
                onClick={clearAllFilters}
                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Clear All
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {/* Email Filter */}
              <div>
                <label className="block text-white text-sm mb-1.5">Email Address</label>
                <input
                  type="text"
                  placeholder="user@example.com"
                  value={filters.email}
                  onChange={(e) => setFilters({ ...filters, email: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>

              {/* User ID Filter */}
              <div>
                <label className="block text-white text-sm mb-1.5">User ID</label>
                <input
                  type="number"
                  placeholder="123"
                  value={filters.userId}
                  onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>

              {/* Network Filter */}
              <div>
                <label className="block text-white text-sm mb-1.5">Network</label>
                <select
                  value={filters.network}
                  onChange={(e) => setFilters({ ...filters, network: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 transition-colors"
                >
                  <option value="">All Networks</option>
                  <option value="TRC20">TRC20</option>
                  <option value="ERC20">ERC20</option>
                  <option value="BEP20">BEP20</option>
                  <option value="BANK_TRANSFER">Bank Transfer</option>
                </select>
              </div>

              {/* Currency Filter */}
              <div>
                <label className="block text-white text-sm mb-1.5">Currency</label>
                <select
                  value={filters.currency}
                  onChange={(e) => setFilters({ ...filters, currency: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 transition-colors"
                >
                  <option value="">All Currencies</option>
                  <option value="USDT">USDT</option>
                  <option value="USD">USD</option>
                  <option value="ZAR">ZAR</option>
                </select>
              </div>

              {/* Min Amount Filter */}
              <div>
                <label className="block text-white text-sm mb-1.5">Min Amount</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={filters.minAmount}
                  onChange={(e) => setFilters({ ...filters, minAmount: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>

              {/* Max Amount Filter */}
              <div>
                <label className="block text-white text-sm mb-1.5">Max Amount</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="10000.00"
                  value={filters.maxAmount}
                  onChange={(e) => setFilters({ ...filters, maxAmount: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>

              {/* Transaction Hash / Payment ID Filter */}
              <div>
                <label className="block text-white text-sm mb-1.5">Transaction Hash / Payment ID</label>
                <input
                  type="text"
                  placeholder="0x... or payment UUID"
                  value={filters.transactionHash}
                  onChange={(e) => setFilters({ ...filters, transactionHash: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>

              {/* Start Date Filter */}
              <div>
                <label className="block text-white text-sm mb-1.5">Start Date</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>

              {/* End Date Filter */}
              <div>
                <label className="block text-white text-sm mb-1.5">End Date</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>
            </div>

            {filterLoading && (
              <div className="mt-4 p-3 bg-gray-900 rounded-lg text-center text-white">
                🔄 Applying filters...
              </div>
            )}

            {!filterLoading && payments.length === 0 && getActiveFilterCount() > 0 && (
              <div className="mt-4 p-3 bg-red-900/20 border border-red-500/30 rounded-lg text-center text-red-400">
                ⚠️ No results found matching your filters
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Pagination and Sorting Controls */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Page Size Selector */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-white">Show:</label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value))
                setCurrentPage(1)
              }}
              className="bg-gray-700 text-white px-3 py-1 rounded border border-gray-600 text-sm"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-white">per page</span>
          </div>

          {/* Sort Controls */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-white">Sort by:</span>
            <button
              onClick={() => toggleSort('created_at')}
              className={`px-3 py-1 rounded text-sm transition-colors ${
                sortField === 'created_at'
                  ? 'bg-yellow-500 text-black font-medium'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Created {sortField === 'created_at' && (sortDirection === 'asc' ? '↑' : '↓')}
            </button>
            <button
              onClick={() => toggleSort('approved_at')}
              className={`px-3 py-1 rounded text-sm transition-colors ${
                sortField === 'approved_at'
                  ? 'bg-yellow-500 text-black font-medium'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Approved {sortField === 'approved_at' && (sortDirection === 'asc' ? '↑' : '↓')}
            </button>
          </div>

          {/* Pagination Info */}
          <div className="text-sm text-white">
            {debouncedSearchQuery ? (
              <span>
                Showing {filteredPayments.length} {filteredPayments.length === 1 ? 'result' : 'results'}
                {filteredPayments.length < payments.length && ` (filtered from ${payments.length})`}
              </span>
            ) : (
              <span>
                Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalCount)} of {totalCount}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Duplicate Finder, Hash Removal Manager, Phase Discrepancy Audit, or Regular Payments List */}
      {activeTab === 'duplicates' ? (
        /* Duplicate Finder — approved bank double-approvals (owner-only) */
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-white font-semibold text-lg">🧬 Duplicate Approved Bank Payments</h3>
              <p className="text-gray-400 text-sm">
                Same user + same amount approved within a short window. Cancel a duplicate leg to
                atomically reverse its shares, commissions, team volume and pool credit. The first
                (original) payment is always kept.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-gray-300 text-sm">Window (min):</label>
              <input
                type="number"
                min={1}
                max={120}
                value={duplicateWindowMinutes}
                onChange={(e) => {
                  const v = parseInt(e.target.value)
                  setDuplicateWindowMinutes(Number.isFinite(v) ? Math.min(Math.max(v, 1), 120) : 30)
                }}
                className="w-20 px-2 py-1 bg-gray-900 border border-gray-600 rounded text-white text-sm"
              />
              <button
                onClick={loadDuplicatePayments}
                disabled={duplicatesLoading}
                className="px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium transition-colors disabled:opacity-50"
              >
                {duplicatesLoading ? 'Scanning…' : 'Rescan'}
              </button>
            </div>
          </div>

          {duplicatesLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500 mx-auto mb-4"></div>
              <p className="text-white">Scanning for duplicate bank payments…</p>
            </div>
          ) : duplicateGroups.length === 0 ? (
            <p className="text-white p-4">No duplicate bank payments found within {duplicateWindowMinutes} minutes.</p>
          ) : (
            <div className="space-y-4">
              {duplicateGroups.map((group) => (
                <div key={`${group.user_id}-${group.amount}-${group.first_approved_at}`} className="bg-gray-900 border border-gray-700 rounded-lg p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div className="text-white">
                      <span className="font-semibold">{group.username || group.full_name || `user ${group.user_id}`}</span>
                      <span className="text-gray-400 text-sm"> (id {group.user_id})</span>
                      {group.email && <span className="text-gray-500 text-xs ml-2">{group.email}</span>}
                    </div>
                    <div className="text-right">
                      <div className="text-white font-semibold">${group.amount.toFixed(2)} {group.currency}</div>
                      <div className="text-gray-400 text-xs">{group.count} approvals within {group.window_minutes} min</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {group.legs.map((leg) => (
                      <div key={leg.payment_id} className="flex flex-wrap items-center justify-between gap-2 bg-gray-800 border border-gray-700 rounded px-3 py-2">
                        <div className="text-sm">
                          <div className="text-gray-300 font-mono text-xs">{leg.payment_id}</div>
                          <div className="text-gray-400 text-xs">
                            Approved: {new Date(leg.approved_at).toLocaleString()}
                            {!leg.keep && leg.seconds_after_first != null && (
                              <span className="text-amber-400"> · +{leg.seconds_after_first}s after original</span>
                            )}
                          </div>
                        </div>
                        {leg.keep ? (
                          <span className="px-3 py-1.5 bg-green-900/40 text-green-300 border border-green-700 rounded text-xs font-medium">✓ Keep (original)</span>
                        ) : (
                          <button
                            onClick={() => cancelDuplicatePayment(group, leg)}
                            disabled={cancellingDuplicateId === leg.payment_id || !canUseManualOverride}
                            className="px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-medium transition-colors disabled:opacity-50"
                          >
                            {cancellingDuplicateId === leg.payment_id ? 'Cancelling…' : 'Cancel Duplicate'}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : activeTab === 'hash-removal' ? (
        <HashRemovalManager adminUser={currentUser} />
      ) : paymentTypeFilter === 'phase-discrepancies' ? (
        /* Phase Discrepancy Audit */
        <PhaseDiscrepancyAudit onFixShares={adjustPaymentShares} />
      ) : (
        /* Default payment list (Yoco tab removed v4.3.62) */
        <div className="bg-gray-800 rounded-lg border border-gray-700">
          <div className="px-6 py-3 border-b border-gray-700 flex flex-wrap gap-2">
            <span className="px-3 py-1 rounded-full text-xs font-black bg-blue-600 text-white">NORMAL PAYMENT</span>
            <span className="px-3 py-1 rounded-full text-xs font-black bg-cyan-300 text-black">COMMISSION TOP-UP</span>
            <span className="px-3 py-1 rounded-full text-xs font-black bg-amber-300 text-black">COMMISSION CONVERSION</span>
          </div>
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto mb-4"></div>
              <p className="text-white">Loading payments...</p>
            </div>
          ) : filteredPayments.length === 0 ? (
            <div className="p-8 text-center">
              {debouncedSearchQuery ? (
                <div>
                  <p className="text-white mb-2">No results found for "{debouncedSearchQuery}"</p>
                  <button
                    onClick={() => setSearchQuery('')}
                    className="text-blue-400 hover:text-blue-300 text-sm underline"
                  >
                    Clear search
                  </button>
                </div>
              ) : (
                <p className="text-white">No {activeTab} payments found</p>
              )}
            </div>
          ) : (
          <div className="space-y-0">
            {filteredPayments.map((payment, index) => (
              <div key={payment.id}>
                {index > 0 && <div className="h-0.5 bg-white my-4"></div>}
                <div className={`rounded-lg border overflow-hidden ${paymentKindCardClass(payment)}`}>
                {/* Header with amount and status */}
                <div className="px-6 py-4 border-b border-white/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{getNetworkIcon(payment.network)}</span>
                      <div>
                        <h3 className="text-xl font-bold text-white">
                          {formatCurrency(payment.amount)}
                        </h3>
                        <p className="text-sm text-white">
                          {(payment.shares_to_purchase || 0).toLocaleString()} shares
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={`px-3 py-1 rounded-full text-sm font-black tracking-wide ${paymentKindBadgeClass(payment)}`}>
                        {paymentKindLabel(payment)}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(payment.status)}`}>
                        {payment.status.toUpperCase()}
                      </span>
                      {/* Balance Payment Badge - identify linked balance payments */}
                      {(() => {
                        try {
                          const notes = typeof payment.transaction_notes === 'string'
                            ? JSON.parse(payment.transaction_notes)
                            : (payment.transaction_notes || {})
                          if (notes.is_balance_payment) {
                            return (
                              <span className="px-3 py-1 rounded-full text-sm font-medium bg-amber-500/20 text-amber-400 border border-amber-500/50">
                                🔗 Balance Payment
                              </span>
                            )
                          }
                          return null
                        } catch (e) {
                          return null
                        }
                      })()}
                      <span className="text-sm text-white">
                        {getTimeAgo(payment.created_at)}
                      </span>
                    </div>
                  </div>
                </div>

                {(isCommissionConversion(payment) || isCommissionTopup(payment)) && (
                  <div className={`px-6 py-4 border-b ${isCommissionConversion(payment) ? 'bg-amber-900/30 border-amber-500/40' : 'bg-sky-900/30 border-sky-500/40'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${isCommissionConversion(payment) ? 'bg-amber-500 text-black' : 'bg-sky-500 text-black'}`}>
                        {isCommissionConversion(payment) ? 'COMMISSION CONVERSION' : 'COMMISSION TOP-UP'}
                      </span>
                      <span className="text-sm text-white">
                        {isCommissionConversion(payment)
                          ? 'Shares stay pending until the reserved commission is verified. This is not a crypto purchase.'
                          : 'Commission is credited only after the hash, sending wallet, amount, and network match.'}
                      </span>
                    </div>
                    {isCommissionConversion(payment) && (
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                        <div><span className="text-gray-400">Requested</span><div className="text-white font-semibold">${Number(payment.commission_review?.requestedUsdt ?? payment.amount).toFixed(2)}</div></div>
                        <div><span className="text-gray-400">Reserved commission</span><div className="text-white font-semibold">${Number(payment.commission_review?.reservedUsdt || 0).toFixed(2)}</div></div>
                        <div><span className="text-gray-400">Still available</span><div className="text-white font-semibold">${Number(payment.commission_review?.availableUsdt || 0).toFixed(2)}</div></div>
                        <div>
                          <span className="text-gray-400">Enough to convert</span>
                          <div className={payment.commission_review?.enough ? 'text-green-300 font-semibold' : 'text-red-300 font-semibold'}>
                            {payment.commission_review?.enough ? 'Yes' : 'No'}
                          </div>
                        </div>
                        {payment.commission_review?.balanceBefore != null && (
                          <div><span className="text-gray-400">Commission before reserve</span><div className="text-white font-semibold">${Number(payment.commission_review.balanceBefore).toFixed(2)}</div></div>
                        )}
                        {Array.isArray(payment.commission_review?.packages) && payment.commission_review.packages.length > 0 && (
                          <div className="md:col-span-3"><span className="text-gray-400">Packages</span><div className="text-white">{payment.commission_review.packages.map((line) => `${line.qty}× ${line.packageId}`).join(', ')}</div></div>
                        )}
                      </div>
                    )}
                    {isCommissionTopup(payment) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <div><span className="text-gray-400">Network</span><div className="text-white">{payment.network}</div></div>
                        <div><span className="text-gray-400">Amount</span><div className="text-white">${Number(payment.amount).toFixed(2)}</div></div>
                        <div className="md:col-span-2"><span className="text-gray-400">Sending wallet</span><div className="text-white font-mono break-all">{payment.sender_wallet || '—'}</div></div>
                        <div className="md:col-span-2"><span className="text-gray-400">Hash</span><div className="text-white font-mono break-all">{payment.transaction_hash || '—'}</div></div>
                        <div className="md:col-span-2">
                          <span className="text-gray-400">Last check</span>
                          <div className={payment.topup_verification?.success ? 'text-green-300' : 'text-yellow-200'}>
                            {payment.topup_verification
                              ? (payment.topup_verification.success
                                ? `Passed. Sender ${payment.topup_verification.matches?.sender ? 'matches' : 'does not match'}, amount ${payment.topup_verification.matches?.amount ? 'matches' : 'does not match'}, receiver ${payment.topup_verification.matches?.receiver ? 'matches' : 'does not match'}.`
                                : (payment.topup_verification.error || 'Not passed'))
                              : 'Not verified yet'}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Search Match Indicators */}
                {debouncedSearchQuery && matchedFields.has(payment.id) && (
                  <div className="px-6 py-3 bg-blue-900/20 border-b border-blue-500/30">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-blue-300 font-medium">Matched:</span>
                      {matchedFields.get(payment.id)?.map((field, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-blue-500/20 border border-blue-500/40 rounded text-sm text-blue-300 font-medium"
                        >
                          {field}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Share Calculation Validation Warning */}
                {shareValidationResults.has(payment.id) && !shareValidationResults.get(payment.id)?.isValid && (
                  <div className="px-6 py-3 bg-red-900/30 border-b border-red-500/50">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">⚠️</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-bold text-red-300">SHARE CALCULATION MISMATCH</span>
                          <span className="px-2 py-0.5 bg-red-500/30 border border-red-500/50 rounded text-sm text-red-200 font-bold">
                            APPROVAL BLOCKED
                          </span>
                        </div>
                        <div className="text-sm text-red-200 space-y-1">
                          {(() => {
                            const validation = shareValidationResults.get(payment.id)!
                            const difference = validation.difference
                            const valueDiff = Math.abs(difference * validation.phasePrice)
                            return (
                              <>
                                <p>
                                  <strong>Phase:</strong> {validation.phaseName} (${validation.phasePrice.toFixed(2)}/share)
                                </p>
                                <p>
                                  <strong>Expected:</strong> {validation.expectedShares} shares
                                  <span className="text-white ml-1">
                                    (${payment.amount} ÷ ${validation.phasePrice} = {validation.expectedShares})
                                  </span>
                                </p>
                                <p>
                                  <strong>Recorded:</strong> {validation.recordedShares} shares
                                </p>
                                <p className="font-bold">
                                  <strong>Difference:</strong> {difference > 0 ? '+' : ''}{difference} shares
                                  ({difference > 0 ? 'OVERPAYMENT' : 'UNDERPAYMENT'} of ${valueDiff.toFixed(2)})
                                </p>
                                <div className="mt-4 flex items-center gap-3">
                                  <button
                                    onClick={() => fixTransactionShares(payment)}
                                    disabled={processingPayment === payment.id}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                                  >
                                    {processingPayment === payment.id ? (
                                      <span className="flex items-center gap-2">
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        Fixing...
                                      </span>
                                    ) : (
                                      '🔧 Fix Transaction'
                                    )}
                                  </button>
                                  <p className="text-yellow-300 text-sm">
                                    Click to automatically correct the share count to {validation.expectedShares} shares
                                  </p>
                                </div>
                              </>
                            )
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Main content */}
                <div className="p-6">
                  {/* Balance Payment Info Banner */}
                  {(() => {
                    try {
                      const notes = typeof payment.transaction_notes === 'string'
                        ? JSON.parse(payment.transaction_notes)
                        : (payment.transaction_notes || {})
                      if (notes.is_balance_payment && notes.original_payment_id) {
                        return (
                          <div className="mb-4 p-4 bg-amber-900/20 border border-amber-500/50 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="text-2xl">🔗</span>
                                <div>
                                  <h4 className="text-amber-400 font-semibold">Balance Payment</h4>
                                  <p className="text-sm text-gray-300">
                                    This is a balance payment linked to an underpaid original payment.
                                  </p>
                                </div>
                              </div>
                              <div className="text-right text-sm">
                                <p className="text-white">Original Payment ID:</p>
                                <p className="text-amber-400 font-mono text-sm">{notes.original_payment_id}</p>
                              </div>
                            </div>
                          </div>
                        )
                      }
                      return null
                    } catch (e) {
                      return null
                    }
                  })()}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* User Information */}
                    <div className="space-y-3">
                      <h4 className="text-lg font-semibold text-white mb-3">👤 User Information</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-white">Name:</span>
                          <button
                            onClick={() => openUserProfile(payment.user_id)}
                            className="text-white font-medium hover:text-yellow-400 underline transition-colors cursor-pointer"
                            title="Click to view user profile"
                          >
                            {payment.users?.full_name || payment.users?.username || 'Unknown User'}
                          </button>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white">Email:</span>
                          <span className="text-white font-mono text-sm">
                            {payment.users?.email || 'No email'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white">User ID:</span>
                          <span className="text-white font-mono">
                            {payment.user_id}
                          </span>
                        </div>

                        {/* Telegram Username */}
                        {(() => {
                          const tg = payment.users?.telegram_users
                          const telegramUser = Array.isArray(tg) ? tg[0] : tg
                          return telegramUser?.username ? (
                            <div className="flex justify-between">
                              <span className="text-white">Telegram:</span>
                              <a
                                href={`https://t.me/${telegramUser.username}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-400 hover:text-blue-300 font-mono text-sm underline transition-colors"
                                title="Contact user on Telegram"
                              >
                                @{telegramUser.username}
                              </a>
                            </div>
                          ) : null
                        })()}

                        {/* Phone Number with WhatsApp */}
                        {payment.users?.phone_number && (
                          <div className="flex justify-between items-center">
                            <span className="text-white">Phone:</span>
                            <div className="flex items-center gap-2">
                              <a
                                href={`tel:${payment.users.phone_number}`}
                                className="text-green-400 hover:text-green-300 font-mono text-sm underline transition-colors"
                                title="Call user"
                              >
                                {payment.users.phone_number}
                              </a>
                              <button
                                onClick={() => openWhatsApp(payment.users?.phone_number || '')}
                                className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm transition-colors"
                                title="Contact via WhatsApp"
                              >
                                💬 WhatsApp
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Payment Details */}
                    <div className="space-y-3">
                      <h4 className="text-lg font-semibold text-white mb-3">💳 Payment Details</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-white">Network:</span>
                          <span className="text-white font-medium">
                            {payment.network || 'Unknown'} ({payment.currency || 'Unknown'})
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-white">Payment ID:</span>
                          <div className="flex items-center space-x-2">
                            <span className="text-white font-mono text-sm break-all max-w-xs">
                              {payment.id}
                            </span>
                            <button
                              onClick={() => navigator.clipboard.writeText(payment.id)}
                              className="p-1 text-gray-400 hover:text-white transition-colors"
                              title="Copy Payment ID"
                            >
                              📋
                            </button>
                          </div>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white">Created:</span>
                          <span className="text-white">
                            {formatDate(payment.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Commission Breakdown (if applicable) */}
                  {(() => {
                    if (!payment.transaction_notes) return null;
                    try {
                      const notes = typeof payment.transaction_notes === 'string'
                        ? JSON.parse(payment.transaction_notes)
                        : payment.transaction_notes;

                      if (notes.commission_used && notes.total_purchase_amount) {
                        const commissionUsed = parseFloat(notes.commission_used);
                        const totalPurchaseAmount = parseFloat(notes.total_purchase_amount);
                        const remainingPayment = parseFloat(notes.remaining_payment || payment.amount);

                        return (
                          <div className="mt-6 p-4 bg-green-900/20 rounded-lg border border-green-600">
                            <h4 className="text-lg font-semibold text-green-400 mb-3">💰 Commission Balance Used</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-white">Total Purchase Amount:</span>
                                <span className="text-white font-semibold">${totalPurchaseAmount.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-green-400">Commission Applied:</span>
                                <span className="text-green-400 font-semibold">-${commissionUsed.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between pt-2 border-t border-green-600/30">
                                <span className="text-white">Topup Payment (Recorded):</span>
                                <span className="text-yellow-400 font-bold">${remainingPayment.toFixed(2)}</span>
                              </div>
                              <div className="mt-3 p-2 bg-blue-900/20 rounded border border-blue-600/30">
                                <p className="text-blue-300 text-sm">
                                  ℹ️ <strong>Note:</strong> Upline commission calculated on full ${totalPurchaseAmount.toFixed(2)} purchase amount
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      }
                    } catch (e) {
                      return null;
                    }
                    return null;
                  })()}

                  {/* Underpayment Info Display */}
                  {(() => {
                    if (payment.status !== 'underpaid' || !payment.transaction_notes) return null;
                    try {
                      const notes = typeof payment.transaction_notes === 'string'
                        ? JSON.parse(payment.transaction_notes)
                        : payment.transaction_notes;

                      if (notes.underpayment) {
                        const { original_amount_received, balance_owed, total_expected, marked_at, reason, marked_by_admin_email } = notes.underpayment;
                        // v7.3.398: surface USDT commission availability for shortfall coverage decisions.
                        // Always render the line (default $0.00 when no commission_balances row exists)
                        // so admins always see whether the new shortfall-coverage path is usable.
                        const balanceOwedNum = parseFloat(balance_owed) || 0;
                        const hasCommissionRow = !!(payment.user_id && commissionBalances[payment.user_id] !== undefined);
                        const userCommission = hasCommissionRow ? commissionBalances[payment.user_id] : 0;
                        const canCoverWithCommission = userCommission >= balanceOwedNum && balanceOwedNum > 0;

                        return (
                          <div className="mt-6 p-4 bg-amber-900/20 rounded-lg border border-amber-600">
                            <h4 className="text-lg font-semibold text-amber-400 mb-3">⚠️ Underpayment Details</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-white">Expected Amount:</span>
                                <span className="text-white font-semibold">${parseFloat(total_expected).toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-white">Amount Received:</span>
                                <span className="text-amber-400 font-semibold">${parseFloat(original_amount_received).toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between pt-2 border-t border-amber-600/30">
                                <span className="text-amber-300">Balance Owed:</span>
                                <span className="text-amber-400 font-bold text-lg">${balanceOwedNum.toFixed(2)}</span>
                              </div>
                              {/* v7.3.398: USDT commission availability for shortfall coverage — always shown */}
                              <div className="flex justify-between pt-2 border-t border-amber-600/30">
                                <span className={canCoverWithCommission ? 'text-green-300' : 'text-red-300'}>
                                  User USDT Commission Available:
                                </span>
                                <span className={canCoverWithCommission ? 'text-green-400 font-bold' : 'text-red-400 font-bold'}>
                                  ${userCommission.toFixed(2)}
                                  {canCoverWithCommission ? ' ✅ (sufficient)' : (hasCommissionRow ? ' ❌ (insufficient)' : ' ❌ (no balance)')}
                                </span>
                              </div>
                              <div className="mt-3 pt-2 border-t border-amber-600/30">
                                <p className="text-white text-sm mb-1">Reason:</p>
                                <p className="text-white text-sm">{reason}</p>
                              </div>
                              <div className="mt-2 text-sm text-gray-500">
                                Marked by {marked_by_admin_email} on {new Date(marked_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        );
                      }
                    } catch (e) {
                      return null;
                    }
                    return null;
                  })()}

                  {/* Additional Hash & Proof Upload Section */}
                  {(payment.status === 'underpaid' || payment.status === 'pending' || payment.status === 'approved') && !isCommissionConversion(payment) && !isCommissionTopup(payment) && (
                    <div className="mt-6 p-4 bg-blue-900/20 rounded-lg border border-blue-600">
                      <h4 className="text-lg font-semibold text-blue-400 mb-4">📎 Additional Hash & Proof Upload</h4>

                      {/* Display existing additional hashes */}
                      {(() => {
                        try {
                          const notes = typeof payment.transaction_notes === 'string'
                            ? JSON.parse(payment.transaction_notes)
                            : (payment.transaction_notes || {})
                          const additionalHashes = notes.additional_hashes || []

                          if (additionalHashes.length > 0) {
                            return (
                              <div className="mb-4">
                                <p className="text-sm text-white mb-2">Existing Additional Hashes:</p>
                                <div className="space-y-2">
                                  {additionalHashes.map((item: any, idx: number) => (
                                    <div key={idx} className="bg-gray-800 p-2 rounded border border-gray-600 text-sm">
                                      <div className="flex items-center justify-between">
                                        <span className="text-green-400 font-mono break-all">{item.hash}</span>
                                        <button
                                          onClick={() => navigator.clipboard.writeText(item.hash)}
                                          className="ml-2 text-blue-400 hover:text-blue-300"
                                          title="Copy hash"
                                        >
                                          📋
                                        </button>
                                      </div>
                                      <p className="text-gray-500 mt-1">
                                        Added by {item.added_by} on {new Date(item.added_at).toLocaleString()}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )
                          }
                          return null
                        } catch (e) {
                          return null
                        }
                      })()}

                      {/* Add new transaction hash */}
                      <div className="mb-4">
                        <label className="block text-white text-sm mb-2">Add Additional Transaction Hash:</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={additionalHash}
                            onChange={(e) => setAdditionalHash(e.target.value)}
                            placeholder="Enter transaction hash..."
                            className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 text-sm font-mono"
                          />
                          <button
                            onClick={() => handleSaveAdditionalHash(payment.id, additionalHash)}
                            disabled={savingAdditionalHash || !additionalHash.trim()}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 text-sm font-medium"
                          >
                            {savingAdditionalHash ? (
                              <span className="flex items-center gap-1">
                                <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                Saving...
                              </span>
                            ) : (
                              '💾 Save Hash'
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Display existing additional proofs */}
                      {(() => {
                        try {
                          const notes = typeof payment.transaction_notes === 'string'
                            ? JSON.parse(payment.transaction_notes)
                            : (payment.transaction_notes || {})
                          const additionalProofs = notes.additional_proofs || []

                          if (additionalProofs.length > 0) {
                            return (
                              <div className="mb-4">
                                <p className="text-sm text-white mb-2">Existing Additional Proofs:</p>
                                <div className="space-y-2">
                                  {additionalProofs.map((item: any, idx: number) => (
                                    <div key={idx} className="bg-gray-800 p-2 rounded border border-gray-600 text-sm flex items-center justify-between">
                                      <div>
                                        <button
                                          type="button"
                                          onClick={() => openProofHref(item.url)}
                                          className="text-blue-400 hover:text-blue-300 underline text-left"
                                        >
                                          📄 {item.filename}
                                        </button>
                                        <p className="text-gray-500 mt-1">
                                          Uploaded by {item.uploaded_by} on {new Date(item.uploaded_at).toLocaleString()}
                                        </p>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => openProofHref(item.url)}
                                        className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm"
                                      >
                                        View
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )
                          }
                          return null
                        } catch (e) {
                          return null
                        }
                      })()}

                      {/* Upload additional proof */}
                      <div>
                        <label className="block text-white text-sm mb-2">Upload Additional Proof of Payment:</label>
                        <div className="flex gap-2">
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => setAdditionalProofFile(e.target.files?.[0] || null)}
                            className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-blue-600 file:text-white hover:file:bg-blue-700"
                          />
                          <button
                            onClick={() => additionalProofFile && handleUploadAdditionalProof(payment.id, additionalProofFile)}
                            disabled={uploadingAdditionalProof || !additionalProofFile}
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 text-sm font-medium"
                          >
                            {uploadingAdditionalProof ? (
                              <span className="flex items-center gap-1">
                                <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                Uploading...
                              </span>
                            ) : (
                              '📤 Upload'
                            )}
                          </button>
                        </div>
                        {additionalProofFile && (
                          <p className="text-green-400 text-sm mt-1">✅ Selected: {additionalProofFile.name}</p>
                        )}
                        <p className="text-gray-500 text-sm mt-1">Max size: 5MB. Accepted: Images, PDF</p>
                      </div>
                    </div>
                  )}

                  {/* Wallet Information */}
                  <div className="mt-6 p-4 bg-gray-900 rounded-lg border border-gray-600">
                    <h4 className="text-lg font-semibold text-white mb-3">🔗 Wallet & Transaction Details</h4>
                    <div className="space-y-3 text-sm">
                      <div>
                        <span className="text-white block mb-1">
                          {isSignableProofRef(payment.sender_wallet)
                            ? 'Payment proof (not a wallet):'
                            : 'Sender Wallet:'}
                        </span>
                        <div className="bg-gray-800 p-2 rounded border border-gray-600">
                          {(() => {
                            const senderWallet = payment.sender_wallet || 'Not provided';
                            const isProof = isSignableProofRef(senderWallet);
                            const isUrl = senderWallet.startsWith('http://') || senderWallet.startsWith('https://');

                            if (isProof) {
                              return (
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => openProofHref(senderWallet)}
                                    className="text-blue-400 hover:text-blue-300 text-sm underline flex-1 text-left"
                                    title="Open signed payment proof"
                                  >
                                    Open payment proof
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => navigator.clipboard.writeText(senderWallet)}
                                    className="text-blue-400 hover:text-blue-300 text-sm flex-shrink-0"
                                    title="Copy stored reference"
                                  >
                                    📋
                                  </button>
                                </div>
                              );
                            }

                            if (isUrl) {
                              return (
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => openProofHref(senderWallet)}
                                    className="text-blue-400 hover:text-blue-300 font-mono text-sm break-all underline flex-1 text-left"
                                    title="Open link"
                                  >
                                    {senderWallet}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => navigator.clipboard.writeText(senderWallet)}
                                    className="text-blue-400 hover:text-blue-300 text-sm flex-shrink-0"
                                    title="Copy to clipboard"
                                  >
                                    📋
                                  </button>
                                </div>
                              );
                            }

                            return (
                              <>
                                <span className="text-green-400 font-mono text-sm break-all">
                                  {senderWallet}
                                </span>
                                {payment.sender_wallet && (
                                  <button
                                    type="button"
                                    onClick={() => navigator.clipboard.writeText(payment.sender_wallet)}
                                    className="ml-2 text-blue-400 hover:text-blue-300 text-sm"
                                    title="Copy to clipboard"
                                  >
                                    📋
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>

                      <div>
                        <span className="text-white block mb-1">Receiver Wallet:</span>
                        <div className="bg-gray-800 p-2 rounded border border-gray-600">
                          <span className="text-blue-400 font-mono text-sm break-all">
                            {payment.receiver_wallet || 'Not provided'}
                          </span>
                          {payment.receiver_wallet && (
                            <button
                              onClick={() => navigator.clipboard.writeText(payment.receiver_wallet)}
                              className="ml-2 text-blue-400 hover:text-blue-300 text-sm"
                              title="Copy to clipboard"
                            >
                              📋
                            </button>
                          )}
                        </div>
                      </div>

                      {payment.transaction_hash && payment.network !== 'BANK_TRANSFER' ? (
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-white">Transaction Hash:</span>
                            <button
                              onClick={() => {
                                setEditingHashPaymentId(editingHashPaymentId === payment.id ? null : payment.id)
                                setEditHashValue(payment.transaction_hash || '')
                              }}
                              className="text-xs px-2 py-0.5 bg-orange-600 hover:bg-orange-700 text-white rounded"
                              title="Edit primary hash"
                            >
                              ✏️ Edit
                            </button>
                          </div>
                          <div className="bg-gray-800 p-2 rounded border border-gray-600">
                            <span className="text-yellow-400 font-mono text-sm break-all">
                              {payment.transaction_hash}
                            </span>
                            <button
                              onClick={() => navigator.clipboard.writeText(payment.transaction_hash)}
                              className="ml-2 text-blue-400 hover:text-blue-300 text-sm"
                              title="Copy to clipboard"
                            >
                              📋
                            </button>
                          </div>
                          {editingHashPaymentId === payment.id && (
                            <div className="mt-2 p-2 bg-gray-700 rounded border border-orange-500">
                              <label className="text-xs text-orange-300 block mb-1">New Transaction Hash:</label>
                              <input
                                type="text"
                                value={editHashValue}
                                onChange={(e) => setEditHashValue(e.target.value)}
                                className="w-full p-1.5 bg-gray-800 text-white border border-gray-600 rounded text-sm font-mono"
                                placeholder="Paste new hash..."
                              />
                              <div className="flex gap-2 mt-2">
                                <button
                                  onClick={() => handleEditTransactionHash(payment.id, editHashValue)}
                                  disabled={savingEditHash || !editHashValue.trim()}
                                  className="flex-1 px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm disabled:opacity-50"
                                >
                                  {savingEditHash ? 'Saving...' : '✅ Save'}
                                </button>
                                <button
                                  onClick={() => { setEditingHashPaymentId(null); setEditHashValue('') }}
                                  className="px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white rounded text-sm"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : payment.network === 'BANK_TRANSFER' ? (
                        <div>
                          <span className="text-white block mb-1">🏦 Bank Transfer Reference:</span>
                          <div className="bg-gray-800 p-2 rounded border border-green-600">
                            {(() => {
                              const bankRef = getBankTransferReference(payment)
                              const isDuplicate = isDuplicateRef(payment)
                              const isFlagged = duplicateFlags.has(payment.id)

                              if (bankRef) {
                                return (
                                  <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                      <span className="text-green-400 font-mono text-sm font-bold break-all">
                                        {bankRef}
                                      </span>
                                      <button
                                        onClick={() => navigator.clipboard.writeText(bankRef)}
                                        className="text-blue-400 hover:text-blue-300 text-sm"
                                        title="Copy reference to clipboard"
                                      >
                                        📋
                                      </button>
                                    </div>

                                    {/* Duplicate Warning */}
                                    {isDuplicate && (
                                      <div className="bg-red-900/30 border border-red-500 rounded p-2">
                                        <div className="flex items-center gap-2">
                                          <span className="text-red-400 text-sm font-bold">
                                            ⚠️ DUPLICATE REFERENCE DETECTED
                                          </span>
                                          <span className="text-red-300 text-sm">
                                            ({duplicateRefs.get(bankRef)} payments with this ref)
                                          </span>
                                        </div>
                                      </div>
                                    )}

                                    {/* Flag Button */}
                                    <button
                                      onClick={() => toggleDuplicateFlag(payment.id)}
                                      className={`w-full px-3 py-1 rounded text-sm font-medium transition-colors ${
                                        isFlagged
                                          ? 'bg-red-600 hover:bg-red-700 text-white'
                                          : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                                      }`}
                                    >
                                      {isFlagged ? '🚩 Flagged as Duplicate' : '🏴 Flag as Duplicate'}
                                    </button>
                                  </div>
                                )
                              } else {
                                return (
                                  <span className="text-orange-400 text-sm">⚠️ No reference number found</span>
                                )
                              }
                            })()}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-2">
                          <span className="text-red-400 text-sm">⚠️ No transaction hash provided</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Rejection Reason */}
                  {payment.rejection_reason && (
                    <div className="mt-4 p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
                      <h4 className="text-red-300 font-semibold mb-2">❌ Rejection Reason:</h4>
                      <p className="text-red-300 text-sm">{payment.rejection_reason}</p>
                    </div>
                  )}

                  {/* Reversal Reason */}
                  {payment.reversal_reason && (
                    <div className="mt-4 p-4 bg-orange-900/20 border border-orange-500/30 rounded-lg">
                      <h4 className="text-orange-300 font-semibold mb-2">🔄 Reversal Reason:</h4>
                      <p className="text-orange-300 text-sm">{payment.reversal_reason}</p>
                      {payment.reversed_at && (
                        <p className="text-orange-400 text-sm mt-2">
                          Reversed on: {new Date(payment.reversed_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Transaction Notes */}
                  <div className="mt-4 p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                    <h4 className="text-blue-300 font-semibold mb-2">📝 Transaction Notes</h4>
                    <textarea
                      value={transactionNotes.get(payment.id) ?? payment.admin_notes ?? ''}
                      onChange={(e) => {
                        const newNotes = new Map(transactionNotes)
                        newNotes.set(payment.id, e.target.value)
                        setTransactionNotes(newNotes)
                      }}
                      placeholder="Add notes about this transaction..."
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded text-white text-sm resize-y min-h-[80px]"
                      rows={3}
                    />
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-white text-sm">
                        Notes are saved to the database and visible to all admins
                      </p>
                      <button
                        onClick={() => saveTransactionNotes(payment.id, transactionNotes.get(payment.id) ?? payment.admin_notes ?? '')}
                        disabled={savingNotes.has(payment.id)}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {savingNotes.has(payment.id) ? (
                          <span className="flex items-center gap-2">
                            <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            Saving...
                          </span>
                        ) : (
                          '💾 Save Notes'
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-6 flex flex-wrap gap-3">
                    {payment.status === 'pending' && isCommissionConversion(payment) && (
                      <>
                        <button
                          onClick={() => verifyCommissionConversionCard(payment)}
                          disabled={processingPayment === payment.id}
                          className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                        >
                          {processingPayment === payment.id ? 'Checking commission...' : 'Verify commission'}
                        </button>
                        <button
                          onClick={() => approveCommissionConversionCard(payment)}
                          disabled={processingPayment === payment.id || !conversionsReadyToApprove.has(payment.id)}
                          className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                        >
                          Approve shares
                        </button>
                        <button
                          onClick={() => rejectCommissionConversionCard(payment)}
                          disabled={processingPayment === payment.id}
                          className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                        >
                          Reject and return commission
                        </button>
                      </>
                    )}
                    {payment.status === 'pending' && isCommissionTopup(payment) && (
                      <>
                        <button
                          onClick={() => verifyCommissionTopupCard(payment)}
                          disabled={processingPayment === payment.id}
                          className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                        >
                          Verify hash
                        </button>
                        <button
                          onClick={() => approveCommissionTopupCard(payment)}
                          disabled={processingPayment === payment.id || payment.topup_verification?.success !== true}
                          className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                        >
                          Approve top-up
                        </button>
                        <button
                          onClick={() => rejectCommissionTopupCard(payment)}
                          disabled={processingPayment === payment.id}
                          className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                        >
                          Reject top-up
                        </button>
                      </>
                    )}
                    {payment.status === 'pending' && !isCommissionConversion(payment) && !isCommissionTopup(payment) && (
                      <>
                        <button
	                          onClick={() => handleManualOverrideClick(payment)}
	                          disabled={processingPayment === payment.id || remitanoVerifying}
                          className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                        >
                          {processingPayment === payment.id ? (
                            <span className="flex items-center gap-2">
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                              Approving...
                            </span>
                          ) : (
                            '✅ Approve Payment'
                          )}
                        </button>
                        <button
                          onClick={() => handleRejectClick(payment)}
                          disabled={processingPayment === payment.id}
                          className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                        >
                          ❌ Reject Payment
                        </button>
                        {/* Remitano Verification Button */}
                        <button
                          onClick={() => handleRemitanoVerifyClick(payment)}
                          disabled={processingPayment === payment.id}
                          className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                        >
                          🔗 Verify Blockchain TX
                        </button>
                        {/* Flag as Duplicate Button */}
                        <button
                          onClick={() => handleFlagDuplicateClick(payment)}
                          disabled={processingPayment === payment.id || processingFlagDuplicate}
                          className="px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                        >
                          🚩 Flag as Duplicate
                        </button>
                        {/* Mark as Underpaid Button */}
                        <button
                          onClick={() => handleMarkUnderpaidClick(payment)}
                          disabled={processingPayment === payment.id || processingUnderpaid}
                          className="px-6 py-3 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                        >
                          ⚠️ Mark as Underpaid
                        </button>
                        {/* Delete Button */}
                        <button
                          onClick={() => handleDeleteClick(payment)}
                          disabled={processingPayment === payment.id || processingDelete}
                          className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                        >
                          🗑️ Delete
                        </button>
                      </>
                    )}

                    {/* Actions for Flagged Duplicate Payments */}
                    {payment.status === 'flagged_duplicate' && (
                      <>
                        {/* Mark as Underpaid Button */}
                        <button
                          onClick={() => handleMarkUnderpaidClick(payment)}
                          disabled={processingPayment === payment.id || processingUnderpaid}
                          className="px-6 py-3 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                        >
                          ⚠️ Mark as Underpaid
                        </button>
                        {/* Approve Anyway Button */}
                        <button
	                          onClick={() => handleManualOverrideClick(payment)}
	                          disabled={processingPayment === payment.id || remitanoVerifying}
                          className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                        >
                          ✅ Approve Anyway
                        </button>
                        {/* Reject Button */}
                        <button
                          onClick={() => handleRejectClick(payment)}
                          disabled={processingPayment === payment.id}
                          className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                        >
                          ❌ Reject
                        </button>
                      </>
                    )}

                    {/* Actions for Underpaid Payments */}
                    {payment.status === 'underpaid' && (() => {
                      // v7.3.398: compute commission-coverage feasibility for this row.
                      // Always render either the active green button (sufficient) OR a disabled
                      // red button (insufficient / no balance) so the feature is always visible.
                      const balanceOwedNum = (() => {
                        try {
                          const notes = typeof payment.transaction_notes === 'string'
                            ? JSON.parse(payment.transaction_notes)
                            : (payment.transaction_notes || {})
                          return parseFloat(notes?.underpayment?.balance_owed) || 0
                        } catch { return 0 }
                      })()
                      const hasCommissionRow = !!(payment.user_id && commissionBalances[payment.user_id] !== undefined)
                      const userCommission = hasCommissionRow ? commissionBalances[payment.user_id] : 0
                      const canCoverWithCommission = balanceOwedNum > 0 && hasUsdCoverage(userCommission, balanceOwedNum)
                      const showDollarOverride = canUseManualOverride
                        && balanceOwedNum > 0
                        && balanceOwedNum <= MANUAL_UNDERPAY_TOLERANCE_USD
                      return (
                      <>
                        {/* v7.3.398: Verify & Cover with Commission — green when sufficient, red-disabled when not */}
                        {balanceOwedNum > 0 && (
                          canCoverWithCommission ? (
                            <button
                              onClick={() => handleVerifyAndCoverWithCommission(payment)}
                              disabled={processingPayment === payment.id || processingUnderpaid || remitanoVerifying || !payment.transaction_hash}
                              className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                              title={`Deduct $${balanceOwedNum.toFixed(2)} from user's USDT commission balance ($${userCommission.toFixed(2)} available) and approve payment for full $${payment.amount.toFixed(2)}`}
                            >
                              💰 Verify & Cover with Commission (${balanceOwedNum.toFixed(2)})
                            </button>
                          ) : (
                            <button
                              disabled
                              className="px-6 py-3 bg-red-600/40 text-white rounded-lg font-medium cursor-not-allowed border border-red-600/50"
                              title={`Cannot cover with commission — user has $${userCommission.toFixed(2)}, needs $${balanceOwedNum.toFixed(2)}`}
                            >
                              💰 Cover with Commission — ${userCommission.toFixed(2)} / ${balanceOwedNum.toFixed(2)} (insufficient)
                            </button>
                          )
                        )}
                        {showDollarOverride && (
                          <button
                            onClick={() => handleApproveWithManualUnderpayTolerance(payment)}
                            disabled={processingPayment === payment.id || processingUnderpaid || remitanoVerifying || !payment.transaction_hash}
                            className="px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                            title="Johan/JP only. Approve this verified shortfall if it is $1.00 or less."
                          >
                            Approve with $1 shortfall override
                          </button>
                        )}
                        {/* View underpayment info - already visible in details above */}
                        <button
                          onClick={async () => {
                            // Approve original payment with partial amount
                            // This would require the balance payment to be submitted separately
                            setMessage({ type: 'info', text: 'Waiting for user to submit balance payment. User has been notified.' })
                          }}
                          disabled={true}
                          className="px-6 py-3 bg-amber-600/50 text-white rounded-lg transition-colors disabled:opacity-50 font-medium cursor-not-allowed"
                        >
                          ⏳ Awaiting Balance Payment
                        </button>
	                        <button
	                          onClick={() => handleManualOverrideClick(payment)}
		                          disabled={processingPayment === payment.id || remitanoVerifying || !canUseManualOverride}
	                          className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
		                          title={canUseManualOverride ? 'Owner manual override after Johan/JP has verified external payment evidence' : 'Manual override is restricted to Johan/JP'}
	                        >
	                          ✅ Manual Override Approve
	                        </button>
                        {/* Re-mark as Underpaid (edit amounts) */}
                        <button
                          onClick={() => handleMarkUnderpaidClick(payment)}
                          disabled={processingPayment === payment.id || processingUnderpaid}
                          className="px-6 py-3 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                        >
                          ✏️ Edit Underpayment
                        </button>
                        {/* Reject underpayment (cancel the payment) */}
                        <button
                          onClick={() => handleRejectClick(payment)}
                          disabled={processingPayment === payment.id}
                          className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                        >
                          ❌ Reject & Cancel
                        </button>
                      </>
                      )
                    })()}

                    {/* Reverse Payment Button for Approved Payments (owner-only: Johan/JP) */}
                    {payment.status === 'approved' && canUseManualOverride && (
                      <button
                        onClick={() => handleReverseClick(payment)}
                        disabled={processingReversal}
                        className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                      >
                        {processingReversal ? (
                          <span className="flex items-center gap-2">
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            Reversing...
                          </span>
                        ) : (
                          '🔄 Reverse Payment'
                        )}
                      </button>
                    )}

                    {/* v4.8.84 — Cancel Duplicate Button for Approved Payments (owner-only: Johan/JP) */}
                    {payment.status === 'approved' && canUseManualOverride && (
                      <button
                        onClick={() => handleCancelDuplicateRow(payment)}
                        disabled={cancellingApprovedRowId === payment.id}
                        title="Reverse this approved payment as an accidental duplicate (atomic; audit-logged)"
                        className="px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                      >
                        {cancellingApprovedRowId === payment.id ? (
                          <span className="flex items-center gap-2">
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            Cancelling…
                          </span>
                        ) : (
                          '🧬 Cancel Duplicate'
                        )}
                      </button>
                    )}

                    {payment.screenshot_url ? (
                      <button
                        onClick={() => handleViewScreenshot(payment)}
                        className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
                      >
                        {payment.network === 'BANK_TRANSFER' ? '📄 View Payment Proof' : '📷 View Screenshot'}
                      </button>
                    ) : payment.network?.includes('USDT') || payment.network?.includes('BSC') ? null : (
                      <div className="px-6 py-3 bg-orange-600 text-white rounded-lg font-medium">
                        ⚠️ No screenshot provided
                      </div>
                    )}
                  </div>
                </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Pagination Controls */}
      {activeTab !== 'hash-removal' && activeTab !== 'duplicates' && !loading && payments.length > 0 && (
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Page Info */}
            <div className="text-sm text-white">
              Page {currentPage} of {Math.ceil(totalCount / pageSize)}
            </div>

            {/* Page Navigation */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                Previous
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, Math.ceil(totalCount / pageSize)) }, (_, i) => {
                  const totalPages = Math.ceil(totalCount / pageSize)
                  let pageNum

                  if (totalPages <= 5) {
                    pageNum = i + 1
                  } else if (currentPage <= 3) {
                    pageNum = i + 1
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i
                  } else {
                    pageNum = currentPage - 2 + i
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 rounded text-sm ${
                        currentPage === pageNum
                          ? 'bg-yellow-500 text-black font-bold'
                          : 'bg-gray-700 text-white hover:bg-gray-600'
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalCount / pageSize), prev + 1))}
                disabled={currentPage >= Math.ceil(totalCount / pageSize)}
                className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(Math.ceil(totalCount / pageSize))}
                disabled={currentPage >= Math.ceil(totalCount / pageSize)}
                className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                Last
              </button>
            </div>

            {/* Total Count */}
            <div className="text-sm text-white">
              Total: {totalCount} payments
            </div>
          </div>
        </div>
      )}

	      {/* Owner Manual Approval Override Modal */}
	      {showManualOverrideModal && manualOverridePayment && (
	        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
	          <div className="bg-gray-800 rounded-lg border border-green-700 p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
	            <h3 className="text-xl font-bold text-green-400 mb-4">✅ Manual Payment Verification Override</h3>

	            <div className="mb-4 p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg space-y-2">
	              <p className="text-yellow-200 text-sm">
	                Use only after Johan/JP has verified external evidence such as bank statements, blockchain transaction, proof, references, user identity, and amount.
	              </p>
	              <p className="text-gray-300"><strong>Payment:</strong> {formatCurrency(manualOverridePayment.amount || 0)}</p>
	              <p className="text-gray-300"><strong>Status:</strong> {manualOverridePayment.status}</p>
	              <p className="text-gray-300"><strong>User:</strong> {manualOverridePayment.users?.full_name || manualOverridePayment.users?.username || 'Unknown User'}</p>
	              <p className="text-gray-300 break-all"><strong>Reference:</strong> {manualOverridePayment.transaction_hash || 'N/A'}</p>
	            </div>

	            <div className="mb-4">
	              <label className="block text-sm font-medium text-gray-300 mb-2">
		                Confirm Payment Paid <span className="text-red-400">*</span>
	              </label>
	              <input
	                type="text"
	                value={manualOverrideConfirmation}
	                onChange={(e) => setManualOverrideConfirmation(e.target.value)}
	                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent"
		                placeholder="Type PAID to confirm"
	              />
		              <p className="text-xs text-gray-300 mt-2">
		                Type PAID to confirm Johan/JP has manually verified this payment.
		              </p>
	              <p className="text-xs text-gray-400 mt-2">
	                This creates shares but bypassed automated evidence gates suppress commission cascade processing.
	              </p>
	            </div>

	            <div className="flex space-x-3">
	              <button
	                onClick={handleManualOverrideSubmit}
	                disabled={
	                  processingPayment === manualOverridePayment.id ||
		                  !canUseManualOverride ||
		                  !isValidManualOverridePaidValue(manualOverrideConfirmation)
	                }
	                className="flex-1 py-2 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
	              >
	                {processingPayment === manualOverridePayment.id ? 'Approving...' : 'Confirm Manual Override Approval'}
	              </button>
	              <button
	                onClick={closeManualOverrideModal}
	                disabled={processingPayment === manualOverridePayment.id}
	                className="flex-1 py-2 px-4 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors disabled:opacity-50"
	              >
	                Cancel
	              </button>
	            </div>
	          </div>
	        </div>
	      )}

	      {/* Rejection Modal */}
      {showRejectionModal && selectedPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">Reject Payment</h3>
            
            <div className="mb-4">
              <p className="text-gray-300 mb-2">
                <strong>Payment:</strong> {formatCurrency(selectedPayment.amount || 0)}
              </p>
              <p className="text-gray-300 mb-4">
                <strong>User:</strong> {selectedPayment.users?.full_name || selectedPayment.users?.username || 'Unknown User'}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Rejection Reason *
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a detailed reason for rejecting this payment..."
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                rows={4}
              />
              <p className="text-sm text-white mt-1">
                This message will be sent to the user. Minimum 5 characters.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleRejectSubmit}
                disabled={processingPayment === selectedPayment.id || !rejectionReason.trim()}
                className="flex-1 py-2 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {processingPayment === selectedPayment.id ? 'Processing...' : 'Confirm Rejection'}
              </button>
              <button
                onClick={() => {
                  setShowRejectionModal(false)
                  setSelectedPayment(null)
                  setRejectionReason('')
                }}
                disabled={processingPayment === selectedPayment.id}
                className="flex-1 py-2 px-4 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Payment Modal */}
      {showDeleteModal && selectedPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-red-700 p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-red-400 mb-4">🗑️ Delete Payment</h3>

            <div className="mb-4 p-4 bg-red-900/20 border border-red-700/50 rounded-lg">
              <p className="text-red-300 text-sm mb-2">
                ⚠️ <strong>Warning:</strong> This action will permanently delete this payment record.
              </p>
              <p className="text-gray-300 mb-2">
                <strong>Payment:</strong> {formatCurrency(selectedPayment.amount || 0)}
              </p>
              <p className="text-gray-300 mb-2">
                <strong>User:</strong> {selectedPayment.users?.full_name || selectedPayment.users?.username || 'Unknown User'}
              </p>
              <p className="text-gray-300">
                <strong>Reference:</strong> {selectedPayment.transaction_hash || 'N/A'}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Deletion Reason *
              </label>
              <textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="Please provide a reason for deleting this payment (e.g., duplicate entry, test transaction)..."
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                rows={3}
              />
            </div>

            <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deleteConfirmed}
                  onChange={(e) => setDeleteConfirmed(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-600 text-red-600 focus:ring-red-500"
                />
                <span className="text-gray-300 text-sm">
                  I confirm that I want to permanently delete this payment
                </span>
              </label>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => deletePayment(selectedPayment, deleteReason)}
                disabled={processingDelete || !deleteReason.trim() || !deleteConfirmed}
                className="flex-1 py-2 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {processingDelete ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Deleting...
                  </span>
                ) : (
                  '🗑️ Delete Payment'
                )}
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setSelectedPayment(null)
                  setDeleteReason('')
                  setDeleteConfirmed(false)
                }}
                disabled={processingDelete}
                className="flex-1 py-2 px-4 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Flag as Duplicate Modal */}
      {showFlagDuplicateModal && selectedPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-orange-700 p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-orange-400 mb-4">🚩 Flag as Duplicate</h3>

            <div className="mb-4 p-4 bg-orange-900/20 border border-orange-700/50 rounded-lg">
              <p className="text-orange-300 text-sm mb-2">
                This will flag the payment as a potential duplicate and notify the user.
              </p>
              <p className="text-gray-300 mb-2">
                <strong>Payment:</strong> {formatCurrency(selectedPayment.amount || 0)}
              </p>
              <p className="text-gray-300 mb-2">
                <strong>User:</strong> {selectedPayment.users?.full_name || selectedPayment.users?.username || 'Unknown User'}
              </p>
              <p className="text-gray-300">
                <strong>Reference:</strong> {selectedPayment.transaction_hash || 'N/A'}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Flag Reason *
              </label>
              <textarea
                value={flagDuplicateReason}
                onChange={(e) => setFlagDuplicateReason(e.target.value)}
                placeholder="Please explain why this payment is being flagged (e.g., same amount submitted twice, duplicate reference number)..."
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                rows={3}
              />
              <p className="text-sm text-white mt-1">
                This reason will be sent to the user via email.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => flagPaymentAsDuplicate(selectedPayment, flagDuplicateReason)}
                disabled={processingFlagDuplicate || !flagDuplicateReason.trim()}
                className="flex-1 py-2 px-4 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {processingFlagDuplicate ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Flagging...
                  </span>
                ) : (
                  '🚩 Flag & Notify User'
                )}
              </button>
              <button
                onClick={() => {
                  setShowFlagDuplicateModal(false)
                  setSelectedPayment(null)
                  setFlagDuplicateReason('')
                }}
                disabled={processingFlagDuplicate}
                className="flex-1 py-2 px-4 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark as Underpaid Modal */}
      {showUnderpaidModal && underpaidPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-yellow-700 p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-yellow-400 mb-4">⚠️ Mark as Underpaid</h3>

            <div className="mb-4 p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
              <p className="text-yellow-300 text-sm mb-2">
                This will mark the payment as underpaid and notify the user to submit the balance.
              </p>
              <p className="text-gray-300 mb-2">
                <strong>Expected Amount:</strong> ${underpaidPayment.amount.toFixed(2)}
              </p>
              <p className="text-gray-300 mb-2">
                <strong>User:</strong> {underpaidPayment.users?.full_name || underpaidPayment.users?.username || 'Unknown User'}
              </p>
              <p className="text-gray-300">
                <strong>Reference:</strong> {underpaidPayment.transaction_hash || 'N/A'}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Amount Received *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={underpaidPayment.amount - 0.01}
                  value={underpaidForm.originalAmountReceived}
                  onChange={(e) => handleUnderpaidFormChange('originalAmountReceived', e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-7 pr-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>
              <p className="text-sm text-white mt-1">
                Enter the actual amount received (must be less than ${underpaidPayment.amount.toFixed(2)})
              </p>
            </div>

            <div className="mb-4 p-3 bg-yellow-900/30 border border-yellow-600/50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-yellow-300 font-medium">Balance Owed:</span>
                <span className="text-yellow-400 text-xl font-bold">
                  ${underpaidForm.balanceOwed || '0.00'}
                </span>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Reason for Underpayment *
              </label>
              <textarea
                value={underpaidForm.reason}
                onChange={(e) => handleUnderpaidFormChange('reason', e.target.value)}
                placeholder="Please explain why this is an underpayment (e.g., user sent wrong amount, partial payment received)..."
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none"
                rows={3}
              />
              <p className="text-sm text-white mt-1">
                This reason will be sent to the user via email.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={markPaymentAsUnderpaid}
                disabled={processingUnderpaid || !underpaidForm.originalAmountReceived || !underpaidForm.reason.trim()}
                className="flex-1 py-2 px-4 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {processingUnderpaid ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Processing...
                  </span>
                ) : (
                  '⚠️ Mark as Underpaid & Notify'
                )}
              </button>
              <button
                onClick={() => {
                  setShowUnderpaidModal(false)
                  setUnderpaidPayment(null)
                  setUnderpaidForm({ originalAmountReceived: '', balanceOwed: '', reason: '' })
                }}
                disabled={processingUnderpaid}
                className="flex-1 py-2 px-4 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {conversionCheck && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-amber-400 p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-1">Commission conversion check</h3>
            <p className={`text-2xl font-black mt-3 ${
              conversionCheck.review?.verified ? 'text-green-300' : 'text-red-300'
            }`}>
              {conversionCheck.review?.verified ? 'ACCURATE' : 'NOT ACCURATE'}
            </p>
            <p className="mt-2 text-white">
              {conversionCheck.error
                ? conversionCheck.error
                : conversionCheck.review?.verified
                  ? `$${Number(conversionCheck.review.reservedUsdt || 0).toFixed(2)} of commission is reserved for this $${Number(conversionCheck.review.requestedUsdt || 0).toFixed(2)} conversion. Approve shares is now available.`
                  : 'This conversion cannot be approved until every check below passes.'}
            </p>
            {conversionCheck.review && (
              <div className="mt-4 space-y-2 text-sm">
                <p className="text-gray-200"><strong>Requested:</strong> ${Number(conversionCheck.review.requestedUsdt || 0).toFixed(2)}</p>
                <p className="text-gray-200"><strong>Reserved:</strong> ${Number(conversionCheck.review.reservedUsdt || 0).toFixed(2)}</p>
                <p className="text-gray-200"><strong>Still available:</strong> ${Number(conversionCheck.review.availableUsdt || 0).toFixed(2)}</p>
                <p className="text-gray-200"><strong>Shares:</strong> {Number(conversionCheck.review.shares || 0)} at ${Number(conversionCheck.review.sharePrice || 0).toFixed(2)}</p>
                {Array.isArray(conversionCheck.review.packages) && conversionCheck.review.packages.length > 0 && (
                  <p className="text-gray-200"><strong>Packages:</strong> {conversionCheck.review.packages.map((line) => `${line.qty}× ${line.packageId}`).join(', ')}</p>
                )}
                <div className="pt-2 space-y-1">
                  {(conversionCheck.review.checks || []).map((check) => (
                    <p key={check.name} className={check.ok ? 'text-green-300' : 'text-red-300'}>
                      {check.ok ? '✓' : '✗'} {check.detail}
                    </p>
                  ))}
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={() => setConversionCheck(null)}
              className="mt-5 w-full py-2 px-4 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Remitano/Blockchain Verification Modal */}
      {showRemitanoModal && remitanoPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-1">🔗 Verify Blockchain Transaction</h3>
            <p className="text-sm text-purple-300 mb-4">Exodus / BSC wallet attestation — BNB Smart Chain USDT only</p>

            <div className="mb-4 p-3 bg-gray-700/50 rounded-lg space-y-2">
              <p className="text-gray-300">
                <strong>Expected Amount:</strong> {formatCurrency(remitanoPayment.amount || 0)}
              </p>
              <p className="text-gray-300">
                <strong>User:</strong> {remitanoPayment.users?.full_name || remitanoPayment.users?.username || 'Unknown User'}
              </p>
              {/* v7.3.398: always show the user's USDT commission balance so admins can */}
              {/* gauge shortfall-coverage feasibility before clicking verify. Defaults to */}
              {/* $0.00 when no commission_balances row or map entry exists. */}
              {(() => {
                const hasCommissionRow = !!(remitanoPayment.user_id && commissionBalances[remitanoPayment.user_id] !== undefined)
                const userCommission = hasCommissionRow ? commissionBalances[remitanoPayment.user_id] : 0
                const expected = parseFloat((remitanoPayment.amount as any) || 0) || 0
                const sufficient = userCommission >= expected && expected > 0
                return (
                  <p className="text-gray-300">
                    <strong>User USDT Commission Available:</strong>{' '}
                    <span className={sufficient ? 'text-green-400 font-semibold' : 'text-amber-300 font-semibold'}>
                      ${userCommission.toFixed(2)}
                    </span>
                    {!hasCommissionRow && (
                      <span className="text-gray-400 text-sm ml-1">(no balance record)</span>
                    )}
                  </p>
                )
              })()}
              <p className="text-gray-300">
                <strong>Network:</strong> {remitanoPayment.network}
              </p>
              <p className="text-gray-300">
                <strong>Sender Wallet:</strong>{' '}
                <span className="font-mono text-sm break-all">{remitanoPayment.sender_wallet || 'N/A'}</span>
              </p>
              <p className="text-gray-300">
                <strong>Receiver Wallet:</strong>{' '}
                <span className="font-mono text-sm break-all">{remitanoPayment.receiver_wallet || 'N/A'}</span>
              </p>
              <p className="text-gray-300">
                <strong>Transaction Hash:</strong>{' '}
                <span className="font-mono text-sm break-all text-purple-400">{remitanoPayment.transaction_hash || 'N/A'}</span>
              </p>

              {/* Revolut Batch Transaction Indicator */}
              {remitanoPayment.sender_wallet &&
               remitanoPayment.sender_wallet.toLowerCase() === '0xb23360CCDd9Ed1b15D45E5d3824Bb409C8D7c460'.toLowerCase() && (
                <div className="mt-2 p-2 bg-purple-900/30 border border-purple-500/50 rounded">
                  <div className="flex items-center gap-2 text-purple-400 text-sm">
                    <span>🔄</span>
                    <span className="font-semibold">Revolut Batch Transaction Detected</span>
                  </div>
                  <div className="text-purple-300 text-sm mt-1">
                    This transaction may contain multiple payments. Verification will find the specific transfer to our wallet.
                  </div>
                </div>
              )}

              {/* ALWAYS show explorer link based on network and hash */}
              {remitanoPayment.transaction_hash && (
                <div className="pt-2 border-t border-gray-600">
                  <a
                    href={(() => {
                      const hash = remitanoPayment.transaction_hash
                      const network = remitanoPayment.network?.toUpperCase() || ''
                      if (network.includes('BSC') || network.includes('BEP20')) {
                        return `https://bscscan.com/tx/${hash}`
                      } else if (network.includes('TRC') || network.includes('TRON')) {
                        return `https://tronscan.org/#/transaction/${hash}`
                      } else if (network.includes('ERC') || network.includes('ETH')) {
                        return `https://etherscan.io/tx/${hash}`
                      } else if (network.includes('POL') || network.includes('POLYGON') || network.includes('MATIC')) {
                        return `https://polygonscan.com/tx/${hash}`
                      }
                      // Default to BSC for USDT
                      return `https://bscscan.com/tx/${hash}`
                    })()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    🔍 View Transaction on Block Explorer →
                  </a>
                </div>
              )}

            </div>

            {!remitanoPayment.transaction_hash && (
              <div className="mb-4 p-3 bg-red-900/30 border border-red-600 rounded-lg">
                <p className="text-red-400 text-sm">
                  ⚠️ No transaction hash found in payment record. Cannot verify on blockchain.
                </p>
              </div>
            )}

            {/* Verification Result Display */}
            {remitanoResult && (
              <div className={`mb-4 p-4 rounded-lg border ${
                remitanoResult.autoApproved ? 'bg-green-900/30 border-green-600' :
                remitanoResult.autoRejected ? 'bg-red-900/30 border-red-600' :
                remitanoResult.verified ? 'bg-blue-900/30 border-blue-600' :
                'bg-red-900/30 border-red-600'
              }`}>
                {remitanoResult.report && (
                  <div className={`mb-4 p-4 rounded-lg border-2 ${
                    remitanoResult.report.verdict === 'ACCURATE'
                      ? 'bg-green-950/60 border-green-400'
                      : remitanoResult.report.verdict === 'CHAIN_VERIFIED'
                        ? 'bg-amber-950/60 border-amber-400'
                        : 'bg-red-950/70 border-red-500'
                  }`}>
                    <p className={`text-2xl font-black ${
                      remitanoResult.report.verdict === 'ACCURATE'
                        ? 'text-green-300'
                        : remitanoResult.report.verdict === 'CHAIN_VERIFIED'
                          ? 'text-amber-200'
                          : 'text-red-300'
                    }`}>
                      {remitanoResult.report.verdict === 'ACCURATE'
                        ? 'ACCURATE'
                        : remitanoResult.report.verdict === 'CHAIN_VERIFIED'
                          ? 'CHAIN VERIFIED'
                          : 'NOT ACCURATE'}
                    </p>
                    <p className="mt-2 text-white font-medium">{remitanoResult.report.summary}</p>
                    <div className="mt-3 space-y-1 text-sm">
                      {remitanoResult.report.lines.map((row) => (
                        <p key={row.label} className="text-gray-100">
                          <span className={row.ok === true ? 'text-green-300' : row.ok === false ? 'text-red-300' : 'text-gray-400'}>
                            {row.ok === true ? '✓' : row.ok === false ? '✗' : '•'}
                          </span>
                          {' '}
                          <strong>{row.label}:</strong> {row.value}
                        </p>
                      ))}
                    </div>
                    {remitanoResult.report.transfers.length > 1 && (
                      <div className="mt-3 pt-3 border-t border-white/10 space-y-1">
                        <p className="text-sm font-bold text-white">USDT transfers in this hash</p>
                        {remitanoResult.report.transfers.map((transfer, index) => (
                          <p key={`${transfer.to}-${index}`} className={`text-sm font-mono break-all ${transfer.selected ? 'text-green-200' : 'text-gray-300'}`}>
                            {transfer.selected ? 'Selected' : 'Other'} ${Number(transfer.amount).toFixed(2)} → {transfer.toLabel} {transfer.to}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {/* Validation Errors Display - Improved Format */}
                {remitanoResult.validationErrors && remitanoResult.validationErrors.length > 0 ? (
                  <div className="space-y-3">
                    <p className="font-medium text-red-400 text-lg">❌ Blockchain Validation Failed</p>

                    {remitanoResult.validationErrors.map((err, idx) => (
                      <div key={idx} className="bg-gray-900/50 p-3 rounded border border-red-700/50">
                        <p className="text-red-400 font-medium mb-1">
                          {err.label} is invalid:
                        </p>
                        <p className="text-gray-300 text-sm font-mono break-all">
                          Found: <span className="text-yellow-400">{err.found}</span>
                        </p>
                        <p className="text-gray-300 text-sm font-mono break-all">
                          Expected: <span className="text-green-400">{err.expected}</span>
                          {err.note && <span className="text-gray-500 ml-2">({err.note})</span>}
                        </p>
                      </div>
                    ))}

                    {/* Transaction Details */}
                    {remitanoResult.verification && (
                      <div className="mt-3 pt-3 border-t border-gray-700 text-sm text-gray-300 space-y-1">
                        <p><strong>Transaction Hash:</strong> <span className="font-mono text-sm text-purple-400 break-all">{remitanoResult.verification.txHash || remitanoPayment?.transaction_hash}</span></p>
                        <p><strong>Network:</strong> {remitanoResult.verification.network || remitanoPayment?.network}</p>
                        <p><strong>Blockchain Amount:</strong> ${remitanoResult.verification.blockchainAmount?.toFixed(2) || remitanoResult.verification.amount?.toFixed(2) || 'N/A'}</p>
                        <p><strong>Expected Amount:</strong> ${remitanoResult.verification.expectedAmount?.toFixed(2) || remitanoPayment?.amount?.toFixed(2) || 'N/A'}</p>
                        {remitanoResult.verification.confirmations !== undefined && (
                          <p><strong>Confirmations:</strong> {remitanoResult.verification.confirmations}</p>
                        )}
                        {remitanoResult.verification.explorerUrl && (
                          <p>
                            <a
                              href={remitanoResult.verification.explorerUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-purple-400 hover:text-purple-300 underline"
                            >
                              View on Block Explorer →
                            </a>
                          </p>
                        )}
                      </div>
                    )}

                    {/* Reject Transaction Button */}
                    <div className="mt-4 pt-3 border-t border-gray-700">
                      <button
                        onClick={openRemitanoRejectionModal}
                        className="w-full py-2 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium"
                      >
                        ❌ Reject Transaction
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {remitanoResult.report ? null : remitanoResult.errorCode === DUPLICATE_HASH_CODE ? (
                      <div className="p-4 bg-red-950 border-2 border-red-500 rounded-lg">
                        <p className="text-2xl font-black text-red-300">DUPLICATE HASH</p>
                        <p className="mt-3 text-white whitespace-pre-wrap font-medium">{remitanoResult.error || remitanoResult.message}</p>
                      </div>
                    ) : (
                    <p className={`font-medium mb-2 ${
                      remitanoResult.autoApproved ? 'text-green-400' :
                      remitanoResult.autoRejected ? 'text-red-400' :
                      remitanoResult.verified ? 'text-blue-400' :
                      'text-yellow-400'
                    }`}>
                      {remitanoResult.message}
                    </p>
                    )}

                    {/* Show hint if provided */}
                    {remitanoResult.hint && (
                      <p className="text-white text-sm mb-2 italic">{remitanoResult.hint}</p>
                    )}

                    {remitanoResult.verification && (
                      <div className="text-sm text-gray-300 space-y-1">
                        <p><strong>Status:</strong> {remitanoResult.verification.status}</p>

                        {/* Show transfer type if it's a native coin */}
                        {remitanoResult.verification.transferType && remitanoResult.verification.transferType !== 'USDT' && (
                          <p className="text-cyan-400 font-medium">
                            <strong>Payment Type:</strong> {remitanoResult.verification.transferType} (Native Coin)
                            {remitanoResult.verification.nativeAmount && (
                              <span className="text-white ml-2">
                                ({remitanoResult.verification.nativeAmount.toFixed(6)} {remitanoResult.verification.nativeCoin} @ ${remitanoResult.verification.nativePrice?.toFixed(2)}/coin)
                              </span>
                            )}
                          </p>
                        )}

                        <p><strong>Blockchain Amount:</strong> ${remitanoResult.verification.amount?.toFixed(2) || remitanoResult.verification.blockchainAmount?.toFixed(2) || 'N/A'}</p>
                        <p><strong>Expected Amount:</strong> ${remitanoResult.verification.expectedAmount?.toFixed(2) || remitanoPayment?.amount?.toFixed(2) || 'N/A'}</p>
                        {remitanoResult.verification.confirmations !== undefined && (
                          <p><strong>Confirmations:</strong> {remitanoResult.verification.confirmations}</p>
                        )}

                        {/* Batch Transaction Info */}
                        {remitanoResult.verification.batchInfo && remitanoResult.verification.batchInfo.isBatch && (
                          <div className="mt-3 p-3 bg-purple-900/30 border border-purple-500/50 rounded">
                            <p className="text-purple-400 font-semibold mb-2">
                              🔄 {remitanoResult.verification.batchInfo.isRevolutBatch ? 'Revolut ' : ''}Batch Transaction
                            </p>
                            <p className="text-purple-300 text-sm mb-2">
                              This transaction contains {remitanoResult.verification.batchInfo.totalTransfers} separate USDT transfers.
                              {remitanoResult.verification.batchInfo.isRevolutBatch && ' Revolut often batches multiple customer payments into one transaction.'}
                            </p>
                            <div className="text-sm text-white space-y-1 max-h-40 overflow-y-auto">
                              {remitanoResult.verification.batchInfo.allTransfers?.map((t: any, i: number) => (
                                <div key={i} className={`p-1.5 rounded ${t.isCompanyTransfer ? 'bg-green-900/30 border border-green-600/50' : 'bg-gray-800/50'}`}>
                                  <span className="font-mono">
                                    #{t.index}: ${t.amount.toFixed(2)} → {t.to.slice(0, 6)}...{t.to.slice(-4)}
                                    {t.isCompanyTransfer && <span className="ml-2 text-green-400 font-semibold">← OUR WALLET</span>}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* ALWAYS show explorer link prominently for errors */}
                        {remitanoResult.verification.explorerUrl && (
                          <div className="mt-3 pt-2 border-t border-gray-600">
                            <a
                              href={remitanoResult.verification.explorerUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors"
                            >
                              🔍 View Transaction on Block Explorer →
                            </a>
                          </div>
                        )}
                        {remitanoResult.amountDifference && (
                          <p className={
                            remitanoResult.amountStatus === 'mismatch' || remitanoResult.amountStatus === 'underpaid' ? 'text-red-400 font-medium' :
                            remitanoResult.amountStatus === 'warning' ? 'text-yellow-400' :
                            'text-green-400'
                          }>
                            <strong>Amount Difference:</strong> ${remitanoResult.amountDifference}
                            {remitanoResult.amountStatus === 'exact' && ' ✅ (within $1 tolerance)'}
                            {remitanoResult.amountStatus === 'warning' && ' ⚠️ (between $1-$5 - allowed)'}
                            {remitanoResult.amountStatus === 'mismatch' && ' ❌ (exceeds $5 - manual review required)'}
                            {remitanoResult.amountStatus === 'underpaid' && ' ❌ (underpaid)'}
                          </p>
                        )}
                        {/* v7.3.392: Shortfall coverage panel — shown for underpayments only */}
                        {remitanoResult.shortfall && remitanoResult.availableCommission !== undefined && (() => {
                          const shortfallNum = parseFloat(remitanoResult.shortfall)
                          const commNum = parseFloat(remitanoResult.availableCommission)
                          const canCover = hasUsdCoverage(commNum, shortfallNum) && shortfallNum > 0
                          const covered = remitanoResult.shortfallCoveredByCommission === true
                          return (
                            <div className={`mt-2 p-3 rounded border ${
                              covered ? 'bg-green-900/30 border-green-600/50 text-green-200' :
                              canCover ? 'bg-blue-900/30 border-blue-600/50 text-blue-200' :
                              'bg-red-900/30 border-red-600/50 text-red-200'
                            }`}>
                              <p className="font-medium mb-1">
                                {covered ? '✅ Shortfall Covered by Commission'
                                  : canCover ? '💡 Shortfall Can Be Covered by Commission'
                                  : '⚠️ Shortfall Cannot Be Covered'}
                              </p>
                              <p className="text-sm"><strong>Shortfall:</strong> ${remitanoResult.shortfall}</p>
                              <p className="text-sm"><strong>User USDT Commission Available:</strong> ${remitanoResult.availableCommission}</p>
                              {covered && (
                                <p className="text-sm mt-1">A shortfall of ${remitanoResult.shortfall} was automatically deducted from the user's USDT commission balance to cover the underpayment.</p>
                              )}
                              {!covered && canCover && (
                                <p className="text-sm mt-1">User has enough USDT commission to cover the shortfall. Verify &amp; Approve will deduct it and credit any overpay remainder to commissions.</p>
                              )}
                              {!covered && !canCover && (
                                <p className="text-sm mt-1">
                                  User does not have enough USDT commission to cover the shortfall.
                                  {canUseManualOverride && shortfallNum <= MANUAL_UNDERPAY_TOLERANCE_USD
                                    ? ' Johan can approve with the $1 shortfall override, or the client can pay the balance.'
                                    : ' Client must pay the balance and provide a second transaction hash, or admin must mark as underpaid.'}
                                </p>
                              )}
                              {!covered && canCover && remitanoPayment && (
                                <button
                                  type="button"
                                  onClick={() => handleVerifyAndCoverWithCommission(remitanoPayment)}
                                  disabled={remitanoVerifying || processingPayment === remitanoPayment.id}
                                  className="mt-3 w-full py-2 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                                >
                                  💰 Cover shortfall with user commission (${shortfallNum.toFixed(2)})
                                </button>
                              )}
                            </div>
                          )
                        })()}
                        {/* Sender wallet correction notification */}
                        {remitanoResult.verification?.senderWalletCorrected && (
                          <div className="mt-2 p-2 bg-amber-900/30 border border-amber-600/50 rounded text-amber-300 text-sm">
                            <p className="font-medium">⚠️ Sender Wallet Auto-Corrected</p>
                            <p>User mistakenly entered company wallet as sender. Actual sender from blockchain: <span className="font-mono">{remitanoResult.verification.senderWallet}</span></p>
                          </div>
                        )}
                      </div>
                    )}

                    {remitanoResult.approval && (
                      <div className="mt-3 pt-3 border-t border-gray-600 text-sm text-gray-300 space-y-1">
                        <p className="font-medium text-green-400">✅ Auto-Approved!</p>
                        <p><strong>Shares:</strong> {remitanoResult.approval.baseShares} + {remitanoResult.approval.bonusShares} bonus = {remitanoResult.approval.totalShares} total</p>
                        <p><strong>Package:</strong> {remitanoResult.approval.packageName}</p>
                        {remitanoResult.approval.commission && (
                          <p><strong>Commission:</strong> ${remitanoResult.approval.commission.usdt?.toFixed(2)} USDT + {remitanoResult.approval.commission.shares?.toFixed(2)} shares</p>
                        )}
                      </div>
                    )}

                    {remitanoResult.error && !remitanoResult.report && remitanoResult.errorCode !== DUPLICATE_HASH_CODE && (
                      <div className={remitanoResult.errorCode === NATIVE_BNB_NOT_USDT_CODE
                        ? 'mt-2 p-3 bg-red-950/50 border border-red-600/70 rounded text-red-200 text-sm'
                        : undefined}
                      >
                        <p className={remitanoResult.errorCode === NATIVE_BNB_NOT_USDT_CODE
                          ? 'font-medium text-red-300 mb-1'
                          : 'text-red-400 text-sm mt-2'}
                        >
                          {remitanoResult.errorCode === NATIVE_BNB_NOT_USDT_CODE
                            ? 'Native BNB — not USDT. Do not approve.'
                            : `Error: ${remitanoResult.error}`}
                        </p>
                        {remitanoResult.errorCode === NATIVE_BNB_NOT_USDT_CODE && (
                          <p>{remitanoResult.error}</p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => handleRemitanoVerify(false)}
                disabled={remitanoVerifying || !remitanoPayment.transaction_hash}
                className="flex-1 py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {remitanoVerifying ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Verifying...
                  </span>
                ) : (
                  '🔍 Verify Only'
                )}
              </button>
              {isExodusBscNetwork(remitanoPayment.network) && !isVerifyAndApproveBlocked(remitanoResult, remitanoPayment) && (
                <button
                  onClick={() => handleRemitanoVerify(true)}
                  disabled={remitanoVerifying || !remitanoPayment.transaction_hash}
                  className="flex-1 py-2 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                >
                  {remitanoVerifying ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Verifying &amp; Approving...
                    </span>
                  ) : (
                    '✅ Verify & Approve'
                  )}
                </button>
              )}
              {(() => {
                const shortfallNum = parseFloat(String(remitanoResult?.shortfall || 0))
                const commNum = parseFloat(String(remitanoResult?.availableCommission || 0))
                const canCover = remitanoResult?.canCoverWithCommission === true
                  || (shortfallNum > 0 && hasUsdCoverage(commNum, shortfallNum))
                const showDollarOverride = canUseManualOverride
                  && shortfallNum > 0
                  && shortfallNum <= MANUAL_UNDERPAY_TOLERANCE_USD
                if (!canCover && !showDollarOverride) return null
                return (
                  <>
                    {canCover && (
                      <button
                        onClick={() => handleVerifyAndCoverWithCommission(remitanoPayment)}
                        disabled={remitanoVerifying || processingPayment === remitanoPayment.id || !remitanoPayment.transaction_hash}
                        className="flex-1 py-2 px-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                      >
                        💰 Cover with user commission
                      </button>
                    )}
                    {showDollarOverride && (
                      <button
                        onClick={() => handleApproveWithManualUnderpayTolerance(remitanoPayment)}
                        disabled={remitanoVerifying || processingPayment === remitanoPayment.id || !remitanoPayment.transaction_hash}
                        className="flex-1 py-2 px-4 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                      >
                        Approve with $1 shortfall override
                      </button>
                    )}
                  </>
                )
              })()}
              <button
                onClick={closeRemitanoModal}
                disabled={remitanoVerifying}
                className="w-full py-2 px-4 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remitano Rejection Modal */}
      {showRemitanoRejectionModal && remitanoPayment && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-gray-800 rounded-lg border border-red-700 p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-red-400 mb-4">❌ Reject Transaction</h3>

            {/* Payment Info */}
            <div className="mb-4 p-3 bg-gray-700/50 rounded-lg space-y-1 text-sm">
              <p className="text-gray-300">
                <strong>User:</strong> {remitanoPayment.users?.full_name || remitanoPayment.users?.username || 'Unknown User'}
              </p>
              <p className="text-gray-300">
                <strong>Email:</strong> {remitanoPayment.users?.email || 'N/A'}
              </p>
              <p className="text-gray-300">
                <strong>Amount:</strong> {formatCurrency(remitanoPayment.amount || 0)}
              </p>
              <p className="text-gray-300">
                <strong>Network:</strong> {remitanoPayment.network}
              </p>
            </div>

            {/* Email Preview */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                📧 Rejection Email Preview
              </label>
              <div className="bg-gray-900 border border-gray-600 rounded-lg p-4 text-sm">
                <p className="text-white mb-2">
                  <strong>To:</strong> {remitanoPayment.users?.email || 'N/A'}
                </p>
                <p className="text-white mb-3">
                  <strong>Subject:</strong> Payment Rejected - Action Required
                </p>
                <div className="border-t border-gray-700 pt-3">
                  <p className="text-gray-300 mb-2">Dear {remitanoPayment.users?.full_name || remitanoPayment.users?.username || 'User'},</p>
                  <p className="text-gray-300 mb-2">
                    Your payment of {formatCurrency(remitanoPayment.amount || 0)} on {remitanoPayment.network} has been rejected for the following reason:
                  </p>
                  <div className="bg-red-900/30 border border-red-700/50 rounded p-3 my-3">
                    <pre className="text-red-300 whitespace-pre-wrap text-sm font-mono">{remitanoRejectionReason}</pre>
                  </div>
                  <p className="text-gray-300">
                    Please submit a new payment with the correct details.
                  </p>
                </div>
              </div>
            </div>

            {/* Rejection Reason (editable) */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Rejection Reason (editable)
              </label>
              <textarea
                value={remitanoRejectionReason}
                onChange={(e) => setRemitanoRejectionReason(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none font-mono text-sm"
                rows={5}
              />
            </div>

            {/* Admin Notes */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Admin Notes (optional - will be appended to rejection reason)
              </label>
              <textarea
                value={remitanoRejectionNotes}
                onChange={(e) => setRemitanoRejectionNotes(e.target.value)}
                placeholder="Add any additional notes for the user..."
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                rows={3}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={handleRemitanoRejectionSubmit}
                disabled={processingRemitanoRejection || !remitanoRejectionReason.trim()}
                className="flex-1 py-2 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
              >
                {processingRemitanoRejection ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Rejecting...
                  </span>
                ) : (
                  '❌ Confirm Rejection & Send Email'
                )}
              </button>
              <button
                onClick={() => {
                  setShowRemitanoRejectionModal(false)
                  setRemitanoRejectionReason('')
                  setRemitanoRejectionNotes('')
                }}
                disabled={processingRemitanoRejection}
                className="flex-1 py-2 px-4 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Screenshot Modal */}
      {showScreenshotModal && currentScreenshot && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => {
                setShowScreenshotModal(false)
                setCurrentScreenshot(null)
                // Clean up blob URL if it was created
                if (currentScreenshot.startsWith('blob:')) {
                  URL.revokeObjectURL(currentScreenshot)
                }
              }}
              className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
            >
              ✕
            </button>
            <img
              src={currentScreenshot}
              alt="Payment Screenshot"
              className="max-w-full max-h-full object-contain rounded-lg"
              onError={() => {
                setMessage({ type: 'error', text: 'Failed to load screenshot image' })
                setShowScreenshotModal(false)
                setCurrentScreenshot(null)
              }}
            />
            <div className="absolute bottom-4 left-4 bg-black/50 text-white px-3 py-2 rounded-lg text-sm">
              Payment Proof Screenshot
            </div>
          </div>
        </div>
      )}

      {/* Payment Reversal Modal */}
      {showReversalModal && selectedPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">🔄 Reverse Payment</h3>

            {/* Warning */}
            <div className="mb-6 p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
              <h4 className="text-red-300 font-semibold mb-2">⚠️ Warning</h4>
              <p className="text-red-300 text-sm">
                This recalls the deposit from the current user (cancelling their shares and clawing
                back the commissions paid to their upline) and re-applies the funds to the correct
                user you select below, re-running the commission cascade for the correct upline.
                This action cannot be undone.
              </p>
            </div>

            {/* Payment Details Summary */}
            <div className="mb-6 p-4 bg-gray-900 rounded-lg border border-gray-600">
              <h4 className="text-white font-semibold mb-3">Payment Details</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-white">Payment ID:</span>
                  <span className="text-white font-mono text-sm">{selectedPayment.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white">Amount:</span>
                  <span className="text-white font-medium">{formatCurrency(selectedPayment.amount || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white">User:</span>
                  <span className="text-white">{selectedPayment.users?.full_name || selectedPayment.users?.username || 'Unknown User'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white">Shares to Remove:</span>
                  <span className="text-white">{(selectedPayment.shares_to_purchase || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white">Network:</span>
                  <span className="text-white">{selectedPayment.network} ({selectedPayment.currency})</span>
                </div>
              </div>
            </div>

            {/* Correct recipient (reassignment target) */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Correct Recipient — funds &amp; shares move here *
              </label>
              <input
                type="text"
                value={reassignSearch}
                onChange={(e) => setReassignSearch(e.target.value)}
                placeholder="Filter by User ID, Email, or Username..."
                className="w-full px-3 py-2 mb-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <select
                value={reassignUserId ?? ''}
                onChange={(e) => setReassignUserId(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="">{usersLoading ? 'Loading users...' : 'Select the correct user...'}</option>
                {allUsers
                  .filter((u) => {
                    const q = reassignSearch.trim().toLowerCase()
                    if (!q) return true
                    if (/^\d+$/.test(q)) return u.id.toString() === q
                    return (u.username || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q)
                  })
                  .slice(0, 50)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.username} ({u.email}) — ID {u.id}
                    </option>
                  ))}
              </select>
              {reassignUserId === selectedPayment.user_id && (
                <p className="text-sm text-red-400 mt-1">Correct recipient must differ from the current user.</p>
              )}
            </div>

            {/* Optional corrected amount */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Corrected Amount (optional)
              </label>
              <input
                type="number"
                min="0"
                step="any"
                value={reassignNewAmount}
                onChange={(e) => setReassignNewAmount(e.target.value)}
                placeholder={`Leave blank to keep ${formatCurrency(selectedPayment.amount || 0)}`}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <p className="text-sm text-white mt-1">
                Only set this if the correct deposit amount differs from the original.
              </p>
            </div>

            {/* Reversal Reason */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Reversal Reason *
              </label>
              <textarea
                value={reversalReason}
                onChange={(e) => setReversalReason(e.target.value)}
                placeholder="Please provide a detailed reason for reversing this payment..."
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                rows={4}
              />
              <div className="flex justify-between items-center mt-1">
                <p className="text-sm text-white">
                  This message will be sent to the user. Minimum 10 characters.
                </p>
                <span className={`text-sm ${reversalReason.length >= 10 ? 'text-green-400' : 'text-red-400'}`}>
                  {reversalReason.length}/10
                </span>
              </div>
            </div>

            {/* Confirmation Checkbox */}
            <div className="mb-4">
              <label className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={reversalConfirmation}
                  onChange={(e) => setReversalConfirmation(e.target.checked)}
                  className="mt-1 w-4 h-4 text-orange-600 bg-gray-700 border-gray-600 rounded focus:ring-orange-500 focus:ring-2"
                />
                <span className="text-sm text-gray-300">
                  I understand this action cannot be undone and will permanently remove shares and reverse commissions.
                </span>
              </label>
            </div>

            {/* Secondary Confirmation */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Type "REVERSE" to confirm *
              </label>
              <input
                type="text"
                value={reversalConfirmText}
                onChange={(e) => setReversalConfirmText(e.target.value)}
                placeholder="Type REVERSE here"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <p className="text-sm text-white mt-1">
                Case-sensitive — must be uppercase REVERSE. Final confirmation step to prevent accidental reversals.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={handleReversalSubmit}
                disabled={
                  processingReversal ||
                  !reassignUserId ||
                  reassignUserId === selectedPayment.user_id ||
                  !reversalReason.trim() ||
                  reversalReason.length < 10 ||
                  !reversalConfirmation ||
                  reversalConfirmText !== 'REVERSE'
                }
                className="flex-1 py-3 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
              >
                {processingReversal ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Reversing Payment...
                  </span>
                ) : (
                  '🔄 Confirm Reversal'
                )}
              </button>
              <button
                onClick={() => {
                  setShowReversalModal(false)
                  setSelectedPayment(null)
                  setReversalReason('')
                  setReversalConfirmation(false)
                  setReversalConfirmText('')
                  setReassignUserId(null)
                  setReassignSearch('')
                  setReassignNewAmount('')
                }}
                disabled={processingReversal}
                className="flex-1 py-3 px-4 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Payment Creation Modal */}
      {showManualPaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gray-900 px-6 py-4 border-b border-gray-700 sticky top-0 z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white">➕ Create Manual Payment</h2>
                  <p className="text-sm text-white mt-1">Step {manualPaymentStep} of 3</p>
                </div>
                <button
                  onClick={() => {
                    if (confirm('Are you sure you want to cancel? All entered data will be lost.')) {
                      resetManualPaymentForm()
                      setShowManualPaymentModal(false)
                    }
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {/* Step 1: User Selection */}
              {manualPaymentStep === 1 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white mb-4">Select User</h3>

                  {!manualPaymentForm.selectedUser ? (
                    <>
                      {/* Search Filter */}
                      <div>
                        <label className="block text-white text-sm mb-2">Search User</label>
                        <input
                          type="text"
                          value={userSearchQuery}
                          onChange={(e) => setUserSearchQuery(e.target.value)}
                          placeholder="Type to filter by User ID, Email, or Username..."
                          className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                        />
                        {usersLoading && (
                          <p className="text-sm text-white mt-2">🔄 Loading users...</p>
                        )}
                      </div>

                      {/* User Selection Dropdown */}
                      {!usersLoading && (
                        <div>
                          <label className="block text-white text-sm mb-2">
                            Select User <span className="text-red-400">*</span>
                          </label>
                          <select
                            value={''}
                            onChange={(e) => {
                              console.log('🔄 [MANUAL PAYMENT] User selected from dropdown:', e.target.value)
                              const userId = parseInt(e.target.value)
                              console.log('🔄 [MANUAL PAYMENT] Parsed user ID:', userId)
                              console.log('🔄 [MANUAL PAYMENT] Total users in allUsers:', allUsers.length)
                              const user = allUsers.find(u => u.id === userId)
                              console.log('🔄 [MANUAL PAYMENT] Found user:', user)
                              if (user) {
                                setManualPaymentForm({ ...manualPaymentForm, selectedUser: user })
                                console.log('✅ [MANUAL PAYMENT] User set in form state')
                              } else {
                                console.error('❌ [MANUAL PAYMENT] User not found in allUsers array')
                              }
                            }}
                            className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                            size={10}
                          >
                            <option value="">-- Select a user --</option>
                            {filteredUsers.map((user) => (
                              <option key={user.id} value={user.id}>
                                ID: {user.id} | {user.username} | {user.email} | {user.full_name || 'N/A'}
                              </option>
                            ))}
                          </select>
                          {filteredUsers.length === 0 && userSearchQuery && (
                            <p className="text-sm text-red-400 mt-2">No users found matching "{userSearchQuery}"</p>
                          )}
                          {filteredUsers.length > 0 && (
                            <p className="text-sm text-white mt-2">
                              Showing {filteredUsers.length} of {allUsers.length} users
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    /* Selected User Confirmation */
                    <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-green-400 font-semibold mb-2">✅ Selected User</p>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <p className="text-white">User ID:</p>
                              <p className="text-white font-medium">{manualPaymentForm.selectedUser.id}</p>
                            </div>
                            <div>
                              <p className="text-white">Username:</p>
                              <p className="text-white font-medium">{manualPaymentForm.selectedUser.username}</p>
                            </div>
                            <div>
                              <p className="text-white">Email:</p>
                              <p className="text-white font-medium">{manualPaymentForm.selectedUser.email}</p>
                            </div>
                            {manualPaymentForm.selectedUser.full_name && (
                              <div>
                                <p className="text-white">Full Name:</p>
                                <p className="text-white font-medium">{manualPaymentForm.selectedUser.full_name}</p>
                              </div>
                            )}
                            {manualPaymentForm.selectedUser.phone_number && (
                              <div>
                                <p className="text-white">Phone:</p>
                                <p className="text-white font-medium">{manualPaymentForm.selectedUser.phone_number}</p>
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => setManualPaymentForm({ ...manualPaymentForm, selectedUser: null })}
                          className="text-red-400 hover:text-red-300 ml-4"
                        >
                          Change
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Payment Details */}
              {manualPaymentStep === 2 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white mb-4">Payment Details</h3>

                  {/* Payment Type Selector */}
                  <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-6">
                    <label className="block text-gray-300 font-semibold mb-3">
                      Payment Type <span className="text-red-400">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => setManualPaymentForm({
                          ...manualPaymentForm,
                          paymentType: 'crypto',
                          currency: 'USDT'
                        })}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          manualPaymentForm.paymentType === 'crypto'
                            ? 'border-blue-500 bg-blue-900/40 text-white'
                            : 'border-gray-600 bg-gray-800 text-gray-400 hover:border-gray-500'
                        }`}
                      >
                        <div className="text-2xl mb-2">💰</div>
                        <div className="font-semibold">Crypto Payment</div>
                        <div className="text-sm mt-1">USDT (TRC20/ERC20/BEP20)</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          // Generate unique payment reference when switching to bank transfer
                          const timestamp = Date.now().toString().slice(-6)
                          const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0')
                          const uniqueRef = `REF-${timestamp}-${random}`

                          setManualPaymentForm({
                            ...manualPaymentForm,
                            paymentType: 'bank_transfer',
                            currency: 'USD',
                            paymentReference: uniqueRef
                          })
                        }}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          manualPaymentForm.paymentType === 'bank_transfer'
                            ? 'border-blue-500 bg-blue-900/40 text-white'
                            : 'border-gray-600 bg-gray-800 text-gray-400 hover:border-gray-500'
                        }`}
                      >
                        <div className="text-2xl mb-2">🏦</div>
                        <div className="font-semibold">Bank Transfer</div>
                        <div className="text-sm mt-1">EFT, Wire Transfer, etc.</div>
                      </button>
                    </div>
                  </div>

                  <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-4 mb-6">
                    <label className="block text-gray-300 font-semibold mb-2">
                      Package <span className="text-red-400">*</span>
                    </label>
                    <p className="text-gray-400 text-sm mb-3">
                      Choose the running package for this user. Bank and crypto both use this choice. The system will not switch to a denser or higher package.
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <button
                        type="button"
                        onClick={() => applyManualPackagePick('standard')}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          manualPaymentForm.packageKind === 'standard'
                            ? 'border-amber-500 bg-amber-900/40 text-white'
                            : 'border-gray-600 bg-gray-800 text-gray-300 hover:border-gray-500'
                        }`}
                      >
                        <div className="font-semibold">Standard</div>
                        <div className="text-xs mt-1 text-gray-400">Phase-price shares. No PCAP bonus.</div>
                      </button>
                      {listActiveProductionCapitalPackages().map((pkg) => (
                        <button
                          key={pkg.id}
                          type="button"
                          onClick={() => applyManualPackagePick('pcap', pkg.id, manualPaymentForm.campaignPackageQty || 1)}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            manualPaymentForm.packageKind === 'pcap' && manualPaymentForm.campaignPackage === pkg.id
                              ? 'border-amber-500 bg-amber-900/40 text-white'
                              : 'border-gray-600 bg-gray-800 text-gray-300 hover:border-gray-500'
                          }`}
                        >
                          <div className="font-semibold">{pkg.id} · {pkg.name}</div>
                          <div className="text-xs mt-1 text-gray-400">
                            ${pkg.priceUsd} · {pkg.paidShares} paid + {pkg.investorBonus} bonus
                          </div>
                        </button>
                      ))}
                    </div>
                    {manualPaymentForm.packageKind === 'pcap' && (
                      <div className="mt-4 max-w-xs">
                        <label className="block text-white text-sm mb-2">Package quantity</label>
                        <input
                          type="number"
                          min={1}
                          max={100}
                          step={1}
                          value={manualPaymentForm.campaignPackageQty}
                          onChange={(e) => applyManualPackagePick('pcap', manualPaymentForm.campaignPackage, parseInt(e.target.value, 10) || 1)}
                          className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-amber-500"
                        />
                      </div>
                    )}
                    {!manualPaymentForm.packageKind && (
                      <p className="text-amber-300 text-sm mt-3">Select Standard or a Production Capital package before continuing.</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Amount */}
                    <div>
                      <label className="block text-white text-sm mb-2">
                        Amount <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="1"
                        value={manualPaymentForm.amount}
                        onChange={(e) => setManualPaymentForm({ ...manualPaymentForm, amount: e.target.value })}
                        placeholder="100.00"
                        className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                      />
                      {manualPaymentForm.amount && parseFloat(manualPaymentForm.amount) < 1 && (
                        <p className="text-red-400 text-sm mt-1">Amount must be at least 1</p>
                      )}
                    </div>

                    {/* Currency */}
                    <div>
                      <label className="block text-white text-sm mb-2">
                        Currency <span className="text-red-400">*</span>
                      </label>
                      <select
                        value={manualPaymentForm.currency}
                        onChange={(e) => setManualPaymentForm({ ...manualPaymentForm, currency: e.target.value })}
                        className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                      >
                        {manualPaymentForm.paymentType === 'crypto' ? (
                          <option value="USDT">USDT</option>
                        ) : (
                          <>
                            <option value="USD">USD</option>
                            <option value="ZAR">ZAR</option>
                          </>
                        )}
                      </select>
                    </div>

                    {/* Share Phase */}
                    <div className="md:col-span-2">
                      <label className="block text-white text-sm mb-2">
                        Share Phase <span className="text-red-400">*</span>
                      </label>
                      <select
                        value={manualPaymentForm.phaseId}
                        onChange={(e) => setManualPaymentForm({ ...manualPaymentForm, phaseId: e.target.value })}
                        className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                      >
                        <option value="">Select Phase</option>
                        {investmentPhases.map((phase) => (
                          <option key={phase.id} value={phase.id}>
                            Phase {phase.phase_number} - ${phase.price_per_share.toFixed(2)}/share
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Conditional Fields Based on Payment Type */}
                  {manualPaymentForm.paymentType === 'crypto' ? (
                    <>
                      {/* Crypto Payment Fields */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Network */}
                        <div>
                          <label className="block text-white text-sm mb-2">
                            Network <span className="text-red-400">*</span>
                          </label>
                          <select
                            value={manualPaymentForm.network}
                            onChange={(e) => setManualPaymentForm({ ...manualPaymentForm, network: e.target.value })}
                            className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                          >
                            <option value="TRC20">TRC20 (Tron)</option>
                            <option value="ERC20">ERC20 (Ethereum)</option>
                            <option value="BEP20">BEP20 (BSC)</option>
                          </select>
                        </div>

                        {/* Transaction Hash */}
                        <div>
                          <label className="block text-white text-sm mb-2">
                            Transaction Hash <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="text"
                            value={manualPaymentForm.transactionHash}
                            onChange={(e) => setManualPaymentForm({ ...manualPaymentForm, transactionHash: e.target.value })}
                            placeholder="0x..."
                            className={`w-full px-4 py-2 bg-gray-900 border rounded-lg text-white placeholder-gray-500 focus:outline-none transition-colors ${
                              duplicateHashCheck.exists ? 'border-red-500 focus:border-red-500' : 'border-gray-600 focus:border-blue-500'
                            }`}
                          />
                          {duplicateHashCheck.exists && (
                            <p className="mt-1 text-sm text-red-300 whitespace-pre-wrap font-medium">
                              {duplicateHashCheck.message || `⚠️ This transaction hash already exists in the system (Payment ID: ${duplicateHashCheck.paymentId}). Create is refused.`}
                            </p>
                          )}
                          {duplicateHashCheck.checkFailed && (
                            <p className="mt-1 text-sm text-red-400">
                              Hash uniqueness check failed. Create is refused until the check succeeds.
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Wallet Addresses */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-white text-sm mb-2">
                            Sender Wallet <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="text"
                            value={manualPaymentForm.senderWallet}
                            onChange={(e) => setManualPaymentForm({ ...manualPaymentForm, senderWallet: e.target.value })}
                            placeholder="User's wallet address"
                            className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 font-mono text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-white text-sm mb-2">
                            Receiver Wallet <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="text"
                            value={manualPaymentForm.receiverWallet}
                            onChange={(e) => setManualPaymentForm({ ...manualPaymentForm, receiverWallet: e.target.value })}
                            placeholder="Company wallet address"
                            className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 font-mono text-sm"
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Bank Transfer Fields */}
                      <div>
                        <label className="block text-white text-sm mb-2">
                          Payment Reference Number <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          value={manualPaymentForm.paymentReference}
                          onChange={(e) => setManualPaymentForm({ ...manualPaymentForm, paymentReference: e.target.value })}
                          placeholder="Bank reference or transaction number"
                          className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                        />
                      </div>
                    </>
                  )}

                  {/* Shares Calculation */}
                  {manualPaymentForm.amount && manualPaymentForm.phaseId && (
                    <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                      <p className="text-blue-400 font-semibold mb-2">📊 Shares Calculation</p>
                      <p className="text-gray-300 text-sm mb-2">
                        {manualPaymentForm.amount} {manualPaymentForm.currency} ÷ $
                        {investmentPhases.find(p => p.id === parseInt(manualPaymentForm.phaseId))?.price_per_share.toFixed(2)} = {' '}
                        <span className="text-yellow-400 font-bold">{calculateShares().toFixed(2)} shares</span>
                      </p>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={manualPaymentForm.sharesOverridden}
                          onChange={(e) => setManualPaymentForm({ ...manualPaymentForm, sharesOverridden: e.target.checked })}
                          className="w-4 h-4"
                        />
                        <span className="text-sm text-gray-300">Override calculated shares</span>
                      </label>
                      {manualPaymentForm.sharesOverridden && (
                        <div className="mt-3">
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={manualPaymentForm.shares}
                            onChange={(e) => setManualPaymentForm({ ...manualPaymentForm, shares: e.target.value })}
                            placeholder="Enter shares manually"
                            className="w-full px-4 py-2 bg-gray-900 border border-yellow-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
                          />
                          <p className="text-yellow-400 text-sm mt-1">⚠️ Manual override - verify calculation</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Payment Proof Upload */}
                  <div>
                    <label className="block text-white text-sm mb-2">
                      Payment Proof {manualPaymentForm.paymentType === 'bank_transfer' && <span className="text-red-400">*</span>}
                      {manualPaymentForm.paymentType === 'crypto' && <span className="text-gray-500">(Optional)</span>}
                    </label>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        console.log('📎 [MANUAL PAYMENT] File selected:', {
                          name: file?.name,
                          type: file?.type,
                          size: file?.size
                        })
                        if (file) {
                          if (file.size > 5 * 1024 * 1024) {
                            setMessage({ type: 'error', text: 'File size must be less than 5MB' })
                            setTimeout(() => setMessage(null), 5000)
                            e.target.value = ''
                            return
                          }
                          console.log('✅ [MANUAL PAYMENT] File accepted, updating form state')
                          setManualPaymentForm({ ...manualPaymentForm, paymentProof: file })
                        }
                      }}
                      className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-blue-600 file:text-white hover:file:bg-blue-700"
                    />
                    {manualPaymentForm.paymentProof && (
                      <p className="text-green-400 text-sm mt-1">✅ {manualPaymentForm.paymentProof.name}</p>
                    )}
                    <p className="text-gray-500 text-sm mt-1">
                      Max size: 5MB. Accepted: Images, PDF
                      {manualPaymentForm.paymentType === 'bank_transfer' && ' (Required for bank transfers)'}
                    </p>
                  </div>

                  {/* Admin Notes */}
                  <div>
                    <label className="block text-white text-sm mb-2">Admin Notes</label>
                    <textarea
                      value={manualPaymentForm.adminNotes}
                      onChange={(e) => setManualPaymentForm({ ...manualPaymentForm, adminNotes: e.target.value })}
                      placeholder="Reason for manual entry, special circumstances, etc."
                      rows={3}
                      className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                    />
                  </div>

                  {/* Payment Date */}
                  <div>
                    <label className="block text-white text-sm mb-2">
                      Payment Date <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="date"
                      value={manualPaymentForm.paymentDate}
                      onChange={(e) => setManualPaymentForm({ ...manualPaymentForm, paymentDate: e.target.value })}
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    />
                    {/* Show warning when using a past phase */}
                    {(() => {
                      const selectedPhase = investmentPhases.find(p => p.id === parseInt(manualPaymentForm.phaseId))
                      if (selectedPhase && !selectedPhase.is_active && selectedPhase.end_date) {
                        return (
                          <p className="text-yellow-400 text-sm mt-1 flex items-center gap-1">
                            <span>⚠️</span>
                            <span>
                              Auto-set to {selectedPhase.phase_name} period. Shares will be calculated at ${selectedPhase.price_per_share.toFixed(2)}/share.
                            </span>
                          </p>
                        )
                      }
                      return (
                        <p className="text-gray-500 text-sm mt-1">
                          Date when the payment was actually made (defaults to today)
                        </p>
                      )
                    })()}
                  </div>

                  {/* Initial Status */}
                  <div>
                    <label className="block text-white text-sm mb-2">Initial Status</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          value="pending"
                          checked={manualPaymentForm.initialStatus === 'pending'}
                          onChange={(e) => setManualPaymentForm({ ...manualPaymentForm, initialStatus: e.target.value as 'pending' | 'approved' })}
                          className="w-4 h-4"
                        />
                        <span className="text-white">Pending</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          value="approved"
                          checked={manualPaymentForm.initialStatus === 'approved'}
                          onChange={(e) => setManualPaymentForm({ ...manualPaymentForm, initialStatus: e.target.value as 'pending' | 'approved' })}
                          className="w-4 h-4"
                        />
                        <span className="text-white">Approved</span>
                      </label>
                    </div>
                  </div>

                  {/* Approval Notes (if approved) */}
                  {manualPaymentForm.initialStatus === 'approved' && (
                    <div>
                      <label className="block text-white text-sm mb-2">Approval Notes</label>
                      <textarea
                        value={manualPaymentForm.approvalNotes}
                        onChange={(e) => setManualPaymentForm({ ...manualPaymentForm, approvalNotes: e.target.value })}
                        placeholder="Notes about why this payment is being approved immediately..."
                        rows={2}
                        className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Review & Confirm */}
              {manualPaymentStep === 3 && manualPaymentForm.selectedUser && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white mb-4">Review & Confirm</h3>

                  <div className="bg-gray-900 rounded-lg border border-gray-700 p-6 space-y-4">
                    {/* User Information */}
                    <div>
                      <h4 className="text-white font-semibold mb-3">User Information</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-white">User ID:</p>
                          <p className="text-white font-medium">{manualPaymentForm.selectedUser.id}</p>
                        </div>
                        <div>
                          <p className="text-white">Username:</p>
                          <p className="text-white font-medium">{manualPaymentForm.selectedUser.username}</p>
                        </div>
                        <div>
                          <p className="text-white">Email:</p>
                          <p className="text-white font-medium">{manualPaymentForm.selectedUser.email}</p>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-700 pt-4">
                      <h4 className="text-white font-semibold mb-3">Payment Details</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-white">Amount:</p>
                          <p className="text-white font-medium">{manualPaymentForm.amount} {manualPaymentForm.currency}</p>
                        </div>
                        <div>
                          <p className="text-white">Payment type:</p>
                          <p className="text-white font-medium">{manualPaymentForm.paymentType === 'crypto' ? 'Crypto' : 'Bank transfer'}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-white">Package:</p>
                          <p className="text-amber-300 font-medium">
                            {manualPaymentForm.packageKind === 'standard'
                              ? 'Standard (no PCAP bonus)'
                              : `${manualPaymentForm.campaignPackage} × ${manualPaymentForm.campaignPackageQty}`}
                          </p>
                        </div>
                        <div>
                          <p className="text-white">Network:</p>
                          <p className="text-white font-medium">{manualPaymentForm.network}</p>
                        </div>
                        <div>
                          <p className="text-white">Share Phase:</p>
                          <p className="text-white font-medium">
                            Phase {investmentPhases.find(p => p.id === parseInt(manualPaymentForm.phaseId))?.phase_number}
                          </p>
                        </div>
                        <div>
                          <p className="text-white">Shares to Purchase:</p>
                          <p className="text-yellow-400 font-bold">
                            {manualPaymentForm.sharesOverridden ? manualPaymentForm.shares : calculateShares().toFixed(2)} shares
                            {manualPaymentForm.sharesOverridden && <span className="text-yellow-500 text-sm ml-1">(Override)</span>}
                          </p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-white">Transaction Hash/Reference:</p>
                          <p className="text-white font-mono text-sm break-all">{manualPaymentForm.transactionHash}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-white">Payment Proof:</p>
                          <p className="text-green-400 text-sm">{manualPaymentForm.paymentProof?.name}</p>
                        </div>
                        {manualPaymentForm.adminNotes && (
                          <div className="col-span-2">
                            <p className="text-white">Admin Notes:</p>
                            <p className="text-white text-sm">{manualPaymentForm.adminNotes}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-white">Initial Status:</p>
                          <p className={`font-semibold ${
                            manualPaymentForm.initialStatus === 'approved' ? 'text-green-400' : 'text-yellow-400'
                          }`}>
                            {manualPaymentForm.initialStatus.toUpperCase()}
                          </p>
                        </div>
                        <div>
                          <p className="text-white">Payment Date:</p>
                          <p className="text-white font-medium">
                            {manualPaymentForm.paymentDate
                              ? new Date(manualPaymentForm.paymentDate).toLocaleDateString('en-ZA', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })
                              : 'Today (Current Date)'}
                          </p>
                        </div>
                        {manualPaymentForm.initialStatus === 'approved' && manualPaymentForm.approvalNotes && (
                          <div className="col-span-2">
                            <p className="text-white">Approval Notes:</p>
                            <p className="text-white text-sm">{manualPaymentForm.approvalNotes}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* New Balance Preview */}
                    <div className="border-t border-gray-700 pt-4">
                      <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                        <h4 className="text-blue-400 font-semibold mb-2">📊 Balance Preview</h4>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-white">New Shares:</p>
                            <p className="text-yellow-400 font-medium">
                              +{manualPaymentForm.sharesOverridden ? manualPaymentForm.shares : calculateShares().toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Confirmation Checkbox */}
                    <div className="border-t border-gray-700 pt-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={manualPaymentForm.forceCreateDuplicate}
                          onChange={(e) => setManualPaymentForm({ ...manualPaymentForm, forceCreateDuplicate: e.target.checked })}
                          className="w-5 h-5 mt-0.5"
                        />
                        <span className="text-white">
                          I confirm that this payment information is accurate and I have verified all details before submission.
                        </span>
                      </label>
                    </div>

                    {/* Warning for Approved Status */}
                    {manualPaymentForm.initialStatus === 'approved' && (
                      <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
                        <p className="text-yellow-400 font-semibold mb-1">⚠️ Immediate Approval</p>
                        <p className="text-gray-300 text-sm">
                          This payment will be approved immediately upon creation. User shares will be updated and commission workflow will be triggered automatically.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-900 px-6 py-4 border-t border-gray-700 flex justify-between">
              <button
                onClick={() => {
                  if (manualPaymentStep > 1) {
                    setManualPaymentStep(manualPaymentStep - 1)
                  } else {
                    if (confirm('Are you sure you want to cancel?')) {
                      resetManualPaymentForm()
                      setShowManualPaymentModal(false)
                    }
                  }
                }}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                {manualPaymentStep === 1 ? 'Cancel' : 'Back'}
              </button>

              <button
                onClick={() => {
                  if (manualPaymentStep < 3) {
                    setManualPaymentStep(manualPaymentStep + 1)
                  } else {
                    submitManualPayment()
                  }
                }}
                disabled={!validateStep(manualPaymentStep) || manualPaymentSubmitting}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {manualPaymentSubmitting ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Creating...
                  </span>
                ) : manualPaymentStep === 3 ? (
                  'Create Payment'
                ) : (
                  'Next'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Adjustment Modal */}
      {showShareAdjustmentModal && hashLookupResult.payment && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                ✏️ Adjust Share Allocation
              </h3>
              <p className="text-white text-sm mt-1">
                Payment ID: {hashLookupResult.payment.id.slice(0, 8)}...
              </p>
            </div>

            <div className="p-6 space-y-4">
              {/* Current Info */}
              <div className="bg-gray-900/50 rounded-lg p-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-white">User:</span>{' '}
                    <span className="text-white">{hashLookupResult.payment.users?.username}</span>
                  </div>
                  <div>
                    <span className="text-white">Amount:</span>{' '}
                    <span className="text-white">${hashLookupResult.payment.amount}</span>
                  </div>
                  <div>
                    <span className="text-white">Current Shares:</span>{' '}
                    <span className="text-yellow-400 font-bold">{hashLookupResult.payment.shares_to_purchase}</span>
                  </div>
                  <div>
                    <span className="text-white">Status:</span>{' '}
                    <span className="text-green-400">{hashLookupResult.payment.status}</span>
                  </div>
                </div>
              </div>

              {/* New Shares Input */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  New Share Amount
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={shareAdjustmentNewShares}
                  onChange={(e) => setShareAdjustmentNewShares(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter new share amount"
                />
                {shareAdjustmentNewShares && parseFloat(shareAdjustmentNewShares) !== hashLookupResult.payment.shares_to_purchase && (
                  <p className="text-sm mt-1">
                    <span className="text-white">Difference:</span>{' '}
                    <span className={parseFloat(shareAdjustmentNewShares) > (hashLookupResult.payment.shares_to_purchase || 0) ? 'text-green-400' : 'text-red-400'}>
                      {parseFloat(shareAdjustmentNewShares) > (hashLookupResult.payment.shares_to_purchase || 0) ? '+' : ''}
                      {(parseFloat(shareAdjustmentNewShares) - (hashLookupResult.payment.shares_to_purchase || 0)).toFixed(2)} shares
                    </span>
                  </p>
                )}
              </div>

              {/* Reason Input */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Reason for Adjustment <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={shareAdjustmentReason}
                  onChange={(e) => setShareAdjustmentReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Explain why shares are being adjusted..."
                />
              </div>

              {/* Warning */}
              <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-3">
                <p className="text-yellow-300 text-sm flex items-start gap-2">
                  <span>⚠️</span>
                  <span>This will update the share allocation for this payment. The change will be logged for audit purposes.</span>
                </p>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowShareAdjustmentModal(false)
                  setShareAdjustmentNewShares('')
                  setShareAdjustmentReason('')
                }}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (hashLookupResult.payment && shareAdjustmentNewShares && shareAdjustmentReason) {
                    manualShareAdjustment(
                      hashLookupResult.payment.id,
                      parseFloat(shareAdjustmentNewShares),
                      shareAdjustmentReason
                    )
                  }
                }}
                disabled={
                  !shareAdjustmentNewShares ||
                  !shareAdjustmentReason ||
                  parseFloat(shareAdjustmentNewShares) === hashLookupResult.payment.shares_to_purchase ||
                  shareAdjustmentProcessing
                }
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {shareAdjustmentProcessing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Adjusting...
                  </>
                ) : (
                  'Confirm Adjustment'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
